import React, { useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import { Document, Page } from 'react-pdf';
import { Stage, Layer, Rect, Text, Circle } from 'react-konva';
// @ts-ignore
import 'pdfjs-dist/web/pdf_viewer.css';
import * as XLSX from '@e965/xlsx';
import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

// Apply autotable plugin to jsPDF
applyPlugin(jsPDF);

import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useLanguage } from './contexts/LanguageContext';
import { EngineeringLoadingScreen } from './components/EngineeringLoadingScreen';
import { auth } from './config/firebase';
import { Tool, isPremiumOrUltimatePlan } from './services/authService';
import { getCompanyProfileCached, getCachedImageUrl, getCachedCompanyLogoDataUrl } from './services/cacheService';
import { logger } from './utils/logger';
import { Language, getStrings } from './constants/strings';
import { initializeFonts, setFontForLanguage, FONT_NAMES } from './utils/pdfFonts';
import { DEFAULT_COMPANY_LOGO_URL } from './constants/storage';
import { TOLERANCE_COLORS } from './constants/colors';
import { DetectedTextItem, DrawingTextType, extractTextFromPdfJs, extractTextFromPdfJsInRegion, extractTextFromGeminiInRegion, extractIsoSentencesFromPdf, extractIso286DesignationFromPdf, extractIso22081TitleBlockFromPdf, extractIso22081TolerancesViaGemini, getPdfPageCanvasFromViewerContainer } from './services/ocrService';
import { extractIso286Designation, lookupIso286Tolerance, parseNominalToMm, hasIso286InSentences, hasDiameterMark, ISO286_HOLE_OPTIONS, ISO286_SHAFT_OPTIONS, ISO286_M_FULL_TOLERANCES, parseHoleFromDesignation, parseShaftFromDesignation, parseIso286MSelectionPair, isIso286MPairHoleToleranceOnly } from './services/iso286Service';
import {
    lookupIso2768Tolerance,
    lookupIso2768RadiusTolerance,
    lookupIso2768AngularTolerance,
    isIso2768ChamferHeightCallout,
    getIso2768ChamferHeightLegMm,
    parseIso2768ChamferHeightLegString,
    getIso2768ChamferNominalDisplayCore,
    getIso2768ChamferToleranceSuffixAfterCore,
    stripIso2768ChamferClassPrefix,
    ISO2768_DROPDOWN_OPTIONS,
    ISO2768_DEFAULT,
    SHAPE_STANDARD_DROPDOWN_OPTIONS,
    SHAPE_STANDARD_DEFAULT,
    parseIsoOcrHints,
    getStandardLabelsRecognizedFromRegionOcr,
    getMenuSelectionsFromIsoOcrHints,
    ocrLinearHintsAllowStandardTolerance
} from './services/iso2768Service';
import { embedExcelInPdf, embedExcelInPdfBlob } from './services/pdfAttachmentService';
import {
    uploadRevision,
    hasConfiguredLocalServer,
    getStorageMode,
    SettingsPayload,
    LocalServerError,
    uploadOperatorReport,
    MeasurementValuePayload
} from './services/localServerService';
import { uploadReportToCloud, uploadSettingsToCloud } from './services/cloudStorageService';
import { buildEditorMarkupSnapshotPng } from './services/editorMarkupSnapshot';
import { saveDownloadDirHandle, getDownloadDirHandle, clearDownloadDirHandle, buildFolderKey, buildSettingsPdfFolderKey } from './utils/downloadFolderStorage';
import { storeSavedPdf } from './utils/savedPdfStorage';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const PDF_REPORT_SAVE_COUNTS_KEY = 'pdfReportSaveCounts';
/** Setup-sheet cell value; null if cell absent — callers should treat as false (no legacy reports). */
function parseSequenceMeasurementExcelValue(seqVal: unknown): boolean | null {
    if (seqVal == null) return null;
    const seq = String(seqVal).toLowerCase().trim();
    return seq === 'yes' || seq === 'true' || seq === '1' || seq === 'ja' || seq === 'да';
}

function parseExamModeExcelValue(val: unknown): boolean | null {
    if (val == null) return null;
    const v = String(val).toLowerCase().trim();
    return v === 'yes' || v === 'true' || v === '1' || v === 'ja' || v === 'да';
}

const EDITOR_WORK_DRAFT_VERSION = 1 as const;
const SESSION_EDITOR_WORK_DRAFT_PREFIX = 'qdEditorWorkDraftV1:';

type EditorWorkDraftV1 = {
    v: typeof EDITOR_WORK_DRAFT_VERSION;
    rects: unknown[];
    referenceDimensions: { width: number; height: number } | null;
    sequenceMeasurement: boolean;
    examMode: boolean;
    nextNumber: number;
    downloadPathDisplayName: string;
};

function buildEditorWorkDraftStorageKey(file: File, detail?: string, revision?: string): string {
    const fk = buildFolderKey(detail, revision);
    const namePart = (file.name || 'doc').replace(/[^\w.-]+/g, '_').slice(0, 120);
    return `${SESSION_EDITOR_WORK_DRAFT_PREFIX}${namePart}:${file.size}:${fk}`;
}

function readEditorWorkDraft(file: File | null, detail?: string, revision?: string): EditorWorkDraftV1 | null {
    if (!file) return null;
    try {
        const raw = sessionStorage.getItem(buildEditorWorkDraftStorageKey(file, detail, revision));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as EditorWorkDraftV1;
        if (parsed?.v !== EDITOR_WORK_DRAFT_VERSION || !Array.isArray(parsed.rects)) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeEditorWorkDraft(
    file: File,
    detail: string | undefined,
    revision: string | undefined,
    draft: EditorWorkDraftV1,
): void {
    try {
        sessionStorage.setItem(buildEditorWorkDraftStorageKey(file, detail, revision), JSON.stringify(draft));
    } catch {
        // ignore
    }
}

function clearEditorWorkDraft(file: File | null, detail?: string, revision?: string): void {
    if (!file) return;
    try {
        sessionStorage.removeItem(buildEditorWorkDraftStorageKey(file, detail, revision));
    } catch {
        // ignore
    }
}

function mapDraftRectsToState(raw: unknown[]): any[] {
    return raw.map((r: any) => ({
        ...r,
        isConfirmed: r.isConfirmed !== undefined ? r.isConfirmed : true,
    }));
}

const PDF_SAVED_RECTS_KEY_PREFIX = 'pdfSavedRects';
const PDF_SAVED_USERNAME_RECTS_KEY_PREFIX = 'pdfSavedUsernameRects';

function getSavedRectsStorageKey(detail?: string, revision?: string): string {
    return `${PDF_SAVED_RECTS_KEY_PREFIX}:${buildFolderKey(detail, revision)}`;
}

function getSavedUsernameRectsStorageKey(detail?: string, revision?: string): string {
    return `${PDF_SAVED_USERNAME_RECTS_KEY_PREFIX}:${buildFolderKey(detail, revision)}`;
}

const getPdfReportSaveCount = (filename: string): number => {
    try {
        const raw = localStorage.getItem(PDF_REPORT_SAVE_COUNTS_KEY);
        if (!raw) return 0;
        const obj = JSON.parse(raw) as Record<string, number>;
        return typeof obj[filename] === 'number' ? obj[filename] : 0;
    } catch {
        return 0;
    }
};

const setPdfReportSaveCount = (filename: string, count: number): void => {
    try {
        const raw = localStorage.getItem(PDF_REPORT_SAVE_COUNTS_KEY);
        const obj: Record<string, number> = raw ? JSON.parse(raw) : {};
        obj[filename] = count;
        localStorage.setItem(PDF_REPORT_SAVE_COUNTS_KEY, JSON.stringify(obj));
    } catch { /* ignore */ }
};

// Delay revoke so a tab opened via window.open() can finish loading the blob URL.
const REVOKE_PDF_BLOB_URL_DELAY_MS = 120_000;
const scheduleRevokePdfBlobUrl = (url: string) => {
    window.setTimeout(() => {
        try {
            URL.revokeObjectURL(url);
        } catch {
            // ignore
        }
    }, REVOKE_PDF_BLOB_URL_DELAY_MS);
};

/** jsPDF addImage expects a data URL; chunked to avoid call stack limits on large PNGs. */
const uint8ToPngDataUrl = (bytes: Uint8Array): string => {
    const chunk = 0x8000;
    const parts: string[] = [];
    for (let i = 0; i < bytes.length; i += chunk) {
        parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]));
    }
    return `data:image/png;base64,${btoa(parts.join(''))}`;
};

interface PdfViewerProps {
    file: File | null;
    excelFile: File | null;
    editorExcelFile: File | null;
    onBack: (opts?: { preserveOperatorHomeInputs?: boolean }) => void;
    detail?: string;
    order?: string;
    density?: string;
    counter?: string;
    role?: 'user' | 'editor' | null;
    engineerName?: string;
    revision?: string;
    /**
     * Local-server revision id this report was generated against. Set when the
     * operator opened the page via the local-server-driven flow; used to link
     * the saved report back to its source revision in /api/v1/reports.
     */
    revisionId?: string;
    /** Editor opened a brand-new revision (uploaded PDF only, no settings from DB/embed). */
    isNewEditorRevision?: boolean;
    onDensityChange?: (density: string) => void;
    reportLanguage?: Language;
}

/** Touch/pen synthesized DOM events may omit `button`; treat like left mouse (0). */
const isMainPointerButton = (evt: { button?: number }): boolean =>
    evt.button === undefined || evt.button === 0;

// Helper to normalize ISO 22081 input value for tolerance (comma to dot, trim)
const normalizeIso22081Value = (val: string): string => {
    if (!val || !val.trim()) return '';
    return val.trim().replace(',', '.');
};

/** After trim, first character is R: use radius & chamfer band (not the linear size band). */
const nominalLeadingIsRadiusLetterR = (nominal: string): boolean => {
    const t = nominal.trim();
    return t.length > 0 && /^[Rr]/.test(t);
};

/** After trim, first character is a digit: use the linear size tolerance table/band. */
const nominalLeadingIsDigit = (nominal: string): boolean => {
    return /^\d/.test(nominal.trim());
};

// Helper to get ISO 22081 tolerance by dimension type.
// Linear sizes: leading digit or diameter (Ø); radius band: leading letter R; chamfer: C callouts.
// Shape sizes: fallback when linear is empty (geometrical / profile-related).
// Angular sizes: angular dimensions (°, deg).
const getIso22081Tolerance = (
    nominal: string,
    linearSizes: string,
    shapeSizes: string,
    angularSizes: string,
    radiusChamferSizes: string
): { tolerancePlus: string; toleranceMinus: string } | null => {
    const linearVal = normalizeIso22081Value(linearSizes);
    const shapeVal = normalizeIso22081Value(shapeSizes);
    const angularVal = normalizeIso22081Value(angularSizes);
    const radiusChamferVal = normalizeIso22081Value(radiusChamferSizes);

    const nomTrim = nominal.trim();
    const isChamferDim =
        isIso2768ChamferHeightCallout(nomTrim) || /^\s*[Cc]\s*\S/.test(nomTrim);
    const bodyForAngle = stripIso2768ChamferClassPrefix(nomTrim);
    const isAngular = /°|deg|grad/i.test(bodyForAngle) && !isChamferDim;
    const isRadial = nominalLeadingIsRadiusLetterR(nomTrim);

    let val = '';
    if (isAngular && angularVal) val = angularVal;
    else if ((isChamferDim || isRadial) && radiusChamferVal) val = radiusChamferVal;
    else if (linearVal) val = linearVal;
    else if (shapeVal) val = shapeVal;
    if (!val) return null;
    return { tolerancePlus: val, toleranceMinus: val };
};

// Helper to parse tolerance values as full decimal numbers
const parseTolerance = (val: string | number | undefined) => {
    // Handle null, undefined, or empty values
    if (val === null || val === undefined) return 0.0;

    // Convert to string if it's a number
    const strVal = typeof val === 'number' ? String(val) : val;

    // Handle empty string
    if (!strVal || (typeof strVal === 'string' && strVal.trim() === '')) return 0.0;

    // Replace comma with dot for Estonian number formatting
    const normalizedVal = String(strVal).replace(',', '.');
    return parseFloat(normalizedVal);
};

// Helper to parse numbers with comma support for Estonian formatting and dimension prefixes
// (diameter Ø/o/O at any position, radius R/r only at the start so "R5" -> 5 while "5" is unchanged).
const parseNumberWithComma = (val: string | undefined): number => {
    if (!val || val.trim() === '') return NaN;
    const normalizedVal = val
        .trim()
        .replace(/^[Rr]\s*/, '')
        .replace(/[Øø⌀∅oO\s]/g, '')
        .replace(',', '.');
    const num = parseFloat(normalizedVal);
    return isNaN(num) ? NaN : num;
};

// OCR often misreads diameter Ø as Latin Q or Greek φ/ϕ/Φ before a number (Q25, φ12.5)
const normalizeOcrMisreadDiameterQ = (text: string): string =>
    text
        .replace(/(^|[\s,;|])Q(\s*)(?=\d)/g, '$1Ø$2')
        .replace(/(^|[\s,;|])[φϕΦ](\s*)(?=\d)/gu, '$1Ø$2');

/**
 * PDF.js join order may place ISO 286 designation before the nominal (e.g. "h7 Ø6.5").
 * Move designation after the size when it parses as tolerance and the second token is the nominal.
 */
const normalizeOcrTokensToleranceBeforeNominal = (text: string): string => {
    const t = text.trim();
    const mDiam = t.match(/^(\S+)\s+([Øø⌀∅oOφϕΦ]\s*\d+([.,]\d*)?)$/u);
    if (mDiam) {
        const head = mDiam[1];
        if (/^M\d/.test(head)) return text;
        const des = extractIso286Designation(head);
        if (des && !/^M\d/i.test(des)) {
            return `${mDiam[2]} ${head}`.replace(/\s+/g, ' ').trim();
        }
    }
    const mNum = t.match(/^(\S+)\s+(\d+([.,]\d*)?)$/);
    if (mNum) {
        const head = mNum[1];
        if (/^M\d/.test(head)) return text;
        const des = extractIso286Designation(head);
        if (des && !/^M\d/i.test(des)) {
            return `${mNum[2]} ${head}`.replace(/\s+/g, ' ').trim();
        }
    }
    return text;
};

/**
 * Client-side fallback classifier for drawing text recognised by OCR.
 * Returns 'dimension' for numeric sizes (with or without tolerances, diameter/radius/angle prefix)
 * or 'text' for non-dimensional callouts (threads, roughness, hardness, materials, notes, etc.).
 */
const classifyDrawingText = (text: string): DrawingTextType => {
    const t = text.trim();
    if (!t) return 'text';

    // Metric threads: M8, M10x1.5, M12x1.75-6g, M10-6H
    if (/^M\d+([.,]\d+)?\s*(x\s*\d+([.,]\d+)?)?\s*(-\s*\d+[A-Za-z])?$/i.test(t)) return 'text';
    // Pipe / specialty threads: G1/2, Rc1/4, Rp1/2, NPT 1/2, BSP, BSPP, UNC, UNF
    if (/^(G|Rc|Rp|NPT|BSPP?|UNC|UNF)\s*\d/i.test(t)) return 'text';
    // Surface roughness: Ra 1.6, Rz 6.3, Ra0.8, Rq, Rp (but NOT plain R5 which is a radius)
    if (/^R[azpq]\s*\d/i.test(t)) return 'text';
    // Hardness: HRC 45, HB 200, HV 300, HRA 80
    if (/^(HRC|HB|HV|HRA)\s*\d/i.test(t)) return 'text';
    // Section labels: A-A, B-B, C-C (single letter dash same letter)
    if (/^[A-Z]-[A-Z]$/i.test(t)) return 'text';
    // Cyrillic-prefixed material/steel grades: Ст3, 12Х18Н10Т — starts with Cyrillic letter(s) followed by digits
    if (/^[А-Яа-яЁё]+\d/.test(t)) return 'text';
    // Common material designations: AISI 304, DIN 1.4301, EN-GJS etc.
    if (/^(AISI|DIN|EN[-\s]|AlMg|CuZn|Fe|SS)\s*\d/i.test(t)) return 'text';
    // Pure text without any digits (length > 1 to skip stray characters)
    if (t.length > 1 && !/\d/.test(t)) return 'text';

    return 'dimension';
};

/** True when OCR line starts with a measurable size (not a lone ISO / note line like "ISO 2768-m"). */
const ocrTrimLooksLikeLeadingDimension = (text: string): boolean => {
    const s = text.trim();
    if (!s) return false;
    if (/^[Øø⌀∅oOφϕΦ]\s*\d/.test(s)) return true;
    if (/^[Rr]\s*\d/.test(s)) return true;
    if (/^C\s*\d/i.test(s)) return true;
    if (/^\d/.test(s)) return true;
    if (/^[.,]\d/.test(s)) return true;
    return false;
};

/**
 * True when the text after the nominal looks like a numeric tolerance (not "(4)", "x 45", notes).
 * Accepts leading ± / + / -, decimals starting with 0 or ., or EU-style ",05".
 */
const ocrSuffixLooksLikeNumericTolerance = (tolerancePart: string): boolean => {
    const s = tolerancePart.trim();
    if (!s) return false;
    if (s.startsWith('±')) return true;
    if (s.startsWith('+') || s.startsWith('-')) return true;
    if (s.startsWith('.')) return true;
    if (s.startsWith('0')) return true;
    if (/^,\d/.test(s)) return true;
    return false;
};

/** OCR often keeps ° / deg on angular tolerances (e.g. "±5°"); strip for numeric parsing only. */
const stripOcrToleranceAngleUnitSuffix = (s: string): string =>
    s.trim().replace(/\s*(?:[°º˚∘′]|deg\b|grad\b)\s*$/iu, '').trim();

/** PDF/OCR may use Unicode minus (U+2212) or en dash; normalize for consistent token / sign detection. */
const ocrDashToAsciiMinus = (s: string) => s.replace(/[\u2212\u2010-\u2015]/g, '-');

/**
 * Map two numeric tolerance tokens to upper/lower by explicit + / − on the token (OCR may reorder stacked limits).
 * If neither token has a leading + or -, returns null and caller may use order or signed-value heuristics.
 */
const assignTwoOcrToleranceTokensByExplicitSign = (
    t0: string,
    t1: string,
    norm: (s: string) => string
): { tolerancePlus: string; toleranceMinus: string; toleranceType: 'plusminus' | 'plus' | 'minus' } | null => {
    const w0 = t0.trim();
    const w1 = t1.trim();
    const plus0 = w0.startsWith('+');
    const plus1 = w1.startsWith('+');
    const minus0 = w0.startsWith('-');
    const minus1 = w1.startsWith('-');
    if (plus0 && minus1) {
        return { tolerancePlus: norm(w0), toleranceMinus: norm(w1), toleranceType: 'plusminus' };
    }
    if (minus0 && plus1) {
        return { tolerancePlus: norm(w1), toleranceMinus: norm(w0), toleranceType: 'plusminus' };
    }
    if (plus0 && !plus1) {
        return { tolerancePlus: norm(w0), toleranceMinus: norm(w1), toleranceType: 'plusminus' };
    }
    if (plus1 && !plus0) {
        return { tolerancePlus: norm(w1), toleranceMinus: norm(w0), toleranceType: 'plusminus' };
    }
    if (minus0 && !minus1) {
        return { tolerancePlus: norm(w1), toleranceMinus: norm(w0), toleranceType: 'plusminus' };
    }
    if (minus1 && !minus0) {
        return { tolerancePlus: norm(w0), toleranceMinus: norm(w1), toleranceType: 'plusminus' };
    }
    return null;
};

/**
 * Two negative deviations (e.g. -0,05 and -0,10): upper limit is the less negative value, lower the more negative.
 * OCR may list them in bottom-to-top order, so we sort by numeric value instead of token order.
 */
const assignTwoNegativeDeviationsToUpperLower = (
    t0: string,
    t1: string,
    norm: (s: string) => string
): { tolerancePlus: string; toleranceMinus: string; toleranceType: 'minus' } | null => {
    const v0 = parseFloat(t0.trim().replace(',', '.'));
    const v1 = parseFloat(t1.trim().replace(',', '.'));
    if (Number.isNaN(v0) || Number.isNaN(v1)) return null;
    if (v0 >= 0 || v1 >= 0) return null;
    const upperTok = v0 > v1 ? t0 : t1;
    const lowerTok = v0 > v1 ? t1 : t0;
    return {
        tolerancePlus: norm(upperTok),
        toleranceMinus: norm(lowerTok),
        toleranceType: 'minus'
    };
};

/** Parse ± / + / - / slash / two-token tolerances after a chamfer core (same rules as main OCR tolerance tail). */
const tryParseNumericToleranceSuffix = (
    tolerancePart: string
): {
    tolerancePlus: string;
    toleranceMinus: string;
    toleranceType: 'plusminus' | 'plus' | 'minus';
} | null => {
    const sRaw = ocrDashToAsciiMinus(tolerancePart.trim());
    if (!sRaw || !ocrSuffixLooksLikeNumericTolerance(sRaw)) return null;
    const s = stripOcrToleranceAngleUnitSuffix(sRaw);
    if (!s || !ocrSuffixLooksLikeNumericTolerance(s)) return null;
    const norm = (str: string) => {
        let v = str.replace(',', '.').replace(/^[±+\-]/, '').trim();
        if (v.startsWith('.')) v = '0' + v;
        return v;
    };
    const getSign = (x: string): 'pos' | 'neg' => (x.trim().startsWith('-') ? 'neg' : 'pos');
    const inferToleranceType = (first: string, second: string): 'plusminus' | 'plus' | 'minus' => {
        const s1 = getSign(first);
        const s2 = getSign(second);
        if (s1 === 'neg' && s2 === 'neg') return 'minus';
        if (s1 === 'pos' && s2 === 'pos') return 'plus';
        return 'plusminus';
    };
    const mPlusMinus = s.match(/^±\s*(\d+[.,]?\d*|[.,]\d+)$/);
    if (mPlusMinus) {
        const v = norm(mPlusMinus[1]);
        return { tolerancePlus: v, toleranceMinus: v, toleranceType: 'plusminus' };
    }
    const mSlash = s.match(/^([+\-]?)(\d+[.,]?\d*|[.,]\d+)\s*\/\s*([+\-]?)(\d+[.,]?\d*|[.,]\d+)$/);
    if (mSlash) {
        const s1 = mSlash[1] === '-' ? 'neg' : 'pos';
        const s2 = mSlash[3] === '-' ? 'neg' : 'pos';
        const toleranceType =
            s1 === 'neg' && s2 === 'neg' ? 'minus' : s1 === 'pos' && s2 === 'pos' ? 'plus' : 'plusminus';
        return {
            tolerancePlus: norm(mSlash[2]),
            toleranceMinus: norm(mSlash[4]),
            toleranceType
        };
    }
    const twoParts = sRaw.split(/\s+/).filter(Boolean).map(stripOcrToleranceAngleUnitSuffix);
    if (twoParts.length === 2) {
        const numLike = /^[±+\-]?\s*(\d+([.,]\d*)?|[.,]\d+)$/;
        if (numLike.test(twoParts[0].trim()) && numLike.test(twoParts[1].trim())) {
            const bySign = assignTwoOcrToleranceTokensByExplicitSign(twoParts[0], twoParts[1], norm);
            if (bySign) return bySign;
            const bothNeg = assignTwoNegativeDeviationsToUpperLower(twoParts[0], twoParts[1], norm);
            if (bothNeg) return bothNeg;
            const toSigned = (t: string) => parseFloat(t.trim().replace(',', '.'));
            const a = toSigned(twoParts[0]);
            const b = toSigned(twoParts[1]);
            if (!Number.isNaN(a) && !Number.isNaN(b) && a < 0 && b >= 0) {
                return {
                    tolerancePlus: norm(twoParts[1]),
                    toleranceMinus: norm(twoParts[0]),
                    toleranceType: inferToleranceType(twoParts[1], twoParts[0])
                };
            }
            return {
                tolerancePlus: norm(twoParts[0]),
                toleranceMinus: norm(twoParts[1]),
                toleranceType: inferToleranceType(twoParts[0], twoParts[1])
            };
        }
    }
    const mSingle = s.match(/^[±+\-]?\s*(\d+([.,]\d*)?|[.,]\d+)$/);
    if (mSingle) {
        const v = norm(mSingle[1]);
        return { tolerancePlus: v, toleranceMinus: v, toleranceType: 'plusminus' };
    }
    return null;
};

/** Full chamfer callout may be stored only in iso2768ChamferCalloutHint while nominal stayed as height leg. */
const nominalDimensionForEditorFromRect = (r: {
    nominalDimension?: string;
    iso2768ChamferCalloutHint?: string;
}): string => {
    const hint = r.iso2768ChamferCalloutHint?.trim() || '';
    const nom = (r.nominalDimension || '').trim();
    const coreNom = getIso2768ChamferNominalDisplayCore(nom);
    const coreHint = getIso2768ChamferNominalDisplayCore(hint);
    if (coreNom) return coreNom;
    if (coreHint) return coreHint;
    return nom;
};

const recognizedValueForEditorFromRect = (r: {
    nominalDimension?: string;
    iso2768ChamferCalloutHint?: string;
}): string => {
    const hint = r.iso2768ChamferCalloutHint?.trim() || '';
    const nom = (r.nominalDimension || '').trim();
    return (
        getIso2768ChamferNominalDisplayCore(hint) ??
        getIso2768ChamferNominalDisplayCore(nom) ??
        (hint || nom)
    );
};

/**
 * Nominal in mm for tolerance UI (in/out, rectangle stroke, Excel min/max):
 * same rules as parseNumberWithComma for linear / Ø / R, plus ISO 2768 chamfer height
 * from full callout (C1, 1x45°…) so C-prefixed editor nominals are not lost as NaN.
 */
const parseSetterRectNominalToComparableNumber = (setterRect: {
    nominalDimension?: string;
    iso2768ChamferCalloutHint?: string;
} | null | undefined): number => {
    if (!setterRect) return NaN;
    const rawNom = (setterRect.nominalDimension || '').trim();
    const rawHint = (setterRect.iso2768ChamferCalloutHint || '').trim();
    for (const candidate of [rawNom, rawHint, `${rawNom} ${rawHint}`.trim()]) {
        if (!candidate) continue;
        const ch = getIso2768ChamferHeightLegMm(candidate);
        if (ch !== null) return ch;
    }
    const display = nominalDimensionForEditorFromRect(setterRect);
    const fromDisplay = parseNumberWithComma(display);
    if (!isNaN(fromDisplay)) return fromDisplay;
    return parseNumberWithComma(rawNom);
};

// Helper to split recognized OCR text: nominal ends at first space, rest is tolerance
const parseRecognizedTextToNominalAndTolerance = (
    text: string,
    applyIso286FromRecognizedText = true
): {
    nominal: string;
    tolerancePlus: string;
    toleranceMinus: string;
    toleranceType: 'plusminus' | 'plus' | 'minus';
    /** True when tolerances were filled from ISO 286 tables via letter designation in OCR text */
    toleranceFromStandardTable?: boolean;
} => {
    // OCR often returns nominal and tolerance glued together without a space
    // e.g. "21±0,1", "Ø21+0.1/-0.05", "R10-0.2", "45°±1". Insert a space before ± / + / -
    // that directly follows the nominal number (with optional Ø/R prefix and trailing °).
    const trimmed = normalizeOcrTokensToleranceBeforeNominal(
        normalizeOcrMisreadDiameterQ(ocrDashToAsciiMinus(text.trim()))
    ).replace(/^([Øø⌀∅oOφϕΦRr]?\s*\d+(?:[.,]\d*)?°?)(?=[±+\-])/, '$1 ');
    const result: {
        nominal: string;
        tolerancePlus: string;
        toleranceMinus: string;
        toleranceType: 'plusminus' | 'plus' | 'minus';
    } = {
        nominal: trimmed,
        tolerancePlus: '',
        toleranceMinus: '',
        toleranceType: 'plusminus'
    };
    // PDF.js may return diameter symbol and number as separate items: "ø 10" -> merge to "ø10"
    const diamNumOnly = trimmed.match(/^([Øø⌀∅oOφϕΦ])\s*(\d+([.,]\d*)?)\s*$/);
    if (diamNumOnly) {
        result.nominal = diamNumOnly[1] + diamNumOnly[2];
        return result;
    }
    const radiusNumOnly = trimmed.match(/^([Rr])\s*(\d+([.,]\d*)?)\s*$/);
    if (radiusNumOnly) {
        result.nominal = 'R' + radiusNumOnly[2];
        return result;
    }
    if (parseIso2768ChamferHeightLegString(trimmed) !== null) {
        const chamferNom = getIso2768ChamferNominalDisplayCore(trimmed) ?? trimmed;
        const chamferTolRest = getIso2768ChamferToleranceSuffixAfterCore(trimmed);
        const tolFromChamfer = chamferTolRest
            ? tryParseNumericToleranceSuffix(chamferTolRest)
            : null;
        if (tolFromChamfer) {
            return {
                ...result,
                nominal: chamferNom,
                tolerancePlus: tolFromChamfer.tolerancePlus,
                toleranceMinus: tolFromChamfer.toleranceMinus,
                toleranceType: tolFromChamfer.toleranceType
            };
        }
        return { ...result, nominal: chamferNom };
    }
    let nominalPart: string;
    let tolerancePart: string;
    const diamNumWithTol = trimmed.match(/^([Øø⌀∅oOφϕΦ])\s*(\d+([.,]\d*)?)\s+(.+)$/);
    const radiusNumWithTol = trimmed.match(/^([Rr])\s*(\d+([.,]\d*)?)\s+(.+)$/);
    if (diamNumWithTol) {
        nominalPart = diamNumWithTol[1] + diamNumWithTol[2];
        tolerancePart = diamNumWithTol[4].trim();
    } else if (radiusNumWithTol) {
        nominalPart = 'R' + radiusNumWithTol[2];
        tolerancePart = radiusNumWithTol[4].trim();
    } else {
        const spaceIdx = trimmed.indexOf(' ');
        if (spaceIdx >= 0) {
            nominalPart = trimmed.slice(0, spaceIdx).trim();
            tolerancePart = trimmed.slice(spaceIdx + 1).trim();
            // Diameter symbol alone + number: "ø 10" -> nominal "ø10"
            if (/^[Øø⌀∅oOφϕΦ]$/.test(nominalPart) && /^\d+([.,]\d*)?$/.test(tolerancePart)) {
                result.nominal = nominalPart + tolerancePart;
                return result;
            }
            // "R 10" split -> nominal "R10"
            if (/^[Rr]$/.test(nominalPart) && /^\d+([.,]\d*)?$/.test(tolerancePart)) {
                result.nominal = 'R' + tolerancePart;
                return result;
            }
            // Vertically stacked limits read as "0 12 -0,05": upper 0, nominal 12, lower -0,05 — not nominal 0.
            const stackTokens = trimmed.split(/\s+/).filter(Boolean);
            const tokenIsPlainNumericZero = (tok: string): boolean => {
                const u = tok.trim();
                if (!u || /^[Øø⌀∅oOφϕΦRr]/.test(u)) return false;
                const n = parseFloat(u.replace(',', '.').replace(/^\+/, ''));
                return !Number.isNaN(n) && Math.abs(n) < 1e-9;
            };
            const tokenIsPositiveLinearSize = (tok: string): boolean => {
                const u = tok.trim();
                if (!u || /^[Øø⌀∅oOφϕΦRr]/.test(u)) return false;
                const n = parseFloat(u.replace(',', '.').replace(/^\+/, ''));
                return !Number.isNaN(n) && n > 1e-9;
            };
            if (
                stackTokens.length >= 3 &&
                tokenIsPlainNumericZero(stackTokens[0]) &&
                tokenIsPositiveLinearSize(stackTokens[1]) &&
                ocrSuffixLooksLikeNumericTolerance(stackTokens.slice(2).join(' ').trim())
            ) {
                nominalPart = stackTokens[1];
                tolerancePart = [stackTokens[0], ...stackTokens.slice(2)].join(' ').trim();
            }
        } else {
            const isoMatch = trimmed.match(/^([Øø⌀∅oOφϕΦ]?\s*)(\d+([.,]\d*)?|[.,]\d+)([A-Za-z][A-Za-z]?\s*\d{1,2}(?:\s*\/\s*[A-Za-z][A-Za-z]?\s*\d{1,2})?)$/i);
            if (isoMatch) {
                nominalPart = (isoMatch[1] || '').trim() + isoMatch[2];
                tolerancePart = isoMatch[4];
            } else {
                return result;
            }
        }
    }
    if (!nominalPart || !tolerancePart) return { ...result, nominal: trimmed };

    result.nominal = nominalPart.replace(/\s+/g, '');

    if (applyIso286FromRecognizedText) {
        const iso286Designation = extractIso286Designation(tolerancePart);
        if (iso286Designation) {
            const nominalMm = parseNominalToMm(nominalPart);
            if (nominalMm !== null) {
                const isoResult = lookupIso286Tolerance(iso286Designation, nominalMm);
                if (isoResult) {
                    result.tolerancePlus = isoResult.tolerancePlus;
                    result.toleranceMinus = isoResult.toleranceMinus;
                    result.toleranceType = isoResult.toleranceType;
                    return { ...result, toleranceFromStandardTable: true };
                }
            }
        }
    }

    if (!ocrSuffixLooksLikeNumericTolerance(tolerancePart)) {
        const mergedForChamfer = `${nominalPart} ${tolerancePart}`.replace(/\s+/g, ' ').trim();
        if (parseIso2768ChamferHeightLegString(mergedForChamfer) !== null) {
            const chamferNom =
                getIso2768ChamferNominalDisplayCore(mergedForChamfer) ?? mergedForChamfer;
            const chamferTolRest = getIso2768ChamferToleranceSuffixAfterCore(mergedForChamfer);
            const tolFromChamfer = chamferTolRest
                ? tryParseNumericToleranceSuffix(chamferTolRest)
                : null;
            if (tolFromChamfer) {
                return {
                    ...result,
                    nominal: chamferNom,
                    tolerancePlus: tolFromChamfer.tolerancePlus,
                    toleranceMinus: tolFromChamfer.toleranceMinus,
                    toleranceType: tolFromChamfer.toleranceType
                };
            }
            return {
                ...result,
                nominal: chamferNom,
                tolerancePlus: '',
                toleranceMinus: '',
                toleranceType: 'plusminus'
            };
        }
        return result;
    }

    const norm = (s: string) => {
        let v = s.replace(',', '.').replace(/^[±+\-]/, '').trim();
        if (v.startsWith('.')) v = '0' + v;
        return v;
    };
    const getSign = (s: string): 'pos' | 'neg' => (s.trim().startsWith('-') ? 'neg' : 'pos');
    const inferToleranceType = (first: string, second: string): 'plusminus' | 'plus' | 'minus' => {
        const s1 = getSign(first);
        const s2 = getSign(second);
        if (s1 === 'neg' && s2 === 'neg') return 'minus';
        if (s1 === 'pos' && s2 === 'pos') return 'plus';
        return 'plusminus';
    };
    const tpRaw = tolerancePart.trim();
    const tpStripped = stripOcrToleranceAngleUnitSuffix(tpRaw);
    const tp =
        tpStripped && ocrSuffixLooksLikeNumericTolerance(tpStripped) ? tpStripped : tpRaw;
    const mPlusMinus = tp.match(/^±\s*(\d+[.,]?\d*|[.,]\d+)$/);
    if (mPlusMinus) {
        const v = norm(mPlusMinus[1]);
        result.tolerancePlus = v;
        result.toleranceMinus = v;
        return result;
    }
    const mSlash = tp.match(/^([+\-]?)(\d+[.,]?\d*|[.,]\d+)\s*\/\s*([+\-]?)(\d+[.,]?\d*|[.,]\d+)$/);
    if (mSlash) {
        result.tolerancePlus = norm(mSlash[2]);
        result.toleranceMinus = norm(mSlash[4]);
        const s1 = mSlash[1] === '-' ? 'neg' : 'pos';
        const s2 = mSlash[3] === '-' ? 'neg' : 'pos';
        result.toleranceType = s1 === 'neg' && s2 === 'neg' ? 'minus' : s1 === 'pos' && s2 === 'pos' ? 'plus' : 'plusminus';
        return result;
    }
    const twoParts = tpRaw.split(/\s+/).filter(Boolean).map(stripOcrToleranceAngleUnitSuffix);
    if (twoParts.length === 2) {
        const numLike = /^[±+\-]?\s*(\d+([.,]\d*)?|[.,]\d+)$/;
        if (numLike.test(twoParts[0].trim()) && numLike.test(twoParts[1].trim())) {
            const bySign = assignTwoOcrToleranceTokensByExplicitSign(twoParts[0], twoParts[1], norm);
            if (bySign) {
                result.tolerancePlus = bySign.tolerancePlus;
                result.toleranceMinus = bySign.toleranceMinus;
                result.toleranceType = bySign.toleranceType;
                return result;
            }
            const bothNeg = assignTwoNegativeDeviationsToUpperLower(twoParts[0], twoParts[1], norm);
            if (bothNeg) {
                result.tolerancePlus = bothNeg.tolerancePlus;
                result.toleranceMinus = bothNeg.toleranceMinus;
                result.toleranceType = bothNeg.toleranceType;
                return result;
            }
            const toSigned = (t: string) => parseFloat(t.trim().replace(',', '.'));
            const a = toSigned(twoParts[0]);
            const b = toSigned(twoParts[1]);
            // Stacked limits often read in wrong order, e.g. "Ø6,5 -0,1 0" → lower then upper. Map by sign, not by token order.
            if (!Number.isNaN(a) && !Number.isNaN(b) && a < 0 && b >= 0) {
                result.tolerancePlus = norm(twoParts[1]);
                result.toleranceMinus = norm(twoParts[0]);
                result.toleranceType = inferToleranceType(twoParts[1], twoParts[0]);
            } else {
                result.tolerancePlus = norm(twoParts[0]);
                result.toleranceMinus = norm(twoParts[1]);
                result.toleranceType = inferToleranceType(twoParts[0], twoParts[1]);
            }
            return result;
        }
    }
    const mSingle = tp.match(/^[±+\-]?\s*(\d+([.,]\d*)?|[.,]\d+)$/);
    if (mSingle) {
        const v = norm(mSingle[1]);
        result.tolerancePlus = v;
        result.toleranceMinus = v;
        return result;
    }
    return result;
};

const normTolCompareNums = (a: string, b: string): boolean => {
    const sa = String(a).replace(',', '.').trim();
    const sb = String(b).replace(',', '.').trim();
    if (!sa && !sb) return true;
    const na = parseFloat(sa);
    const nb = parseFloat(sb);
    if (isNaN(na) || isNaN(nb)) return sa === sb;
    return Math.abs(na - nb) < 1e-8;
};

const tolerancesMatchLookup = (
    parsed: {
        tolerancePlus: string;
        toleranceMinus: string;
        toleranceType: 'plusminus' | 'plus' | 'minus';
    },
    lookup: { tolerancePlus: string; toleranceMinus: string; toleranceType?: 'plusminus' | 'plus' | 'minus' } | null
): boolean => {
    if (!lookup) return false;
    const lt = lookup.toleranceType ?? 'plusminus';
    return (
        normTolCompareNums(parsed.tolerancePlus, lookup.tolerancePlus) &&
        normTolCompareNums(parsed.toleranceMinus, lookup.toleranceMinus) &&
        parsed.toleranceType === lt
    );
};

/** ISO 2768-1 Table 1 linear vs Table 2 (radius / chamfer height) from nominal and optional chamfer callout text. */
const lookupIso2768ToleranceForNominalDisplay = (
    standardStr: string,
    nominalMm: number | null,
    nominalForKind: string,
    chamferCalloutHint?: string | null
): { tolerancePlus: string; toleranceMinus: string } | null => {
    // Leading R → table for radii; leading digit → linear table; Ø… handled below as linear.
    if (nominalLeadingIsRadiusLetterR(nominalForKind)) {
        const mm = nominalMm ?? parseNominalToMm(nominalForKind);
        if (mm === null) return null;
        return lookupIso2768RadiusTolerance(standardStr, mm);
    }
    if (nominalLeadingIsDigit(nominalForKind)) {
        const mm = nominalMm ?? parseNominalToMm(nominalForKind);
        if (mm === null) return null;
        return lookupIso2768Tolerance(standardStr, mm);
    }
    const hint = (chamferCalloutHint ?? '').trim();
    const sources = [ nominalForKind.trim(), hint ].filter(s => s.length > 0);
    for (const src of sources) {
        const t = src.trim();
        if (!t) continue;
        // Use full string: "C2" shorthand is chamfer Table 2; strip would wrongly test "2" as linear.
        if (isIso2768ChamferHeightCallout(t)) {
            const legMm = getIso2768ChamferHeightLegMm(t);
            if (legMm !== null) return lookupIso2768RadiusTolerance(standardStr, legMm);
        }
    }
    const mm = nominalMm ?? parseNominalToMm(nominalForKind);
    if (mm === null) return null;
    return lookupIso2768Tolerance(standardStr, mm);
};

/** After OCR: treat tolerances as standard-sourced if they match active ISO 2768 / 286 / 22081 lookups */
const ocrParsedTolerancesMatchStandards = (params: {
    parsed: {
        nominal: string;
        tolerancePlus: string;
        toleranceMinus: string;
        toleranceType: 'plusminus' | 'plus' | 'minus';
        toleranceFromStandardTable?: boolean;
    };
    displayRecognized: string;
    premiumUltimatePlanAllowed: boolean;
    effectiveGeneralStandard: string | null;
    bothHoleShaftSelected: boolean;
    iso286ActiveButton: 'hole' | 'shaft' | null;
    effectiveIso286Hole: string | null;
    effectiveIso286Shaft: string | null;
    iso22081LinearSizes: string;
    iso22081ShapeSizes: string;
    iso22081AngularSizes: string;
    iso22081RadiusChamferSizes: string;
}): boolean => {
    const {
        parsed,
        displayRecognized,
        premiumUltimatePlanAllowed,
        effectiveGeneralStandard,
        bothHoleShaftSelected,
        iso286ActiveButton,
        effectiveIso286Hole,
        effectiveIso286Shaft,
        iso22081LinearSizes,
        iso22081ShapeSizes,
        iso22081AngularSizes,
        iso22081RadiusChamferSizes
    } = params;

    if (parsed.toleranceFromStandardTable) return true;

    const nominal = parsed.nominal.trim();
    if (!nominal) return false;
    if (!String(parsed.tolerancePlus).trim() && !String(parsed.toleranceMinus).trim()) return false;

    const disp = displayRecognized.trim();
    const nominalChamferBody = stripIso2768ChamferClassPrefix(nominal);
    const isChamfer =
        isIso2768ChamferHeightCallout(nominal.trim()) || isIso2768ChamferHeightCallout(disp);
    const isAngular = /°|deg|grad/i.test(nominalChamferBody) && !isChamfer;
    const nominalMm = parseNominalToMm(nominal);
    const chamferLegMm = isChamfer
        ? (getIso2768ChamferHeightLegMm(nominal.trim()) ?? getIso2768ChamferHeightLegMm(disp))
        : null;
    const nominalMmForIso2768 = chamferLegMm ?? nominalMm;
    const hasDiam = hasDiameterMark(nominal);

    if (effectiveGeneralStandard === 'ISO 22081') {
        const r = getIso22081Tolerance(
            nominal,
            iso22081LinearSizes,
            iso22081ShapeSizes,
            iso22081AngularSizes,
            iso22081RadiusChamferSizes
        );
        return tolerancesMatchLookup(parsed, r ? { ...r, toleranceType: 'plusminus' as const } : null);
    }

    if (!effectiveGeneralStandard) return false;

    if (isAngular) {
        const r = lookupIso2768AngularTolerance(effectiveGeneralStandard);
        return tolerancesMatchLookup(parsed, r ? { ...r, toleranceType: 'plusminus' as const } : null);
    }

    if (nominalMmForIso2768 === null) return false;

    if (hasDiam && premiumUltimatePlanAllowed) {
        const afterNominal = displayRecognized.replace(/^[Øø⌀∅oOφϕΦ]?\s*[\d.,]+/, '').trim();
        const des = extractIso286Designation(afterNominal);
        if (des && nominalMm !== null) {
            const iso = lookupIso286Tolerance(des, nominalMm);
            if (tolerancesMatchLookup(parsed, iso)) return true;
        }
        if (bothHoleShaftSelected && iso286ActiveButton && nominalMm !== null) {
            const designationToUse =
                iso286ActiveButton === 'hole'
                    ? (effectiveIso286Hole || 'H7')
                    : (effectiveIso286Shaft || 'h6');
            const iso = lookupIso286Tolerance(designationToUse, nominalMm);
            if (tolerancesMatchLookup(parsed, iso)) return true;
        }
    }

    const r2768 = lookupIso2768ToleranceForNominalDisplay(
        effectiveGeneralStandard,
        nominalMmForIso2768,
        nominal,
        disp
    );
    return tolerancesMatchLookup(parsed, r2768 ? { ...r2768, toleranceType: 'plusminus' as const } : null);
};

// Helper function to determine if a rectangle should be visible based on measurement density
const shouldShowRectangle = (rect: any, currentCounter: string, density: string): boolean => {
    // Always show all rectangles - density filtering removed
    return true;
};

/** EU-style decimal comma → dot for on-screen labels (nominal / tolerances near user input). */
const formatDecimalDotForDisplay = (s: string | undefined | null): string => {
    if (s == null || s === '') return '';
    return String(s).replace(/,/g, '.');
};

// Helper to format tolerance values with signs according to tolerance type
const formatToleranceForReport = (tolerancePlus: string | number | undefined, toleranceMinus: string | number | undefined, toleranceType: 'plusminus' | 'plus' | 'minus' | undefined, isUpper: boolean): string => {
    if (tolerancePlus == null || toleranceMinus == null) {
        return '';
    }

    // Convert to string if number
    const plusStr = String(tolerancePlus);
    const minusStr = String(toleranceMinus);

    if (!plusStr || !minusStr) {
        return '';
    }

    // Remove any existing signs from the values; use dot as decimal separator in display
    const plusValue = formatDecimalDotForDisplay(plusStr.replace(/^[+\-]/, '').trim());
    const minusValue = formatDecimalDotForDisplay(minusStr.replace(/^[+\-]/, '').trim());

    if (!toleranceType || toleranceType === 'plusminus') {
        // +- logic: Upper has +, Lower has -
        return isUpper ? `+${plusValue}` : `-${minusValue}`;
    } else if (toleranceType === 'plus') {
        // ++ logic: both have +
        return isUpper ? `+${plusValue}` : `+${minusValue}`;
    } else if (toleranceType === 'minus') {
        // -- logic: both have -
        return isUpper ? `-${plusValue}` : `-${minusValue}`;
    }

    return isUpper ? plusValue : minusValue;
};

// Helper to calculate tolerance limits based on tolerance type
const calculateToleranceLimits = (nominal: number, tolerancePlus: string | number | undefined, toleranceMinus: string | number | undefined, toleranceType: 'plusminus' | 'plus' | 'minus' | undefined) => {
    const plusValue = parseTolerance(tolerancePlus);
    const minusValue = parseTolerance(toleranceMinus);

    if (!toleranceType) {
        // Default behavior: standard +/- logic
        return {
            upperLimit: nominal + plusValue,
            lowerLimit: nominal - minusValue
        };
    }

    switch (toleranceType) {
        case 'plusminus': // +- logic (standard tolerance)
            // First value is positive tolerance, second value is negative tolerance
            return {
                upperLimit: nominal + plusValue,
                lowerLimit: nominal - minusValue
            };
        case 'plus': // ++ logic (both tolerances are positive deviations)
            // Both values represent positive deviations from nominal
            return {
                upperLimit: nominal + Math.max(plusValue, minusValue),
                lowerLimit: nominal + Math.min(plusValue, minusValue)
            };
        case 'minus': // -- logic (both tolerances are negative deviations)
            // Both values represent negative deviations from nominal
            return {
                upperLimit: nominal - Math.min(plusValue, minusValue),
                lowerLimit: nominal - Math.max(plusValue, minusValue)
            };
        default:
            return {
                upperLimit: nominal + plusValue,
                lowerLimit: nominal - minusValue
            };
    }
};

// Helper to check if a value is within tolerance range, accounting for floating point precision issues
const isValueInTolerance = (value: number, lowerLimit: number, upperLimit: number): boolean => {
    // Kasuta epsilonit ujukoma täpsusega seotud vigade vältimiseks
    // Mõõtmiste täpsus kuni 0.0001, seega kasutame epsilonit 1e-8
    const epsilon = 1e-8;
    return value >= (lowerLimit - epsilon) && value <= (upperLimit + epsilon);
};

// Helper to get tolerance status: 'in' (green), 'warning' (orange 75%), 'out' (red)
const getToleranceStatus = (
    value: number,
    nominal: number,
    tolerancePlus: string | number | undefined,
    toleranceMinus: string | number | undefined,
    toleranceType: 'plusminus' | 'plus' | 'minus' | undefined
): 'in' | 'warning' | 'out' => {
    const epsilon = 1e-8;
    const { upperLimit, lowerLimit } = calculateToleranceLimits(nominal, tolerancePlus, toleranceMinus, toleranceType);

    // Check if value is within full tolerance
    if (value >= (lowerLimit - epsilon) && value <= (upperLimit + epsilon)) {
        // Calculate 75% tolerance limits
        const plusValue = parseTolerance(tolerancePlus);
        const minusValue = parseTolerance(toleranceMinus);

        let upperLimit75: number;
        let lowerLimit75: number;

        if (!toleranceType || toleranceType === 'plusminus') {
            // For ± tolerance: 75% of each direction
            upperLimit75 = nominal + (plusValue * 0.75);
            lowerLimit75 = nominal - (minusValue * 0.75);
        } else if (toleranceType === 'plus') {
            // For ++ tolerance: both positive deviations
            const max75 = Math.max(plusValue, minusValue) * 0.75;
            const min75 = Math.min(plusValue, minusValue) * 0.75;
            upperLimit75 = nominal + max75;
            lowerLimit75 = nominal + min75;
        } else {
            // For -- tolerance: both negative deviations
            const max75 = Math.max(plusValue, minusValue) * 0.75;
            const min75 = Math.min(plusValue, minusValue) * 0.75;
            upperLimit75 = nominal - min75;
            lowerLimit75 = nominal - max75;
        }

        // Check if value is within 75% tolerance (green)
        if (value >= (lowerLimit75 - epsilon) && value <= (upperLimit75 + epsilon)) {
            return 'in';
        } else {
            // Value is between 75% and 100% tolerance (orange)
            return 'warning';
        }
    } else {
        // Value is outside tolerance (red)
        return 'out';
    }
};

export default function PdfViewer({ file, excelFile, editorExcelFile, onBack, detail, order, density, counter, role, engineerName, revision, revisionId, isNewEditorRevision, onDensityChange, reportLanguage: reportLanguageProp }: PdfViewerProps) {
    const { strings } = useLanguage();
    const [ reportLanguage, setReportLanguage ] = useState<Language>(reportLanguageProp || 'et');
    const [ numPages, setNumPages ] = useState<number | null>(null);
    const [ pageNumber, setPageNumber ] = useState(1);
    const [ rects, setRects ] = useState<Array<{ x: number, y: number, width: number, height: number, number: number, text?: string, enteredNumber?: number, tolerancePlus?: string, toleranceMinus?: string, nominalDimension?: string, toleranceType?: 'plusminus' | 'plus' | 'minus', isConfirmed?: boolean, toleranceFromStandard?: boolean, hasIso286LetterDesignation?: boolean, toleranceFromIso286Standard?: boolean, toleranceManuallySet?: boolean, toleranceFromDrawingRecognition?: boolean, mDesignation?: string, mDesignationActivePart?: 'hole' | 'shaft', iso2768ChamferCalloutHint?: string }>>([]);
    const [ usernameRects, setUsernameRects ] = useState<Array<{ x: number, y: number, number: number, text?: string, enteredNumber?: number, isSubmitted?: boolean, toolWithoutMeasurement?: boolean, examSelectedToolNumber?: number }>>([]);
    const [ newSetterRect, setNewSetterRect ] = useState<{ x: number, y: number, width: number, height: number, number: number, text?: string } | null>(null);
    const [ isDrawing, setIsDrawing ] = useState(false);
    const [ isDragging, setIsDragging ] = useState(false);
    const [ isLegendBarDragging, setIsLegendBarDragging ] = useState(false);
    const [ rightMouseButtonDown, setRightMouseButtonDown ] = useState(false);
    const [ hasMovedOnRightClick, setHasMovedOnRightClick ] = useState(false);
    const dragStartRef = useRef<{ x: number, y: number, scrollLeft: number, scrollTop: number } | null>(null);
    const dragStartStagePosRef = useRef<{ x: number, y: number } | null>(null);
    const legendBarRef = useRef<HTMLDivElement>(null);
    const legendBarDragStartRef = useRef<{ x: number, scrollLeft: number } | null>(null);
    const legendBarVelocityRef = useRef<{ x: number; t: number }[]>([]);
    const legendBarMomentumRef = useRef<number | null>(null);


    // Fixed dimensions for consistent positioning across all screen sizes
    const containerRef = useRef<HTMLDivElement>(null);
    /** Prevents repeated Gemini title-block calls for the same file page when PDF text has no tolerances. */
    const iso22081GeminiPageKeyRef = useRef('');
    const centererRef = useRef<HTMLDivElement>(null);
    const [ stageSize, setStageSize ] = useState({ width: 1400, height: 1600 });
    const [ pdfSize, setPdfSize ] = useState({ width: 1400, height: 1600 });
    const [ pdfOrientation, setPdfOrientation ] = useState<'portrait' | 'landscape'>('portrait');
    const [ companyLogoDataUrl, setCompanyLogoDataUrl ] = useState<string | null>(null);
    const [ premiumUltimatePlanAllowed, setPremiumUltimatePlanAllowed ] = useState(false);

    // Reference dimensions for coordinate normalization
    const [ referenceDimensions, setReferenceDimensions ] = useState<{ width: number, height: number } | null>(null);
    const MIN_RECT_SIZE = 20;
    const MIN_DRAG_DISTANCE = 20; // Minimum distance to drag before creating rectangle
    const [ menuPosition, setMenuPosition ] = useState({ x: 0, y: 0 });
    const [ showEditorMenu, setShowEditorMenu ] = useState(false);
    const [ selectedToleranceType, setSelectedToleranceType ] = useState<'plusminus' | 'plus' | 'minus'>('plusminus');
    const [ selectedRectIndex, setSelectedRectIndex ] = useState<number | null>(null);
    const [ textInput, setTextInput ] = useState('');
    const toolInputRef = useRef<HTMLInputElement>(null);
    const stageRef = useRef<any>(null);
    // Ref for context menu to measure its real size
    const editorMenuRef = useRef<HTMLDivElement>(null);
    // Ref for user context menu to measure its real size
    const userMenuRef = useRef<HTMLDivElement>(null);
    // Ref for tolerance input group container to handle Enter key
    const toleranceInputGroupRef = useRef<HTMLDivElement>(null);
    // Ref for first OK button to set default focus
    const firstOkButtonRef = useRef<HTMLButtonElement>(null);
    // Ref for user measurement input field
    const userMeasurementInputRef = useRef<HTMLInputElement>(null);
    // Store current rect data for menu positioning
    const currentMenuRectRef = useRef<{ x: number, y: number, width: number, height: number } | null>(null);
    // Store current rect data for user menu positioning
    const currentUserMenuRectRef = useRef<{ x: number, y: number, width: number, height: number } | null>(null);
    const [ nextNumber, setNextNumber ] = useState(1);
    const [ excelData, setExcelData ] = useState<any[]>([]);
    const [ selectedCircleIndex, setSelectedCircleIndex ] = useState<number | null>(null);
    const [ circleMenuPosition, setCircleMenuPosition ] = useState({ x: 0, y: 0 });
    const [ showUserMenu, setShowUserMenu ] = useState(false);
    // Store PDF-relative coordinates for context menus (so they move with PDF scroll)
    const editorMenuPdfCoordsRef = useRef<{ x: number, y: number } | null>(null);
    const circleMenuPdfCoordsRef = useRef<{ x: number, y: number } | null>(null);
    // Refs to track current menu state for legend tool selection
    const showEditorMenuRef = useRef(false);
    const selectedRectIndexRef = useRef<number | null>(null);
    const roleRef = useRef(role);

    // Editor can switch to user view mode (no save capability)
    const [ editorViewMode, setEditorViewMode ] = useState<'editor' | 'user'>('editor');
    const effectiveRole = role === 'user' ? 'user' : editorViewMode;

    // User: MDN from embedded settings (counter prop) if present; else save count per PDF file + 1
    const effectiveCounter = (() => {
        if (effectiveRole !== 'user') return counter || '1';
        const trimmed = counter != null ? String(counter).trim() : '';
        if (trimmed !== '') return trimmed;
        return file?.name ? String(getPdfReportSaveCount(file.name) + 1) : '1';
    })();

    // Update roleRef when role changes
    useEffect(() => {
        roleRef.current = role;
    }, [ role ]);
    const [ circleTextInput, setCircleTextInput ] = useState('');
    const [ circleOkNoResult, setCircleOkNoResultState ] = useState<'ok' | 'no' | null>(null);
    // Ref must mirror ok/no so document key handlers see the value without waiting for the next render (stale closure on Enter after +/−)
    const circleOkNoResultRef = useRef<'ok' | 'no' | null>(null);
    const setCircleOkNo = (value: 'ok' | 'no' | null) => {
        circleOkNoResultRef.current = value;
        setCircleOkNoResultState(value);
    };
    const [ blinkPhase, setBlinkPhase ] = useState(0);
    const [ dashOffset, setDashOffset ] = useState(0);
    const blinkDirectionRef = useRef(1);

    useEffect(() => {
        if (!(effectiveRole === 'user' && showUserMenu && selectedCircleIndex !== null)) {
            setBlinkPhase(0);
            setDashOffset(0);
            return;
        }
        const step = 0.15;
        const interval = 35;
        blinkDirectionRef.current = 1;
        const id = setInterval(() => {
            setBlinkPhase(p => {
                const dir = blinkDirectionRef.current;
                const next = p + step * dir;
                if (next >= 1) {
                    blinkDirectionRef.current = -1;
                    return 1;
                }
                if (next <= 0) {
                    blinkDirectionRef.current = 1;
                    return 0;
                }
                return next;
            });
            // Marching ants: monotonically decreasing offset creates movement along the perimeter
            setDashOffset(o => (o - 1.2) % 1000);
        }, interval);
        return () => clearInterval(id);
    }, [ effectiveRole, showUserMenu, selectedCircleIndex ]);
    const [ toolImageOrientation, setToolImageOrientation ] = useState<'landscape' | 'portrait' | 'square' | null>(null);
    const [ tolerancePlus, setTolerancePlus ] = useState('');
    const [ toleranceMinus, setToleranceMinus ] = useState('');
    const toleranceSourceFromAutoFillRef = useRef(false);
    // True when the user manually overrides tolerance values (or used the M button).
    // Orange dot also shows {@link toleranceFromDrawingRecognition} on saved rects (drawing-specific OCR).
    const toleranceManuallyCorrectedRef = useRef(false);
    /** Set when OCR returned a tolerance tied to this dimension (ISO 286, explicit ±, etc.) */
    const drawingToleranceFromOcrRef = useRef(false);
    /** ISO 286 field designation parsed from OCR (e.g. h6, H7/g6); used to re-query tables when nominal is edited */
    const ocrIso286DesignationRef = useRef<string | null>(null);
    const [ nominalDimension, setNominalDimension ] = useState('');
    const [ recognizedValue, setRecognizedValue ] = useState('');
    const [ regionOcrLoading, setRegionOcrLoading ] = useState(false);
    const [ regionOcrError, setRegionOcrError ] = useState('');
    const nominalInputRef = useRef<HTMLInputElement>(null);
    const [ maxToolNumber, setMaxToolNumber ] = useState<number>(0);
    const [ toolInputError, setToolInputError ] = useState<string>('');
    const [ holeShaftError, setHoleShaftError ] = useState<string>('');
    const [ settingsFileError, setSettingsFileError ] = useState<string>('');
    const [ selectedLegendToolNumber, setSelectedLegendToolNumber ] = useState<number | null>(null);
    const [ companyToolsForLegend, setCompanyToolsForLegend ] = useState<Array<{
        number: number;
        name: string;
        imageUrl: string;
        color: string;
        toolWithoutMeasurement?: boolean;
        precisionClass?: string;
        minToolPrecision?: string;
    }>>([]);

    // PDF zoom state (custom zoom that only affects PDF content, not UI elements)
    const [ pdfZoom, setPdfZoom ] = useState(1);

    // OCR detected measurements state
    const [ detectedMeasurements, setDetectedMeasurements ] = useState<DetectedTextItem[]>([]);
    const [ isDetecting, setIsDetecting ] = useState(false);
    // Editor: start with PDF.js hits hidden; badge still shows count after initial OCR until user toggles on.
    const [ showDetectedMeasurements, setShowDetectedMeasurements ] = useState(role !== 'editor');
    const [ isOcrLoading, setIsOcrLoading ] = useState(false);
    /** Full-viewer overlay while PDF report / settings PDF is being generated and written. */
    const [ isSavingReport, setIsSavingReport ] = useState(false);
    const [ pageLoadedForOcr, setPageLoadedForOcr ] = useState(false);
    const ocrRunRef = useRef(false);

    // G button recognition: 10x10 rects on each recognized place

    const [ g2UserSelectedStandard, setG2UserSelectedStandard ] = useState<string | null>(null);
    const [ isoRecognizedSentences, setIsoRecognizedSentences ] = useState<string[]>([]);
    const [ editorRecognizedStandardPanelCollapsed, setEditorRecognizedStandardPanelCollapsed ] = useState(false);
    const [ iso286Designation, setIso286Designation ] = useState<string | null>(null);
    const [ iso286UserSelectedHole, setIso286UserSelectedHole ] = useState<string | null>(null);
    const [ iso286UserSelectedShaft, setIso286UserSelectedShaft ] = useState<string | null>(null);
    const [ iso286ActiveButton, setIso286ActiveButton ] = useState<'hole' | 'shaft' | null>(null);
    const [ iso286MOpen, setIso286MOpen ] = useState(false);
    const iso286MDropdownRef = useRef<HTMLDivElement>(null);
    const [ g2UserSelectedShapeStandard, setG2UserSelectedShapeStandard ] = useState<string | null>(null);
    const [ iso22081LinearSizes, setIso22081LinearSizes ] = useState('');
    const [ iso22081ShapeSizes, setIso22081ShapeSizes ] = useState('');
    const [ iso22081AngularSizes, setIso22081AngularSizes ] = useState('');
    const [ iso22081RadiusChamferSizes, setIso22081RadiusChamferSizes ] = useState('');
    const toleranceHadIso286LetterRef = useRef(false);
    const mDesignationRef = useRef<string | null>(null);
    const prevEffectiveIso286DesignationRef = useRef<string | null>(null);
    const prevEffectiveGeneralStandardRef = useRef<string | null>(null);
    const [ showHelpMenu, setShowHelpMenu ] = useState(false);
    const helpMenuRef = useRef<HTMLDivElement>(null);
    const [ showSettingsMenu, setShowSettingsMenu ] = useState(false);
    const settingsMenuRef = useRef<HTMLDivElement>(null);
    const [ sequenceMeasurement, setSequenceMeasurement ] = useState(role === 'editor');
    const [ examMode, setExamMode ] = useState(false);
    const [ examSelectedTool, setExamSelectedTool ] = useState<number | null>(null);
    const [ examToolDropdownOpen, setExamToolDropdownOpen ] = useState(false);
    const examToolDropdownRef = useRef<HTMLDivElement>(null);
    const downloadDirHandleRef = useRef<FileSystemDirectoryHandle | null>(null);
    /** Editor: folder for PDF with embedded settings (separate from report folder). */
    const settingsPdfDirHandleRef = useRef<FileSystemDirectoryHandle | null>(null);
    /** IndexedDB key from embedded settings (DetailNumber/Revision); editor saves folder under this key. */
    const userExcelFolderKeyRef = useRef<string | null>(null);
    /** Editor: report folder key from embedded setup sheet (DetailNumber/Revision in file). */
    const editorEmbeddedReportFolderKeyRef = useRef<string | null>(null);
    const [ downloadPathDisplayName, setDownloadPathDisplayName ] = useState<string>('');
    const [ settingsPdfPathDisplayName, setSettingsPdfPathDisplayName ] = useState('');
    const [ saveSuccessModal, setSaveSuccessModal ] = useState<{
        title: string;
        fileName: string;
        folderName: string;
        /** When true, folder label/value are hidden (e.g. saved to local server only). */
        hideFolder?: boolean;
        pdfBlobUrl?: string;
        onClose?: () => void;
    } | null>(null);
    /** Blocking modal when local-server save/publish fails (server-only flows). */
    const [ localServerSaveErrorModal, setLocalServerSaveErrorModal ] = useState<{
        title: string;
        message: string;
    } | null>(null);
    const [ orderNumberModalForEditor, setOrderNumberModalForEditor ] = useState(false);
    const [ orderNumberInputForSave, setOrderNumberInputForSave ] = useState('');
    const [ editorPdfMeasuredDetailNumberForSave, setEditorPdfMeasuredDetailNumberForSave ] = useState('');
    /** After embedded Excel (or blank-PDF draft) is applied; avoids overwriting session draft with empty initial state. */
    const [ editorDraftHydrated, setEditorDraftHydrated ] = useState(false);
    const suppressEditorDraftPersistRef = useRef(false);

    useEffect(() => {
        setEditorDraftHydrated(false);
    }, [ file?.name, file?.size, detail, revision, role ]);

    useEffect(() => {
        if (role === 'editor') {
            suppressEditorDraftPersistRef.current = false;
        }
    }, [ rects, sequenceMeasurement, examMode, referenceDimensions, nextNumber, role ]);

    useEffect(() => {
        if (role !== 'editor' || !file || !editorDraftHydrated || suppressEditorDraftPersistRef.current) return;
        writeEditorWorkDraft(file, detail, revision, {
            v: EDITOR_WORK_DRAFT_VERSION,
            rects,
            referenceDimensions,
            sequenceMeasurement,
            examMode,
            nextNumber,
            downloadPathDisplayName: downloadPathDisplayName || '',
        });
    }, [
        role,
        file,
        detail,
        revision,
        editorDraftHydrated,
        rects,
        referenceDimensions,
        sequenceMeasurement,
        examMode,
        nextNumber,
        downloadPathDisplayName,
    ]);

    const folderKey = buildFolderKey(detail, revision);
    const settingsPdfFolderKey = buildSettingsPdfFolderKey(detail, revision);

    const resolveReportFolderPersistKey = (): string => {
        if (role === 'user' && userExcelFolderKeyRef.current) {
            return userExcelFolderKeyRef.current;
        }
        if (role === 'editor' && editorEmbeddedReportFolderKeyRef.current) {
            return editorEmbeddedReportFolderKeyRef.current;
        }
        return folderKey;
    };

    const openPdfSettingsMenu = () => {
        setShowSettingsMenu(true);
    };

    const handleEditorSettingsChooseReportFolder = async () => {
        if (!('showDirectoryPicker' in window)) {
            logger.error('[PdfViewer] showDirectoryPicker not supported in this browser');
            return;
        }
        try {
            const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
            downloadDirHandleRef.current = dirHandle;
            setDownloadPathDisplayName(dirHandle.name);
            const persistFolderKey = resolveReportFolderPersistKey();
            await saveDownloadDirHandle(dirHandle, persistFolderKey);
        } catch (err) {
            if ((err as Error).name !== 'AbortError') {
                logger.error('[PdfViewer] Failed to choose folder:', err);
            }
        }
    };

    const handleEditorSettingsClearReportFolder = async () => {
        downloadDirHandleRef.current = null;
        setDownloadPathDisplayName('');
        const persistFolderKey = resolveReportFolderPersistKey();
        await clearDownloadDirHandle(persistFolderKey).catch(() => {});
    };

    const handleEditorSettingsChooseSettingsPdfFolder = async () => {
        if (!('showDirectoryPicker' in window)) {
            logger.error('[PdfViewer] showDirectoryPicker not supported in this browser');
            return;
        }
        try {
            const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
            settingsPdfDirHandleRef.current = dirHandle;
            setSettingsPdfPathDisplayName(dirHandle.name);
            await saveDownloadDirHandle(dirHandle, settingsPdfFolderKey);
        } catch (err) {
            if ((err as Error).name !== 'AbortError') {
                logger.error('[PdfViewer] Failed to choose settings PDF folder:', err);
            }
        }
    };

    const handleEditorSettingsClearSettingsPdfFolder = async () => {
        settingsPdfDirHandleRef.current = null;
        setSettingsPdfPathDisplayName('');
        await clearDownloadDirHandle(settingsPdfFolderKey).catch(() => {});
    };

    const handleEditorSettingsReportLanguageChange = (lang: Language) => {
        setReportLanguage(lang);
        try {
            localStorage.setItem('reportLanguage', lang);
        } catch {
            // ignore
        }
    };

    useEffect(() => {
        // User + embedded Excel: folder handle is keyed by DetailNumber in settings; loading only folderKey from home would miss it or overwrite.
        if (role === 'user' && excelFile) {
            return;
        }
        // Editor + embedded setup: processEditorExcel loads handle by DetailNumber from file; do not overwrite with props folderKey.
        if (role === 'editor' && editorExcelFile) {
            return;
        }
        getDownloadDirHandle(folderKey).then((handle) => {
            if (handle) {
                downloadDirHandleRef.current = handle;
                setDownloadPathDisplayName(handle.name);
            }
        }).catch(() => {});
    }, [ folderKey, role, excelFile, editorExcelFile ]);

    useEffect(() => {
        if (role !== 'editor') {
            settingsPdfDirHandleRef.current = null;
            setSettingsPdfPathDisplayName('');
            return;
        }
        getDownloadDirHandle(settingsPdfFolderKey).then((handle) => {
            if (handle) {
                settingsPdfDirHandleRef.current = handle;
                setSettingsPdfPathDisplayName(handle.name);
            } else {
                settingsPdfDirHandleRef.current = null;
                setSettingsPdfPathDisplayName('');
            }
        }).catch(() => {});
    }, [ settingsPdfFolderKey, role ]);

    useEffect(() => {
        if (role !== 'user' || !excelFile) {
            userExcelFolderKeyRef.current = null;
        }
    }, [ role, excelFile ]);

    /** Must run while user activation is still valid; long async work after this can invalidate gesture for requestPermission. */
    const ensureDirectoryWritePermission = async (handle: FileSystemDirectoryHandle): Promise<boolean> => {
        try {
            const h = handle as { queryPermission?: (opts: { mode: string }) => Promise<string>; requestPermission?: (opts: { mode: string }) => Promise<string> };
            let permission = h.queryPermission ? await h.queryPermission({ mode: 'readwrite' }) : 'granted';
            if (permission !== 'granted' && h.requestPermission) {
                permission = await h.requestPermission({ mode: 'readwrite' });
            }
            return permission === 'granted';
        } catch (err) {
            logger.error('[PdfViewer] Folder write permission failed:', err);
            return false;
        }
    };

    const writeBlobToHandle = async (handle: FileSystemDirectoryHandle, blob: Blob, fileName: string): Promise<boolean> => {
        try {
            const ok = await ensureDirectoryWritePermission(handle);
            if (!ok) return false;
            const fileHandle = await handle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            return true;
        } catch (err) {
            logger.error('[PdfViewer] Failed to save to chosen folder:', err);
            return false;
        }
    };

    const ensureDownloadFolderHandle = async (): Promise<{ success: boolean; displayPath: string }> => {
        const finalizeWithPermission = async (
            handle: FileSystemDirectoryHandle,
            displayPath: string
        ): Promise<{ success: boolean; displayPath: string } | null> => {
            downloadDirHandleRef.current = handle;
            const ok = await ensureDirectoryWritePermission(handle);
            if (ok) {
                setDownloadPathDisplayName(displayPath);
                return { success: true, displayPath };
            }
            downloadDirHandleRef.current = null;
            return null;
        };

        const keysToTry: string[] = [];
        if (role === 'user' && userExcelFolderKeyRef.current) {
            keysToTry.push(userExcelFolderKeyRef.current);
        }
        if (role === 'editor' && editorEmbeddedReportFolderKeyRef.current) {
            const ek = editorEmbeddedReportFolderKeyRef.current;
            if (!keysToTry.includes(ek)) {
                keysToTry.push(ek);
            }
        }
        if (!keysToTry.includes(folderKey)) {
            keysToTry.push(folderKey);
        }

        // Prefer IndexedDB for report folder so in-memory ref cannot override with wrong handle
        // (e.g. same session as settings PDF folder).
        try {
            for (const k of keysToTry) {
                const stored = await getDownloadDirHandle(k);
                if (stored) {
                    const done = await finalizeWithPermission(stored, stored.name);
                    if (done) return done;
                }
            }
        } catch {
            // ignore
        }

        if (downloadDirHandleRef.current) {
            let skipCachedRef = false;
            if (role === 'editor' && settingsPdfDirHandleRef.current) {
                try {
                    const dl = downloadDirHandleRef.current as FileSystemDirectoryHandle & {
                        isSameEntry?: (other: FileSystemHandle) => Promise<boolean>;
                    };
                    if (typeof dl.isSameEntry === 'function') {
                        skipCachedRef = await dl.isSameEntry(settingsPdfDirHandleRef.current);
                    }
                } catch {
                    skipCachedRef = false;
                }
            }
            if (!skipCachedRef) {
                const done = await finalizeWithPermission(
                    downloadDirHandleRef.current,
                    downloadDirHandleRef.current.name
                );
                if (done) return done;
            }
        }

        if (!('showDirectoryPicker' in window)) return { success: false, displayPath: '' };
        try {
            const picked = await (window as any).showDirectoryPicker({ mode: 'readwrite' }) as FileSystemDirectoryHandle;
            const done = await finalizeWithPermission(picked, picked.name);
            if (done) {
                await saveDownloadDirHandle(picked, resolveReportFolderPersistKey());
                return done;
            }
            return { success: false, displayPath: '' };
        } catch (err) {
            if ((err as Error).name !== 'AbortError') {
                logger.error('[PdfViewer] Failed to choose folder:', err);
            }
            return { success: false, displayPath: '' };
        }
    };

    const ensureSettingsPdfFolderHandle = async (): Promise<{ success: boolean; displayPath: string }> => {
        const finalizeWithPermission = async (
            handle: FileSystemDirectoryHandle,
            displayPath: string
        ): Promise<{ success: boolean; displayPath: string } | null> => {
            settingsPdfDirHandleRef.current = handle;
            const ok = await ensureDirectoryWritePermission(handle);
            if (ok) {
                setSettingsPdfPathDisplayName(displayPath);
                return { success: true, displayPath };
            }
            settingsPdfDirHandleRef.current = null;
            return null;
        };

        if (settingsPdfDirHandleRef.current) {
            const done = await finalizeWithPermission(
                settingsPdfDirHandleRef.current,
                settingsPdfDirHandleRef.current.name
            );
            if (done) return done;
        }
        try {
            const stored = await getDownloadDirHandle(settingsPdfFolderKey);
            if (stored) {
                const done = await finalizeWithPermission(stored, stored.name);
                if (done) return done;
            }
        } catch {
            // ignore
        }
        // Editors no longer choose a settings-PDF folder in the PDF settings menu;
        // use a persisted handle if any, otherwise browser download on save.
        if (role === 'editor') {
            return { success: false, displayPath: '' };
        }
        if (!('showDirectoryPicker' in window)) return { success: false, displayPath: '' };
        try {
            const picked = await (window as any).showDirectoryPicker({ mode: 'readwrite' }) as FileSystemDirectoryHandle;
            const done = await finalizeWithPermission(picked, picked.name);
            if (done) {
                await saveDownloadDirHandle(picked, settingsPdfFolderKey);
                return done;
            }
            return { success: false, displayPath: '' };
        } catch (err) {
            if ((err as Error).name !== 'AbortError') {
                logger.error('[PdfViewer] Failed to choose settings PDF folder:', err);
            }
            return { success: false, displayPath: '' };
        }
    };

    const saveBlobToChosenFolder = async (blob: Blob, fileName: string): Promise<boolean> => {
        const handle = downloadDirHandleRef.current;
        if (!handle) return false;
        return writeBlobToHandle(handle, blob, fileName);
    };

    const saveSettingsPdfBlobToChosenFolder = async (blob: Blob, fileName: string): Promise<boolean> => {
        const handle = settingsPdfDirHandleRef.current;
        if (!handle) return false;
        return writeBlobToHandle(handle, blob, fileName);
    };

    const getFirstUnmeasuredVisibleIndex = (rectsToUse: typeof usernameRects = usernameRects): number | null => {
        const withSetter = rectsToUse.map((ur, idx) => {
            const setterRect = rects.find(r => r.number === ur.number);
            return { idx, ur, setterRect };
        }).filter(({ setterRect }) => setterRect && shouldShowRectangle(setterRect, effectiveCounter || '', density || ''));
        const unmeasured = withSetter.filter(({ ur }) => !ur.isSubmitted).sort((a, b) => a.ur.number - b.ur.number);
        return unmeasured.length > 0 ? unmeasured[ 0 ].idx : null;
    };

    const centerViewOnRect = (rect: { x: number, y: number, width: number, height: number }) => {
        const centerer = centererRef.current;
        if (!centerer) return;
        const rectCenterX = rect.x + rect.width / 2;
        const rectCenterY = rect.y + rect.height / 2;
        const scaledX = rectCenterX * pdfZoom;
        const scaledY = rectCenterY * pdfZoom;
        const maxScrollLeft = Math.max(0, centerer.scrollWidth - centerer.clientWidth);
        const maxScrollTop = Math.max(0, centerer.scrollHeight - centerer.clientHeight);
        const newScrollLeft = Math.max(0, Math.min(scaledX - centerer.clientWidth / 2, maxScrollLeft));
        const newScrollTop = Math.max(0, Math.min(scaledY - centerer.clientHeight / 2, maxScrollTop));
        centerer.scrollTo({ left: newScrollLeft, top: newScrollTop, behavior: 'smooth' });
    };

    const activateSequenceRect = (index: number) => {
        const usernameRect = usernameRects[ index ];
        const setterRect = rects.find(r => r.number === usernameRect.number);
        if (!setterRect) return;
        currentUserMenuRectRef.current = setterRect;
        const menuWidth = 300;
        const centerer = centererRef.current;
        if (centerer) {
            setCircleMenuPosition({
                x: centerer.scrollLeft + (centerer.clientWidth - menuWidth) / 2,
                y: centerer.scrollTop + 10
            });
        }
        circleMenuPdfCoordsRef.current = { x: setterRect.x + setterRect.width / 2, y: setterRect.y + setterRect.height / 2 };
        setShowUserMenu(true);
        setSelectedCircleIndex(index);
        setCircleTextInput(usernameRect.text || '');
        setCircleOkNo(null);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => centerViewOnRect(setterRect));
        });
    };

    // Coordinate normalization functions
    const normalizeCoordinates = (absoluteCoords: { x: number, y: number, width: number, height: number }, refDimensions: { width: number, height: number }) => {
        return {
            x: absoluteCoords.x / refDimensions.width,
            y: absoluteCoords.y / refDimensions.height,
            width: absoluteCoords.width / refDimensions.width,
            height: absoluteCoords.height / refDimensions.height
        };
    };

    const denormalizeCoordinates = (relativeCoords: { x: number, y: number, width: number, height: number }, currentDimensions: { width: number, height: number }) => {
        return {
            x: relativeCoords.x * currentDimensions.width,
            y: relativeCoords.y * currentDimensions.height,
            width: relativeCoords.width * currentDimensions.width,
            height: relativeCoords.height * currentDimensions.height
        };
    };

    // Use fixed dimensions for consistent positioning across all screen sizes
    useEffect(() => {
        // Set fixed dimensions that work well on all screen sizes
        const fixedSize = { width: 1400, height: 1600 };
        setStageSize(fixedSize);
        setPdfSize(fixedSize);
        setReferenceDimensions(fixedSize);


    }, []);

    // Sync report language from parent prop (includes localStorage via PdfViewerPage) or localStorage fallback
    useEffect(() => {
        if (reportLanguageProp && (reportLanguageProp === 'et' || reportLanguageProp === 'en' || reportLanguageProp === 'ru')) {
            setReportLanguage(reportLanguageProp);
            logger.log('[PdfViewer] Report language from prop:', reportLanguageProp);
            return;
        }
        const savedReportLanguage = localStorage.getItem('reportLanguage') as Language;
        if (savedReportLanguage === 'et' || savedReportLanguage === 'en' || savedReportLanguage === 'ru') {
            setReportLanguage(savedReportLanguage);
            logger.log('[PdfViewer] Report language from localStorage:', savedReportLanguage);
            return;
        }
        setReportLanguage('et');
        logger.log('[PdfViewer] Using default report language: et');
    }, [ reportLanguageProp ]);

    // Load company logo from cache (loaded once at login, no Storage API requests).
    // Fallback: if cache is missing/old, load logo URL and store data URL back to cache.
    useEffect(() => {
        const loadLogo = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    return;
                }

                logger.log('[PdfViewer] Getting company logo from cache for user:', currentUser.uid);
                const cachedLogoDataUrl = getCachedCompanyLogoDataUrl(currentUser.uid);

                if (cachedLogoDataUrl) {
                    setCompanyLogoDataUrl(cachedLogoDataUrl);
                    logger.log('[PdfViewer] Company logo loaded from cache for report');
                    return;
                }

                logger.log('[PdfViewer] No cached company logo data URL, loading fallback logo');

                // Try to use cached company profile to determine logo URL without extra requests.
                const companyProfile = await getCompanyProfileCached(currentUser.uid);
                const logoUrlToLoad =
                    companyProfile?.logoUrl ||
                    (companyProfile?.logoFileName === DEFAULT_COMPANY_LOGO_URL ? DEFAULT_COMPANY_LOGO_URL : null) ||
                    DEFAULT_COMPANY_LOGO_URL;

                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = logoUrlToLoad;

                await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error('Failed to load company logo image'));
                });

                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return;
                }
                ctx.drawImage(img, 0, 0);
                const dataUrl = canvas.toDataURL('image/png');
                setCompanyLogoDataUrl(dataUrl);

                // Store back into session cache so next report uses cached data URL.
                try {
                    const cacheKey = `user_cache_${currentUser.uid}`;
                    const cachedRaw = sessionStorage.getItem(cacheKey);
                    if (cachedRaw) {
                        const parsed = JSON.parse(cachedRaw);
                        parsed.companyLogoDataUrl = dataUrl;
                        sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
                        logger.log('[PdfViewer] Stored company logo data URL back into cache');
                    }
                } catch (cacheWriteError) {
                    logger.warn('[PdfViewer] Failed to write logo data URL to cache:', cacheWriteError);
                }
            } catch (error) {
                logger.error('Error loading company logo for report:', error);
            }
        };

        loadLogo();
    }, []);

    // Helper function to get tool name by number
    const getToolNameByNumber = (toolNumber: number, strings: any): string => {
        switch (toolNumber) {
            case 1:
                return strings.CALIBER_TOOL;
            case 2:
                return strings.MICROMETER;
            case 3:
                return strings.DIGITAL_INSIDE_MICROMETER;
            case 4:
                return strings.STOPWATCH;
            case 5:
                return strings.HEIGHT_MEASURING_JACK;
            case 6:
                return strings.SCREW_DRIVER;
            case 7:
                return strings.TOOL_7 || `Tööriist 7`;
            case 8:
                return strings.TOOL_8 || `Tööriist 8`;
            case 9:
                return strings.TOOL_9 || `Tööriist 9`;
            case 10:
                return strings.TOOL_10 || `Tööriist 10`;
            case 11:
                return strings.TOOL_11 || `Tööriist 11`;
            case 12:
                return strings.MULTIMETER || 'Multimeeter';
            default:
                return `Tööriist ${toolNumber}`;
        }
    };

    // Helper function to get tool image path by number
    const getToolImagePathByNumber = (toolNumber: number): string => {
        switch (toolNumber) {
            case 1:
                return '/assets/measurement-tools/6.png';
            case 2:
                return '/assets/measurement-tools/5.png';
            case 3:
                return '/assets/measurement-tools/1.png';
            case 4:
                return '/assets/measurement-tools/2.png';
            case 5:
                return '/assets/measurement-tools/3.png';
            case 6:
                return '/assets/measurement-tools/4.png';
            case 7:
                return '/assets/measurement-tools/7.png';
            case 8:
                return '/assets/measurement-tools/8.png';
            case 9:
                return '/assets/measurement-tools/9.png';
            case 10:
                return '/assets/measurement-tools/10.png';
            case 11:
                return '/assets/measurement-tools/11.png';
            case 12:
                return '/assets/measurement-tools/multimeter.png';
            default:
                return '';
        }
    };

    // Load company tools for legend and editor validation
    useEffect(() => {
        const loadCompanyTools = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    setPremiumUltimatePlanAllowed(false);
                    return;
                }

                logger.log('[PdfViewer] Loading company tools:', currentUser.uid);
                // Use cached company profile
                const companyProfile = await getCompanyProfileCached(currentUser.uid);
                if (!companyProfile) {
                    logger.log('[PdfViewer] No company profile found');
                    setPremiumUltimatePlanAllowed(false);
                    return;
                }

                setPremiumUltimatePlanAllowed(isPremiumOrUltimatePlan(companyProfile.selectedPlan));

                // Build tools array for legend
                const toolsForLegend: Array<{
                    number: number;
                    name: string;
                    imageUrl: string;
                    color: string;
                    toolWithoutMeasurement?: boolean;
                    precisionClass?: string;
                    minToolPrecision?: string;
                }> = [];

                // Combine standard and custom tools in order they appear
                // Get tools array (unified structure)
                const tools = companyProfile.tools || [];

                // Number tools sequentially starting from 1
                let sequentialNumber = 1;

                // Add all tools in order they appear in array
                tools.forEach((tool: Tool) => {
                    let imageUrl = '';
                    if (tool.type === 'standard') {
                        // Standard tool - use image path
                        imageUrl = tool.imageUrl || getToolImagePathByNumber(tool.toolNumber || 1);
                    } else {
                        // Custom tool - use cached image URL if available
                        imageUrl = tool.imageUrl
                            ? getCachedImageUrl(currentUser.uid, tool.imageUrl)
                            : '';
                    }

                    toolsForLegend.push({
                        number: sequentialNumber,
                        name: tool.name,
                        imageUrl: imageUrl,
                        color: tool.color,
                        toolWithoutMeasurement: tool.toolWithoutMeasurement,
                        precisionClass: tool.precisionClass,
                        minToolPrecision: tool.minToolPrecision,
                    });
                    sequentialNumber++;
                });

                setCompanyToolsForLegend(toolsForLegend);

                // Calculate max tool number based on actual company tools
                const totalTools = tools.length;
                setMaxToolNumber(totalTools);
                logger.log('[PdfViewer] Max tool number set to:', totalTools);

                logger.log('[PdfViewer] Company tools loaded for legend:', toolsForLegend.length);
            } catch (error) {
                logger.error('Error loading company tools:', error);
                setPremiumUltimatePlanAllowed(false);
            }
        };

        loadCompanyTools();
    }, [ role, strings ]);

    // Update usernameRects when companyToolsForLegend changes (e.g., after tool deletion)
    // This ensures that toolWithoutMeasurement settings are preserved from original tools
    // Important: toolWithoutMeasurement should NEVER change - it's set when measurement is created
    // and must remain the same even if tools are reordered or deleted
    useEffect(() => {
        if (effectiveRole === 'user' && usernameRects.length > 0 && companyToolsForLegend.length > 0) {
            setUsernameRects(currentUsernameRects => {
                return currentUsernameRects.map(usernameRect => {
                    // Preserve toolWithoutMeasurement - it was set when measurement was created
                    // and should never change, even if tools are deleted or reordered
                    // Only update if toolWithoutMeasurement is not set yet (for backward compatibility)
                    if (usernameRect.toolWithoutMeasurement === undefined && usernameRect.enteredNumber) {
                        const toolIndex = usernameRect.enteredNumber - 1;
                        if (toolIndex >= 0 && toolIndex < companyToolsForLegend.length) {
                            const currentTool = companyToolsForLegend[toolIndex];
                            if (currentTool && currentTool.toolWithoutMeasurement !== undefined) {
                                return {
                                    ...usernameRect,
                                    toolWithoutMeasurement: currentTool.toolWithoutMeasurement
                                };
                            }
                        }
                    }
                    
                    // Always preserve existing toolWithoutMeasurement - never overwrite it
                    return usernameRect;
                });
            });
        }
    }, [ companyToolsForLegend, role ]);

    // Set default selected tool for editor role
    useEffect(() => {
        if (effectiveRole === 'editor' && companyToolsForLegend.length > 0 && selectedLegendToolNumber === null) {
            setSelectedLegendToolNumber(1);
        }
    }, [ role, companyToolsForLegend, selectedLegendToolNumber ]);

    // Refs for two-digit tool selection
    const firstDigitRef = useRef<number | null>(null);
    const twoDigitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Handle keyboard shortcuts for tool selection (editor role only)
    useEffect(() => {
        if (effectiveRole !== 'editor') {
            return;
        }

        const handleToolSelectionKey = (e: KeyboardEvent) => {
            // Don't handle if focus is on an input field
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                // Clear any pending two-digit selection when typing in input
                if (firstDigitRef.current !== null) {
                    firstDigitRef.current = null;
                    if (twoDigitTimeoutRef.current) {
                        clearTimeout(twoDigitTimeoutRef.current);
                        twoDigitTimeoutRef.current = null;
                    }
                }
                return;
            }

            // Only work when no context menu is visible
            if (showEditorMenu || showUserMenu) {
                // Clear any pending two-digit selection when menu is open
                if (firstDigitRef.current !== null) {
                    firstDigitRef.current = null;
                    if (twoDigitTimeoutRef.current) {
                        clearTimeout(twoDigitTimeoutRef.current);
                        twoDigitTimeoutRef.current = null;
                    }
                }
                return;
            }

            // Check if key is a digit (0-9)
            const digit = parseInt(e.key);
            if (isNaN(digit) || digit < 0 || digit > 9) {
                // Clear any pending two-digit selection on non-digit key
                if (firstDigitRef.current !== null) {
                    firstDigitRef.current = null;
                    if (twoDigitTimeoutRef.current) {
                        clearTimeout(twoDigitTimeoutRef.current);
                        twoDigitTimeoutRef.current = null;
                    }
                }
                return;
            }

            // Clear any existing timeout
            if (twoDigitTimeoutRef.current) {
                clearTimeout(twoDigitTimeoutRef.current);
                twoDigitTimeoutRef.current = null;
            }

            // If we have a first digit, try to form a two-digit number
            if (firstDigitRef.current !== null) {
                const twoDigitNumber = firstDigitRef.current * 10 + digit;

                // Check if this tool number exists
                if (twoDigitNumber <= maxToolNumber && twoDigitNumber > 0) {
                    // Valid two-digit number - select it
                    setSelectedLegendToolNumber(twoDigitNumber);
                    firstDigitRef.current = null;
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                } else {
                    // Invalid two-digit number, treat current digit as new first digit
                    const previousFirstDigit = firstDigitRef.current;
                    firstDigitRef.current = null;

                    // Process current digit as new first digit or single digit
                    if (digit >= 1 && digit <= 9) {
                        const potentialTwoDigit = digit * 10;
                        if (potentialTwoDigit <= maxToolNumber) {
                            // Select single digit tool immediately
                            if (digit <= maxToolNumber) {
                                setSelectedLegendToolNumber(digit);
                            }
                            // Store as first digit and wait for second digit
                            firstDigitRef.current = digit;
                            twoDigitTimeoutRef.current = setTimeout(() => {
                                firstDigitRef.current = null;
                                twoDigitTimeoutRef.current = null;
                            }, 1000);
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        } else if (digit <= maxToolNumber) {
                            // Can't form two-digit, select as single digit
                            setSelectedLegendToolNumber(digit);
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
            }

            // No first digit stored, process current digit
            // Check if digit can be first digit of two-digit number
            if (digit >= 1 && digit <= 9) {
                const potentialTwoDigit = digit * 10;
                if (potentialTwoDigit <= maxToolNumber) {
                    // Select single digit tool immediately
                    if (digit <= maxToolNumber) {
                        setSelectedLegendToolNumber(digit);
                    }
                    // Also store as first digit and wait for second digit to potentially override
                    firstDigitRef.current = digit;
                    twoDigitTimeoutRef.current = setTimeout(() => {
                        // Clear first digit after timeout (tool already selected)
                        firstDigitRef.current = null;
                        twoDigitTimeoutRef.current = null;
                    }, 1000);
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                } else if (digit <= maxToolNumber) {
                    // Can't form two-digit, select as single digit immediately
                    setSelectedLegendToolNumber(digit);
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
            }

            // Handle digit 0 - can only be second digit of two-digit number
            if (digit === 0) {
                // If no first digit, ignore 0
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        };

        document.addEventListener('keydown', handleToolSelectionKey, { capture: true });

        return () => {
            document.removeEventListener('keydown', handleToolSelectionKey, { capture: true });
            // Cleanup timeout on unmount
            if (twoDigitTimeoutRef.current) {
                clearTimeout(twoDigitTimeoutRef.current);
                twoDigitTimeoutRef.current = null;
            }
        };
    }, [ role, showEditorMenu, showUserMenu, maxToolNumber ]);

    useEffect(() => {
        if (effectiveRole !== 'editor') return;
        const handleWindowFocus = () => {
            const active = document.activeElement;
            const isEditable = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || (active instanceof HTMLElement && active.isContentEditable);
            if (!isEditable && centererRef.current) {
                centererRef.current.focus();
            }
        };
        window.addEventListener('focus', handleWindowFocus);
        return () => window.removeEventListener('focus', handleWindowFocus);
    }, [ effectiveRole ]);

    // Auto-scroll legend bar to show selected tool
    useEffect(() => {
        if (effectiveRole !== 'editor' || selectedLegendToolNumber === null || !legendBarRef.current) {
            return;
        }

        // Find the selected tool element
        const selectedToolElement = legendBarRef.current.querySelector(
            `[data-tool-number="${selectedLegendToolNumber}"]`
        ) as HTMLElement;

        if (selectedToolElement && legendBarRef.current) {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
                const container = legendBarRef.current;
                if (!container) return;

                const elementLeft = selectedToolElement.offsetLeft;
                const elementWidth = selectedToolElement.offsetWidth;
                const containerWidth = container.clientWidth;
                const containerScrollLeft = container.scrollLeft;

                // Calculate target scroll position to center the element
                const targetScrollLeft = elementLeft - (containerWidth / 2) + (elementWidth / 2);

                // Smooth scroll with custom duration (800ms for slower scrolling)
                const startScrollLeft = containerScrollLeft;
                const distance = targetScrollLeft - startScrollLeft;
                const duration = 800; // milliseconds
                let startTime: number | null = null;

                const animateScroll = (currentTime: number) => {
                    if (startTime === null) {
                        startTime = currentTime;
                    }
                    const timeElapsed = currentTime - startTime;
                    const progress = Math.min(timeElapsed / duration, 1);

                    // Easing function for smooth animation (ease-in-out)
                    const easeInOut = progress < 0.5
                        ? 2 * progress * progress
                        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

                    if (container) {
                        container.scrollLeft = startScrollLeft + distance * easeInOut;
                    }

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }, 0);
        }
    }, [ role, selectedLegendToolNumber ]);

    // Apply CSS classes based on PDF orientation
    useEffect(() => {
        const viewer = document.querySelector('.pdf-viewer');
        const centerer = document.querySelector('.pdf-canvas-centerer');
        const container = document.querySelector('.pdf-konva-container');
        const wrapper = document.querySelector('.pdf-canvas-wrapper');
        const stage = document.querySelector('.stage-absolute');

        if (viewer && centerer && container && wrapper && stage) {
            if (pdfOrientation === 'landscape') {
                viewer.classList.add('landscape');
                centerer.classList.add('landscape');
                container.classList.add('landscape');
                wrapper.classList.add('landscape');
                stage.classList.add('landscape');
            } else {
                viewer.classList.remove('landscape');
                centerer.classList.remove('landscape');
                container.classList.remove('landscape');
                wrapper.classList.remove('landscape');
                stage.classList.remove('landscape');
            }
        }
    }, [ pdfOrientation ]);





    // Auto-focus and select nominal measurement field when context menu opens in editor mode
    useEffect(() => {
        if (showEditorMenu && effectiveRole === 'editor' && nominalInputRef.current) {
            // Use setTimeout to ensure the input is rendered before focusing
            setTimeout(() => {
                if (nominalInputRef.current) {
                    nominalInputRef.current.focus();
                    nominalInputRef.current.select();
                }
            }, 50);
        }
    }, [ showEditorMenu, role ]);

    useEffect(() => {
        if (!showEditorMenu) {
            setRegionOcrLoading(false);
            setRegionOcrError('');
        }
    }, [ showEditorMenu ]);

    // Auto-focus nominal measurement input when context menu opens in editor mode
    useEffect(() => {
        if (showEditorMenu && effectiveRole === 'editor') {
            // Use setTimeout to ensure the input is fully rendered and value is updated
            setTimeout(() => {
                if (!showEditorMenu) return; // Menu was closed during timeout

                // Focus on nominal measurement input first (nominal dimension field)
                if (nominalInputRef.current) {
                    const input = nominalInputRef.current;
                    input.focus();
                    // Use setSelectionRange for more reliable text selection
                    if (input.value.length > 0) {
                        input.setSelectionRange(0, input.value.length);
                    }
                } else if (toolInputRef.current) {
                    // Fallback to tool input
                    const input = toolInputRef.current;
                    input.focus();
                    if (input.value.length > 0) {
                        input.setSelectionRange(0, input.value.length);
                    }
                }
            }, 150);
        }
    }, [ showEditorMenu, role, selectedRectIndex ]);

    // Measure real menu size and adjust position after menu renders
    useLayoutEffect(() => {
        if (showEditorMenu && editorMenuRef.current) {
            const menu = editorMenuRef.current;
            const menuWidth = menu.offsetWidth;
            const menuHeight = menu.offsetHeight;
            const centerer = centererRef.current;

            // Position editor context menu at top right corner of screen
            if (centerer) {
                const viewportLeft = centerer.scrollLeft;
                const viewportTop = centerer.scrollTop;
                const viewportWidth = centerer.clientWidth;

                const newPos = {
                    x: viewportLeft + viewportWidth - menuWidth - 10, // 10px margin from right edge
                    y: viewportTop + 10 // 10px margin from top edge
                };

                setMenuPosition(newPos);
            }
        }
    }, [ showEditorMenu, textInput ]);

    // Measure real user menu size and adjust position after menu renders
    useLayoutEffect(() => {
        if (showUserMenu && userMenuRef.current) {
            const menu = userMenuRef.current;
            const menuWidth = menu.offsetWidth;
            const menuHeight = menu.offsetHeight;
            const centerer = centererRef.current;

            // Position user context menu centered at the top
            if (centerer) {
                const viewportLeft = centerer.scrollLeft;
                const viewportTop = centerer.scrollTop;
                const viewportWidth = centerer.clientWidth;

                const newPos = {
                    x: viewportLeft + (viewportWidth - menuWidth) / 2, // Center horizontally
                    y: viewportTop + 10  // 10px margin from top edge
                };

                // Update position if it changed
                if (newPos.x !== circleMenuPosition.x || newPos.y !== circleMenuPosition.y) {
                    setCircleMenuPosition(newPos);
                }
            }
        }
    }, [ showUserMenu, toolImageOrientation ]);

    // Keep user context menu centered at top when scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (showUserMenu && userMenuRef.current && centererRef.current) {
                const menu = userMenuRef.current;
                const menuWidth = menu.offsetWidth;
                const menuHeight = menu.offsetHeight;
                const centerer = centererRef.current;

                const viewportLeft = centerer.scrollLeft;
                const viewportTop = centerer.scrollTop;
                const viewportWidth = centerer.clientWidth;

                const newPos = {
                    x: viewportLeft + (viewportWidth - menuWidth) / 2, // Center horizontally
                    y: viewportTop + 10  // 10px margin from top edge
                };

                setCircleMenuPosition(newPos);
            }
        };

        if (showUserMenu && centererRef.current) {
            centererRef.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (centererRef.current) {
                centererRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [ showUserMenu ]);

    // Keep editor context menu at right center point when scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (showEditorMenu && editorMenuRef.current && centererRef.current) {
                const menu = editorMenuRef.current;
                const menuWidth = menu.offsetWidth;
                const menuHeight = menu.offsetHeight;
                const centerer = centererRef.current;

                const viewportLeft = centerer.scrollLeft;
                const viewportTop = centerer.scrollTop;
                const viewportWidth = centerer.clientWidth;

                const newPos = {
                    x: viewportLeft + viewportWidth - menuWidth - 10, // 10px margin from right edge
                    y: viewportTop + 10 // 10px margin from top edge
                };

                setMenuPosition(newPos);
            }
        };

        if (showEditorMenu && centererRef.current) {
            centererRef.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (centererRef.current) {
                centererRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [ showEditorMenu ]);

    // Persist last PDF for HomePage restore — manual workflow only (IndexedDB).
    useEffect(() => {
        if (!file) return;
        let cancelled = false;
        void (async () => {
            try {
                const mode = await getStorageMode();
                if (cancelled || mode !== 'manual') return;
                await storeSavedPdf(file);
            } catch (error) {
                if (!cancelled) {
                    logger.error('[PdfViewer] Failed to persist PDF for home restore:', error);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ file ]);


    // Save state data (per drawing — global keys caused old rects to re-apply on new PDF when toggling settings)
    useEffect(() => {
        if (role === 'editor') return;
        if (rects.length > 0) {
            try {
                localStorage.setItem(getSavedRectsStorageKey(detail, revision), JSON.stringify(rects));
            } catch {
                // ignore
            }
        }
    }, [ rects, detail, revision, role ]);

    useEffect(() => {
        if (usernameRects.length > 0) {
            try {
                localStorage.setItem(getSavedUsernameRectsStorageKey(detail, revision), JSON.stringify(usernameRects));
            } catch {
                // ignore
            }
        }
    }, [ usernameRects, detail, revision ]);

    // Load saved state data
    useEffect(() => {
        if (role === 'editor') return;
        let savedRects: string | null;
        let savedUsernameRects: string | null;
        try {
            savedRects = localStorage.getItem(getSavedRectsStorageKey(detail, revision));
            savedUsernameRects = localStorage.getItem(getSavedUsernameRectsStorageKey(detail, revision));
        } catch {
            return;
        }

        if (savedRects) {
            try {
                const parsedRects = JSON.parse(savedRects);
                // Ensure loaded rects have isConfirmed set
                const rectsWithConfirmed = parsedRects.map((r: any) => ({
                    ...r,
                    isConfirmed: r.isConfirmed !== undefined
                        ? r.isConfirmed
                        : true
                }));
                setRects(rectsWithConfirmed);
                setNextNumber(Math.max(...rectsWithConfirmed.map((r: any) => r.number), 0) + 1);
            } catch (error) {
                // Error loading saved rectangles
            }
        }

        if (savedUsernameRects && !sequenceMeasurement) {
            try {
                const parsedUsernameRects = JSON.parse(savedUsernameRects);
                setUsernameRects(parsedUsernameRects);
            } catch (error) {
                // Error loading saved username rectangles
            }
        }
    // Intentionally not listing `detail`/`revision` — re-running on navigation can race the Excel loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ sequenceMeasurement, role ]);


    //TODO: Delete local storage of saved files     up





    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
        setPageNumber(1);
    }

    function onPageLoadSuccess(page: any) {
        const isLandscape = page.width > page.height;
        const orientation = isLandscape ? 'landscape' : 'portrait';
        setPdfOrientation(orientation);

        // Intentionally do NOT call setReferenceDimensions here.
        // The dedicated resize effect converts existing rectangles from the old
        // reference dimensions to the new pdfSize and only then updates
        // referenceDimensions. Updating both at once would skip that conversion
        // and leave rectangles in a stale coordinate system (e.g. shifted to the
        // left when a settings file was created on a landscape PDF and is later
        // re-loaded before the PDF page has finished loading).
        if (orientation === 'landscape') {
            const newSize = { width: 2000, height: 1600 };
            setStageSize(newSize);
            setPdfSize(newSize);
        } else {
            const newSize = { width: 1400, height: 1600 };
            setStageSize(newSize);
            setPdfSize(newSize);
        }
        setPageLoadedForOcr(true);
    }

    useEffect(() => {
        if (!file) return;
        setIsOcrLoading(true);
        setPageLoadedForOcr(false);
        setDetectedMeasurements([]);
        ocrRunRef.current = false;
        iso22081GeminiPageKeyRef.current = '';
    }, [ file ]);

    useEffect(() => {
        if (!file) return;
        setIsOcrLoading(true);
        setPageLoadedForOcr(false);
        setDetectedMeasurements([]);
        ocrRunRef.current = false;
    }, [ pageNumber ]);

    useEffect(() => {
        if (!file || !pageLoadedForOcr || ocrRunRef.current) return;
        if (effectiveRole === 'user') {
            setIsOcrLoading(false);
            return;
        }
        ocrRunRef.current = true;

        const runInitialOcr = async () => {
            try {
                await new Promise(r => setTimeout(r, 150));

                const pdfJsResult = await extractTextFromPdfJs(
                    file,
                    pageNumber,
                    stageSize.width,
                    stageSize.height
                );

                if (pdfJsResult.success && pdfJsResult.items.length > 0) {
                    const items = pdfJsResult.items;
                    logger.log(`[OCR] Initial OCR: ${items.length} items, source=${items[0]?.source}`);
                    setDetectedMeasurements(items);
                    setShowDetectedMeasurements(roleRef.current !== 'editor');
                }
            } catch (error) {
                logger.error('[OCR] Initial OCR failed:', error);
            } finally {
                setIsOcrLoading(false);
            }
        };

        runInitialOcr();
    }, [ file, pageNumber, stageSize.width, stageSize.height, pageLoadedForOcr, role ]);





    useEffect(() => {
        // Prevent default browser context menu
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            return false;
        };

        // Handle Escape key to close menus (editor+selected rect: if submitted -> close menu, if not -> delete via handleDeleteKey)
        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (orderNumberModalForEditor) {
                    setOrderNumberModalForEditor(false);
                    return;
                }
                if (showHelpMenu) {
                    setShowHelpMenu(false);
                    return;
                }
                if (showSettingsMenu) {
                    setShowSettingsMenu(false);
                    return;
                }
                if (effectiveRole === 'editor' && showEditorMenu && selectedRectIndex !== null) {
                    const currentRect = rects[selectedRectIndex];
                    if (currentRect?.isConfirmed) {
                        setShowUserMenu(false);
                        setShowEditorMenu(false);
                        showEditorMenuRef.current = false;
                        selectedRectIndexRef.current = null;
                        circleMenuPdfCoordsRef.current = null;
                        editorMenuPdfCoordsRef.current = null;
                        setCircleTextInput('');
                        setToolInputError('');
                        setHoleShaftError('');
                        setTolerancePlus('');
                        setToleranceMinus('');
                        setNominalDimension('');
                        setRecognizedValue('');
                        setSelectedToleranceType('plusminus');
                        mDesignationRef.current = null;
                    }
                    return;
                }
                if (showUserMenu || showEditorMenu) {
                    setShowUserMenu(false);
                    setShowEditorMenu(false);
                    showEditorMenuRef.current = false;
                    selectedRectIndexRef.current = null;
                    setSelectedCircleIndex(null);
                    circleMenuPdfCoordsRef.current = null;
                    editorMenuPdfCoordsRef.current = null;
                    setCircleTextInput('');
                    setToolInputError('');
                    setHoleShaftError('');
                    setTolerancePlus('');
                    setToleranceMinus('');
                    setNominalDimension('');
                    setRecognizedValue('');
                    setSelectedToleranceType('plusminus');
                    mDesignationRef.current = null;
                }
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [ showUserMenu, showEditorMenu, showHelpMenu, showSettingsMenu, orderNumberModalForEditor, role, selectedRectIndex, rects ]);

    // Load setter rectangles from embedded Excel and/or session draft (editor unsaved work survives refresh).
    useEffect(() => {
        if (effectiveRole !== 'editor' || !file) return;

        if (!editorExcelFile) {
            if (revisionId) {
                return;
            }
            if (isNewEditorRevision) {
                editorEmbeddedReportFolderKeyRef.current = buildFolderKey(detail, revision);
                clearEditorWorkDraft(file, detail, revision);
                localStorage.removeItem('editorExcelRects');
                localStorage.removeItem('editorExcelRefDimensions');
                setRects([]);
                setNextNumber(1);
                setReferenceDimensions(null);
                setSettingsFileError('');
                setSequenceMeasurement(true);
                setExamMode(false);
                setEditorDraftHydrated(true);
                return;
            }
            editorEmbeddedReportFolderKeyRef.current = buildFolderKey(detail, revision);
            const draftOnly = readEditorWorkDraft(file, detail, revision);
            if (draftOnly?.v === 1) {
                if (draftOnly.rects.length > 0) {
                    setRects(mapDraftRectsToState(draftOnly.rects));
                    setNextNumber(draftOnly.nextNumber);
                } else {
                    setRects([]);
                    setNextNumber(1);
                }
                setReferenceDimensions(draftOnly.referenceDimensions);
                setSequenceMeasurement(draftOnly.sequenceMeasurement);
                setExamMode(draftOnly.examMode ?? false);
                if (draftOnly.downloadPathDisplayName) {
                    setDownloadPathDisplayName(draftOnly.downloadPathDisplayName);
                }
                setSettingsFileError('');
                setEditorDraftHydrated(true);
                return;
            }
            editorEmbeddedReportFolderKeyRef.current = null;
            setRects([]);
            setNextNumber(1);
            setReferenceDimensions(null);
            setSettingsFileError('');
            setSequenceMeasurement(true);
            setExamMode(false);
            clearEditorWorkDraft(file, detail, revision);
            localStorage.removeItem('editorExcelRects');
            localStorage.removeItem('editorExcelRefDimensions');
            setEditorDraftHydrated(true);
            return;
        }

        const processEditorExcel = async () => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    editorEmbeddedReportFolderKeyRef.current = null;
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const getRowVal = (row: any, keys: string[]) => {
                        for (const k of keys) {
                            const v = row[ k ];
                            if (v != null && v !== '') return v;
                        }
                        return undefined;
                    };

                    let jsonData: any[] = [];
                    const setupSheetName = workbook.SheetNames.find(
                        n => n.toLowerCase().includes('setup') || n.toLowerCase().includes('seadistus')
                    );
                    const sheetToUse = setupSheetName || workbook.SheetNames[ 0 ];
                    const worksheet = workbook.Sheets[ sheetToUse ];
                    if (worksheet) jsonData = XLSX.utils.sheet_to_json(worksheet);
                    if (jsonData.length === 0 && workbook.SheetNames.length > 0) {
                        const firstSheet = workbook.Sheets[ workbook.SheetNames[ 0 ] ];
                        if (firstSheet) jsonData = XLSX.utils.sheet_to_json(firstSheet);
                    }

                    const firstRow = jsonData[ 0 ] as any;
                    let excelRefDimensions: { width: number; height: number } | null = null;
                    const refW = firstRow && getRowVal(firstRow, [ 'Ref. Width', 'Ref. Width.', 'Ref Width' ]);
                    const refH = firstRow && getRowVal(firstRow, [ 'Ref. Height', 'Ref. Height.', 'Ref Height' ]);
                    if (refW != null && refH != null) {
                        excelRefDimensions = {
                            width: parseFloat(String(refW || '800')),
                            height: parseFloat(String(refH || '600'))
                        };
                    }

                    const saveFolderFromExcel = firstRow && getRowVal(firstRow, [ 'SaveFolder', 'Savefolder', 'SaveFolder.' ]);
                    const detailFromExcel = firstRow && getRowVal(firstRow, [ 'DetailNumber', 'DetailNumber.', 'Detail' ]);
                    const revisionFromExcel = firstRow && getRowVal(firstRow, [ 'Revision', 'Revision.' ]);
                    const excelFolderKey = buildFolderKey(String(detailFromExcel || ''), String(revisionFromExcel || ''));
                    editorEmbeddedReportFolderKeyRef.current = excelFolderKey;

                    const seqVal = firstRow && getRowVal(firstRow, [ 'SequenceMeasurement', 'SequenceMeasurement.' ]);
                    const fromFile = parseSequenceMeasurementExcelValue(seqVal) ?? true;
                    const examVal = firstRow && getRowVal(firstRow, [ 'ExamMode', 'ExamMode.' ]);
                    const examFromFile = parseExamModeExcelValue(examVal) ?? false;
                    const storedHandle = await getDownloadDirHandle(excelFolderKey);
                    if (storedHandle) {
                        downloadDirHandleRef.current = storedHandle;
                        setDownloadPathDisplayName(storedHandle.name);
                    } else if (saveFolderFromExcel) {
                        setDownloadPathDisplayName(String(saveFolderFromExcel));
                    } else {
                        setDownloadPathDisplayName('');
                    }

                    const validRows = jsonData.filter((row: any) => {
                        const mNr = getRowVal(row, [ 'M. Nr', 'M.Nr', 'M Nr', 'M. Nr.' ]);
                        if (mNr === 'M. Nr' || mNr === 'M.Nr' || String(mNr).toLowerCase() === 'm. nr') return false;
                        const xVal = getRowVal(row, [ 'X', 'x' ]);
                        const yVal = getRowVal(row, [ 'Y', 'y' ]);
                        const wVal = getRowVal(row, [ 'Width', 'width' ]);
                        const hVal = getRowVal(row, [ 'Height', 'height' ]);
                        return mNr != null && xVal != null && yVal != null && wVal != null && hVal != null;
                    });

                    const newRects = validRows.map((row: any, index: number) => {
                        const mNr = getRowVal(row, [ 'M. Nr', 'M.Nr', 'M Nr', 'M. Nr.' ]);
                        const number = parseInt(String(mNr || index + 1), 10);
                        const x = parseFloat(String(getRowVal(row, [ 'X', 'x' ]) || 0));
                        const y = parseFloat(String(getRowVal(row, [ 'Y', 'y' ]) || 0));
                        const width = parseFloat(String(getRowVal(row, [ 'Width', 'width' ]) || 50));
                        const height = parseFloat(String(getRowVal(row, [ 'Height', 'height' ]) || 50));
                        if (isNaN(number) || isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) return null;
                        return {
                            x, y, width, height, number, text: '',
                            enteredNumber: getRowVal(row, [ 'Tool', 'tool' ]) ? parseInt(String(getRowVal(row, [ 'Tool', 'tool' ])), 10) : undefined,
                            tolerancePlus: String(getRowVal(row, [ 'Upper', 'upper' ]) || ''),
                            toleranceMinus: String(getRowVal(row, [ 'Lower', 'lower' ]) || ''),
                            nominalDimension: String(getRowVal(row, [ 'Nominal Dim.', 'Nominal Dim', 'Nominal' ]) || ''),
                            toleranceType: (getRowVal(row, [ 'Type', 'type' ]) as 'plusminus' | 'plus' | 'minus' | undefined) || undefined,
                            isConfirmed: true,
                            toleranceFromStandard: false,
                            hasIso286LetterDesignation: false,
                            toleranceFromIso286Standard: false,
                            toleranceFromDrawingRecognition: false,
                        };
                    }).filter((r): r is NonNullable<typeof r> => r !== null);

                    if (newRects.length === 0) {
                        const draftEmpty = revisionId ? null : readEditorWorkDraft(file, detail, revision);
                        if (draftEmpty?.v === 1) {
                            if (draftEmpty.rects.length > 0) {
                                setRects(mapDraftRectsToState(draftEmpty.rects));
                                setNextNumber(draftEmpty.nextNumber);
                            } else {
                                setRects([]);
                                setNextNumber(1);
                            }
                            setReferenceDimensions(
                                draftEmpty.referenceDimensions ?? excelRefDimensions ??
                                (pdfSize.width > 0 && pdfSize.height > 0 ? { width: pdfSize.width, height: pdfSize.height } : null)
                            );
                            setSequenceMeasurement(draftEmpty.sequenceMeasurement);
                            setExamMode(draftEmpty.examMode ?? false);
                            if (draftEmpty.downloadPathDisplayName) {
                                setDownloadPathDisplayName(draftEmpty.downloadPathDisplayName);
                            }
                        } else {
                            setRects([]);
                            setNextNumber(1);
                            setSequenceMeasurement(fromFile);
                            setExamMode(examFromFile);
                            setReferenceDimensions(
                                excelRefDimensions ??
                                (pdfSize.width > 0 && pdfSize.height > 0 ? { width: pdfSize.width, height: pdfSize.height } : null)
                            );
                        }
                        setSettingsFileError('');
                        setEditorDraftHydrated(true);
                        return;
                    }

                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        try {
                            const companyProfile = await getCompanyProfileCached(currentUser.uid);
                            if (companyProfile) {
                                const tools = companyProfile.tools || [];
                                const companyActiveToolCount = tools.length;
                                let maxToolInSettings = 0;
                                newRects.forEach((rect: any) => {
                                    if (rect.enteredNumber) {
                                        const toolNumber = parseInt(String(rect.enteredNumber), 10);
                                        if (!isNaN(toolNumber) && toolNumber > maxToolInSettings) maxToolInSettings = toolNumber;
                                    }
                                });
                                if (maxToolInSettings > 0 && maxToolInSettings > companyActiveToolCount) {
                                    setSettingsFileError(strings.PDFVIEWER_SETTINGS_TOOL_COUNT_MISMATCH);
                                    setRects([]);
                                    setNextNumber(1);
                                    setEditorDraftHydrated(true);
                                    return;
                                }
                            }
                        } catch (error) {
                            logger.error('Error validating editor tools:', error);
                        }
                    }
                    setSettingsFileError('');
                    const workDraft = revisionId ? null : readEditorWorkDraft(file, detail, revision);
                    if (workDraft?.v === 1) {
                        if (workDraft.rects.length > 0) {
                            setRects(mapDraftRectsToState(workDraft.rects));
                            setNextNumber(workDraft.nextNumber);
                        } else {
                            setRects([]);
                            setNextNumber(workDraft.nextNumber);
                        }
                        setReferenceDimensions(
                            (workDraft.referenceDimensions ?? excelRefDimensions) ||
                            { width: pdfSize.width, height: pdfSize.height }
                        );
                        setSequenceMeasurement(workDraft.sequenceMeasurement);
                        setExamMode(workDraft.examMode ?? false);
                        if (workDraft.downloadPathDisplayName) {
                            setDownloadPathDisplayName(workDraft.downloadPathDisplayName);
                        }
                    } else {
                        setRects(newRects);
                        setNextNumber(Math.max(...newRects.map(r => r.number), 0) + 1);
                        setReferenceDimensions(excelRefDimensions || { width: pdfSize.width, height: pdfSize.height });
                        setSequenceMeasurement(fromFile);
                        setExamMode(examFromFile);
                    }
                    setEditorDraftHydrated(true);
                } catch (error) {
                    setRects([]);
                    setNextNumber(1);
                    setEditorDraftHydrated(true);
                }
            };
            reader.readAsArrayBuffer(editorExcelFile);
        };

        processEditorExcel();
    }, [ effectiveRole, role, editorExcelFile, strings, file, detail, revision, revisionId, isNewEditorRevision ]);

    useEffect(() => {
        if (effectiveRole !== 'editor' || !file) return;
        if (!premiumUltimatePlanAllowed) {
            setIsoRecognizedSentences([]);
            return;
        }
        extractIsoSentencesFromPdf(file, pageNumber).then(result => {
            if (result.success && result.sentences.length > 0) {
                setIsoRecognizedSentences(result.sentences);
            } else {
                setIsoRecognizedSentences([]);
            }
        });
    }, [ role, file, pageNumber, premiumUltimatePlanAllowed ]);

    useEffect(() => {
        if (effectiveRole !== 'editor' || !file) return;
        if (!premiumUltimatePlanAllowed) {
            return;
        }
        let cancelled = false;
        const pageKey = `${file.name}\0${file.size}\0${pageNumber}`;

        (async () => {
            const result = await extractIso22081TitleBlockFromPdf(file, pageNumber);
            if (cancelled || !result.success || !result.mentions22081) {
                return;
            }
            setG2UserSelectedStandard('ISO 22081');
            if (result.linear) {
                setIso22081LinearSizes(result.linear);
            }
            if (result.shape) {
                setIso22081ShapeSizes(result.shape);
            }
            if (result.angular) {
                setIso22081AngularSizes(result.angular);
            }
            if (result.radiusChamfer) {
                setIso22081RadiusChamferSizes(result.radiusChamfer);
            }
            setIsoRecognizedSentences(prev => {
                if (prev.some(s => s.trim().toLowerCase() === 'iso 22081')) {
                    return prev;
                }
                return [ ...prev, 'ISO 22081' ];
            });

            const hasBand =
                !!(result.linear || result.shape || result.angular || result.radiusChamfer);
            if (hasBand || !pageLoadedForOcr) {
                return;
            }
            if (iso22081GeminiPageKeyRef.current === pageKey) {
                return;
            }

            const tryGemini = () => {
                if (cancelled) return;
                const canvas = getPdfPageCanvasFromViewerContainer(containerRef.current);
                if (!canvas) {
                    return;
                }
                iso22081GeminiPageKeyRef.current = pageKey;
                const sw = stageSize.width;
                const sh = stageSize.height;
                const region = {
                    x: sw * 0.48,
                    y: sh * 0.52,
                    width: sw * 0.5,
                    height: sh * 0.45
                };
                void extractIso22081TolerancesViaGemini(canvas, sw, sh, region).then(g => {
                    if (cancelled || !g.success) {
                        return;
                    }
                    if (!g.mentions22081) {
                        return;
                    }
                    if (g.linear) {
                        setIso22081LinearSizes(g.linear);
                    }
                    if (g.shape) {
                        setIso22081ShapeSizes(g.shape);
                    }
                    if (g.angular) {
                        setIso22081AngularSizes(g.angular);
                    }
                    if (g.radiusChamfer) {
                        setIso22081RadiusChamferSizes(g.radiusChamfer);
                    }
                });
            };

            setTimeout(tryGemini, 300);
        })();

        return () => {
            cancelled = true;
        };
    }, [ effectiveRole, file, pageNumber, premiumUltimatePlanAllowed, pageLoadedForOcr, stageSize.width, stageSize.height ]);

    useEffect(() => {
        if (effectiveRole !== 'editor' || !file) return;
        if (!premiumUltimatePlanAllowed) {
            setIso286Designation(null);
            return;
        }
        if (!hasIso286InSentences(isoRecognizedSentences)) {
            setIso286Designation(null);
            return;
        }
        extractIso286DesignationFromPdf(file, pageNumber).then(designationResult => {
            const designation = designationResult.success && designationResult.designation
                ? designationResult.designation
                : 'H7';
            setIso286Designation(designation);
        });
    }, [ role, file, pageNumber, isoRecognizedSentences, premiumUltimatePlanAllowed ]);

    const effectiveIso286Hole = iso286UserSelectedHole ?? parseHoleFromDesignation(iso286Designation);
    const effectiveIso286Shaft = iso286UserSelectedShaft ?? parseShaftFromDesignation(iso286Designation);
    const bothHoleShaftSelected = !!(effectiveIso286Hole && effectiveIso286Hole.trim() !== '' && effectiveIso286Shaft && effectiveIso286Shaft.trim() !== '');
    const effectiveIso286Designation = (effectiveIso286Hole && effectiveIso286Shaft)
        ? `${effectiveIso286Hole}/${effectiveIso286Shaft}`
        : (effectiveIso286Hole || effectiveIso286Shaft || iso286Designation);
    const effectiveGeneralStandard = premiumUltimatePlanAllowed
        ? (g2UserSelectedStandard || ISO2768_DEFAULT)
        : null;

    /** C button selected state: explicit ISO 2768 chamfer class prefix only (toggle off clears C, not the callout text). */
    const editorIso2768ChamferClassMarkActive = useMemo(
        () => /^\s*[Cc]\s*\S/.test(nominalDimension.trim()),
        [ nominalDimension ]
    );

    useEffect(() => {
        if (!premiumUltimatePlanAllowed) return;
        if (effectiveRole !== 'editor' || !effectiveIso286Designation) return;
        setRects(prev => {
            const shouldUpdate = prev.some(r => {
                if (r.toleranceFromDrawingRecognition === true) return false;
                const hasNominal = r.nominalDimension && r.nominalDimension.trim() !== '';
                const hasDiam = hasDiameterMark(r.nominalDimension || '');
                const hasLetter = r.hasIso286LetterDesignation === true;
                const noTolerance = (!r.tolerancePlus || r.tolerancePlus.trim() === '') &&
                    (!r.toleranceMinus || r.toleranceMinus.trim() === '');
                const fromIso286 = r.toleranceFromIso286Standard === true;
                const hasM = !!r.mDesignation;
                return hasNominal && hasDiam && !hasLetter && !hasM && (noTolerance || fromIso286);
            });
            if (!shouldUpdate) return prev;
            const newRects = prev.map(rect => {
                const hasNominal = rect.nominalDimension && rect.nominalDimension.trim() !== '';
                const hasDiam = hasDiameterMark(rect.nominalDimension || '');
                const hasLetter = rect.hasIso286LetterDesignation === true;
                const hasM = !!rect.mDesignation;
                const noTolerance = (!rect.tolerancePlus || rect.tolerancePlus.trim() === '') &&
                    (!rect.toleranceMinus || rect.toleranceMinus.trim() === '');
                const fromIso286 = rect.toleranceFromIso286Standard === true;
                if (!hasNominal || !hasDiam || hasLetter || hasM || rect.toleranceFromDrawingRecognition === true) {
                    return rect;
                }
                if (!noTolerance && !fromIso286) return rect;
                const nominalMm = parseNominalToMm(rect.nominalDimension || '');
                if (nominalMm === null) return rect;
                const designationToUse = iso286ActiveButton === 'hole'
                    ? (effectiveIso286Hole || 'H7')
                    : (effectiveIso286Shaft || 'h6');
                const isoResult = lookupIso286Tolerance(designationToUse, nominalMm);
                if (!isoResult) return rect;
                return {
                    ...rect,
                    tolerancePlus: isoResult.tolerancePlus,
                    toleranceMinus: isoResult.toleranceMinus,
                    toleranceType: isoResult.toleranceType,
                    toleranceFromIso286Standard: true,
                    toleranceManuallySet: !!rect.mDesignation
                };
            });
            const hasActualChanges = prev.some((r, i) => {
                const nr = newRects[i];
                return (
                    r.tolerancePlus !== nr.tolerancePlus ||
                    r.toleranceMinus !== nr.toleranceMinus ||
                    r.toleranceType !== nr.toleranceType ||
                    r.toleranceManuallySet !== nr.toleranceManuallySet
                );
            });
            return hasActualChanges ? newRects : prev;
        });
    }, [ role, effectiveIso286Designation, effectiveIso286Hole, effectiveIso286Shaft, iso286ActiveButton, rects, premiumUltimatePlanAllowed ]);

    useEffect(() => {
        if (!premiumUltimatePlanAllowed) return;
        if (bothHoleShaftSelected && iso286ActiveButton === null && !mDesignationRef.current) {
            setIso286ActiveButton('shaft');
        }
    }, [ bothHoleShaftSelected, iso286ActiveButton, premiumUltimatePlanAllowed ]);

    useEffect(() => {
        if (!premiumUltimatePlanAllowed || !showEditorMenu) return;
        const m = mDesignationRef.current;
        if (m && isIso286MPairHoleToleranceOnly(m) && iso286ActiveButton === 'shaft') {
            setIso286ActiveButton('hole');
        }
    }, [ showEditorMenu, premiumUltimatePlanAllowed, iso286ActiveButton, nominalDimension, recognizedValue ]);

    useEffect(() => {
        if (!premiumUltimatePlanAllowed) return;
        if (effectiveRole !== 'editor' || !showEditorMenu) return;
        if (mDesignationRef.current) {
            const nominalM = (nominalDimension || recognizedValue || '').trim();
            if (hasDiameterMark(nominalM)) {
                const nominalMmM = parseNominalToMm(nominalM);
                if (nominalMmM !== null) {
                    const pairM = parseIso286MSelectionPair(mDesignationRef.current);
                    if (pairM) {
                        const designationM = isIso286MPairHoleToleranceOnly(mDesignationRef.current)
                            ? pairM.hole
                            : (iso286ActiveButton === 'hole' ? pairM.hole : pairM.shaft);
                        const isoResultM = lookupIso286Tolerance(designationM, nominalMmM);
                        if (isoResultM) {
                            setTolerancePlus(isoResultM.tolerancePlus);
                            setToleranceMinus(isoResultM.toleranceMinus);
                            setSelectedToleranceType(isoResultM.toleranceType);
                            toleranceSourceFromAutoFillRef.current = true;
                            toleranceHadIso286LetterRef.current = false;
                        }
                    }
                }
            }
            return;
        }
        const nomTrim = nominalDimension.trim();
        const recTrim = recognizedValue.trim();
        const nominal = (nomTrim || recTrim).trim();
        if (hasDiameterMark(nominal) && toleranceHadIso286LetterRef.current) {
            setHoleShaftError('');
        }
        const nomChamferBody = stripIso2768ChamferClassPrefix(nomTrim);
        const noTolerance = (!tolerancePlus || tolerancePlus.trim() === '') &&
            (!toleranceMinus || toleranceMinus.trim() === '');
        // When OCR parsed explicit limits from the region, do not replace them with ISO 2768/22081
        // general-class values unless that OCR was already identified as matching the active standard.
        const shouldApplyTolerance =
            (noTolerance || toleranceSourceFromAutoFillRef.current) &&
            (!drawingToleranceFromOcrRef.current || toleranceSourceFromAutoFillRef.current);
        const isChamferDim =
            isIso2768ChamferHeightCallout(nomTrim) || isIso2768ChamferHeightCallout(recTrim);
        const isAngular = /°|deg|grad/i.test(nomChamferBody) && !isChamferDim;
        prevEffectiveGeneralStandardRef.current = effectiveGeneralStandard;
        if (!nominal) return;

        // ISO 286 field from OCR (e.g. h6): re-query tables when user corrects nominal after recognition
        // Do not apply ISO 286 fits to radii (R…); parseNominalToMm strips R and would use wrong IT limits.
        if (
            !isAngular &&
            !isChamferDim &&
            !nominalLeadingIsRadiusLetterR(nominal) &&
            !mDesignationRef.current &&
            toleranceHadIso286LetterRef.current &&
            !toleranceManuallyCorrectedRef.current &&
            !bothHoleShaftSelected
        ) {
            const des =
                ocrIso286DesignationRef.current ||
                extractIso286Designation(
                    recTrim.replace(/^[Øø⌀∅oOφϕΦ]?\s*[\d.,]+/, '').trim()
                );
            if (des) {
                const nominalStrForMm = nomTrim || recTrim;
                const nominalMmOcr = parseNominalToMm(nominalStrForMm);
                if (nominalMmOcr !== null) {
                    const isoOcr = lookupIso286Tolerance(des, nominalMmOcr);
                    if (isoOcr) {
                        ocrIso286DesignationRef.current = des;
                        setTolerancePlus(isoOcr.tolerancePlus);
                        setToleranceMinus(isoOcr.toleranceMinus);
                        setSelectedToleranceType(isoOcr.toleranceType);
                        toleranceSourceFromAutoFillRef.current = true;
                        return;
                    }
                }
            }
        }

        if (isAngular && effectiveGeneralStandard && shouldApplyTolerance) {
            let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
            if (effectiveGeneralStandard === 'ISO 22081') {
                result = getIso22081Tolerance(nominal, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
            } else {
                result = lookupIso2768AngularTolerance(effectiveGeneralStandard);
            }
            if (result) {
                setTolerancePlus(result.tolerancePlus);
                setToleranceMinus(result.toleranceMinus);
                setSelectedToleranceType('plusminus');
                toleranceSourceFromAutoFillRef.current = true;
            }
            return;
        }
        const nominalMm = parseNominalToMm(nominal);
        const chamferLegMm = isChamferDim
            ? (getIso2768ChamferHeightLegMm(nomTrim) ?? getIso2768ChamferHeightLegMm(recTrim))
            : null;
        const nominalMmEffective = chamferLegMm ?? nominalMm;
        if (nominalMmEffective === null) return;
        if (hasDiameterMark(nominal) && bothHoleShaftSelected && nominalMm !== null) {
            setHoleShaftError('');
            const designationToUse = iso286ActiveButton === 'hole'
                ? (effectiveIso286Hole || 'H7')
                : (effectiveIso286Shaft || 'h6');
            const isoResult = lookupIso286Tolerance(designationToUse, nominalMm);
            if (isoResult) {
                const designationChanged = prevEffectiveIso286DesignationRef.current !== effectiveIso286Designation;
                prevEffectiveIso286DesignationRef.current = effectiveIso286Designation;
                if (noTolerance || (designationChanged && toleranceSourceFromAutoFillRef.current)) {
                    setTolerancePlus(isoResult.tolerancePlus);
                    setToleranceMinus(isoResult.toleranceMinus);
                    setSelectedToleranceType(isoResult.toleranceType);
                    toleranceSourceFromAutoFillRef.current = true;
                    toleranceHadIso286LetterRef.current = false;
                }
            }
        } else if (
            hasDiameterMark(nominal) &&
            !bothHoleShaftSelected &&
            effectiveGeneralStandard &&
            shouldApplyTolerance &&
            !toleranceHadIso286LetterRef.current
        ) {
            prevEffectiveIso286DesignationRef.current = null;
            setHoleShaftError((strings as Record<string, string>).PDFVIEWER_HOLE_SHAFT_STANDARD_REQUIRED || 'Select shaft and hole standard in standard corner at the left...');
            let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
            if (effectiveGeneralStandard === 'ISO 22081') {
                result = getIso22081Tolerance(nominal, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
            } else {
                result = lookupIso2768ToleranceForNominalDisplay(
                    effectiveGeneralStandard,
                    nominalMmEffective,
                    nominal,
                    recTrim
                );
            }
            if (result) {
                setTolerancePlus(result.tolerancePlus);
                setToleranceMinus(result.toleranceMinus);
                setSelectedToleranceType('plusminus');
                toleranceSourceFromAutoFillRef.current = true;
            }
        } else if (!hasDiameterMark(nominal) && effectiveGeneralStandard && shouldApplyTolerance) {
            let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
            if (effectiveGeneralStandard === 'ISO 22081') {
                result = getIso22081Tolerance(nominal, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
            } else {
                result = lookupIso2768ToleranceForNominalDisplay(
                    effectiveGeneralStandard,
                    nominalMmEffective,
                    nominal,
                    recTrim
                );
            }
            if (result) {
                setTolerancePlus(result.tolerancePlus);
                setToleranceMinus(result.toleranceMinus);
                setSelectedToleranceType('plusminus');
                toleranceSourceFromAutoFillRef.current = true;
            }
        }
    }, [ role, showEditorMenu, nominalDimension, recognizedValue, tolerancePlus, toleranceMinus, effectiveGeneralStandard, effectiveIso286Designation, effectiveIso286Hole, effectiveIso286Shaft, iso286ActiveButton, bothHoleShaftSelected, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes, strings, premiumUltimatePlanAllowed ]);

    // When general standard (e.g. ISO 2768 - m → - f) or ISO 22081 size bands change, refresh tolerances
    // for all rectangles that were not manually set. Do not depend on `rects` — only on standard inputs.
    useEffect(() => {
        if (effectiveRole !== 'editor' || !effectiveGeneralStandard) return;
        setRects(prev => {
            const shouldUpdate = prev.some(r => {
                if (r.toleranceManuallySet === true || r.toleranceFromDrawingRecognition === true) return false;
                const nom = r.nominalDimension || '';
                const chamH = r.iso2768ChamferCalloutHint?.trim() || '';
                const hasNominal = nom.trim() !== '';
                if (!hasNominal) return false;
                const noDiam = !hasDiameterMark(nom);
                const hasDiam = hasDiameterMark(nom);
                const isCh = isIso2768ChamferHeightCallout(nom) || isIso2768ChamferHeightCallout(chamH);
                const hasAngular = /°|deg|grad/i.test(nom) && !isCh;
                const hasLetter = r.hasIso286LetterDesignation === true;
                const hasM = !!r.mDesignation;
                const fromIso286Fit = r.toleranceFromIso286Standard === true;
                const angularNeeds = hasAngular;
                const linearNeeds = noDiam && !hasAngular;
                const diamNeedsIso2768 =
                    hasDiam && !hasLetter && !hasM && !effectiveIso286Designation && !fromIso286Fit;
                return angularNeeds || linearNeeds || diamNeedsIso2768;
            });
            if (!shouldUpdate) return prev;
            const newRects = prev.map(rect => {
                if (rect.toleranceManuallySet === true || rect.toleranceFromDrawingRecognition === true) return rect;
                const nominalStr = rect.nominalDimension || '';
                const chamHint = rect.iso2768ChamferCalloutHint?.trim() || '';
                const hasNominal = nominalStr.trim() !== '';
                if (!hasNominal) return rect;
                const noDiam = !hasDiameterMark(nominalStr);
                const hasDiam = hasDiameterMark(nominalStr);
                const isChRect = isIso2768ChamferHeightCallout(nominalStr) || isIso2768ChamferHeightCallout(chamHint);
                const hasAngular = /°|deg|grad/i.test(nominalStr) && !isChRect;
                const hasLetter = rect.hasIso286LetterDesignation === true;
                const hasM = !!rect.mDesignation;
                const fromIso286Fit = rect.toleranceFromIso286Standard === true;
                const applyToAngular = hasNominal && hasAngular;
                const applyToNonDiam = hasNominal && noDiam && !hasAngular;
                const applyToDiam =
                    hasNominal && hasDiam && !hasLetter && !hasM && !effectiveIso286Designation && !fromIso286Fit;
                if (!applyToAngular && !applyToNonDiam && !applyToDiam) return rect;
                const nominal = nominalStr;
                let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
                if (applyToAngular) {
                    if (effectiveGeneralStandard === 'ISO 22081') {
                        result = getIso22081Tolerance(nominal, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
                    } else {
                        result = lookupIso2768AngularTolerance(effectiveGeneralStandard);
                    }
                } else if (effectiveGeneralStandard === 'ISO 22081') {
                    result = getIso22081Tolerance(nominal, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
                } else {
                    const nominalMmRect = parseNominalToMm(nominalStr);
                    const chamferLegRect = isChRect
                        ? (getIso2768ChamferHeightLegMm(nominalStr) ?? getIso2768ChamferHeightLegMm(chamHint))
                        : null;
                    const mmEffRect = chamferLegRect ?? nominalMmRect;
                    if (mmEffRect === null) return rect;
                    result = lookupIso2768ToleranceForNominalDisplay(
                        effectiveGeneralStandard,
                        mmEffRect,
                        nominalStr,
                        chamHint
                    );
                }
                if (!result) return rect;
                return {
                    ...rect,
                    tolerancePlus: result.tolerancePlus,
                    toleranceMinus: result.toleranceMinus,
                    toleranceType: 'plusminus' as const,
                    toleranceFromStandard: true,
                    toleranceManuallySet: !!rect.mDesignation
                };
            });
            const hasActualChanges = prev.some((r, i) => {
                const nr = newRects[i];
                return (
                    r.tolerancePlus !== nr.tolerancePlus ||
                    r.toleranceMinus !== nr.toleranceMinus ||
                    r.toleranceType !== nr.toleranceType ||
                    r.toleranceManuallySet !== nr.toleranceManuallySet
                );
            });
            return hasActualChanges ? newRects : prev;
        });
    }, [ effectiveRole, effectiveGeneralStandard, effectiveIso286Designation, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes, premiumUltimatePlanAllowed ]);

    useEffect(() => {
        if (role !== 'user') return;
        if (!excelFile) {
            setSequenceMeasurement(false);
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                userExcelFolderKeyRef.current = null;
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    let jsonData: any[] = [];
                    const setupSheetName = workbook.SheetNames.find(
                        n => n.toLowerCase().includes('setup') || n.toLowerCase().includes('seadistus')
                    );
                    const sheetToUse = setupSheetName || workbook.SheetNames[ 0 ];
                    const worksheet = workbook.Sheets[ sheetToUse ];
                    if (worksheet) {
                        jsonData = XLSX.utils.sheet_to_json(worksheet);
                    }
                    if (jsonData.length === 0 && workbook.SheetNames.length > 0) {
                        const firstSheet = workbook.Sheets[ workbook.SheetNames[ 0 ] ];
                        if (firstSheet) {
                            jsonData = XLSX.utils.sheet_to_json(firstSheet);
                        }
                    }

                    // Check tool count in settings file for operator role
                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        try {
                            const companyProfile = await getCompanyProfileCached(currentUser.uid);
                            if (companyProfile) {
                                const tools = companyProfile.tools || [];
                                const companyActiveToolCount = tools.length;

                                // Find max tool number in settings file
                                let maxToolInSettings = 0;
                                jsonData.forEach((row: any) => {
                                    if (row[ 'Tool' ]) {
                                        const toolNumber = parseInt(row[ 'Tool' ], 10);
                                        if (!isNaN(toolNumber) && toolNumber > maxToolInSettings) {
                                            maxToolInSettings = toolNumber;
                                        }
                                    }
                                });

                                // Validate that max tool number doesn't exceed company active tools count
                                // User can load settings file with fewer tools, but tool numbers must be within valid range (1 to companyActiveToolCount)
                                if (maxToolInSettings > 0 && maxToolInSettings > companyActiveToolCount) {
                                    setSettingsFileError(strings.PDFVIEWER_SETTINGS_TOOL_COUNT_MISMATCH);
                                    setRects([]);
                                    setUsernameRects([]);
                                    setNextNumber(1);
                                    setExcelData([]);
                                    setSequenceMeasurement(false);
                                    return;
                                }
                            }
                        } catch (error) {
                            logger.error('Error checking company tools for settings file validation:', error);
                        }
                    }

                    // Clear error if validation passes
                    setSettingsFileError('');

                    setExcelData(jsonData);

                    const getRowVal = (row: any, keys: string[]) => {
                        for (const k of keys) {
                            const v = row[ k ];
                            if (v != null && v !== '') return v;
                        }
                        return undefined;
                    };

                    // Check for reference dimensions in Excel file (settings file is always in English)
                    const firstRow = jsonData[ 0 ] as any;
                    let excelRefDimensions = null;
                    const refW = firstRow && getRowVal(firstRow, [ 'Ref. Width', 'Ref. Width.', 'Ref Width' ]);
                    const refH = firstRow && getRowVal(firstRow, [ 'Ref. Height', 'Ref. Height.', 'Ref Height' ]);
                    if (refW != null && refH != null) {
                        excelRefDimensions = {
                            width: parseFloat(String(refW || '800')),
                            height: parseFloat(String(refH || '600'))
                        };
                    }

                    const seqValUser = firstRow && getRowVal(firstRow, [ 'SequenceMeasurement', 'SequenceMeasurement.' ]);
                    const parsedSeqUser = parseSequenceMeasurementExcelValue(seqValUser);
                    setSequenceMeasurement(parsedSeqUser ?? false);

                    const examValUser = firstRow && getRowVal(firstRow, [ 'ExamMode', 'ExamMode.' ]);
                    const parsedExamUser = parseExamModeExcelValue(examValUser);
                    setExamMode(parsedExamUser ?? false);

                    const saveFolderFromExcelUser = firstRow && getRowVal(firstRow, [ 'SaveFolder', 'Savefolder', 'SaveFolder.' ]);
                    const detailFromExcelUser = firstRow && getRowVal(firstRow, [ 'DetailNumber', 'DetailNumber.', 'Detail' ]);
                    const revisionFromExcelUser = firstRow && getRowVal(firstRow, [ 'Revision', 'Revision.' ]);
                    const excelFolderKeyUser = buildFolderKey(String(detailFromExcelUser || ''), String(revisionFromExcelUser || ''));
                    userExcelFolderKeyRef.current = excelFolderKeyUser;
                    try {
                        const storedHandleUser = await getDownloadDirHandle(excelFolderKeyUser);
                        if (storedHandleUser) {
                            downloadDirHandleRef.current = storedHandleUser;
                            setDownloadPathDisplayName(storedHandleUser.name);
                        } else if (saveFolderFromExcelUser) {
                            setDownloadPathDisplayName(String(saveFolderFromExcelUser));
                        }
                    } catch {
                        if (saveFolderFromExcelUser) {
                            setDownloadPathDisplayName(String(saveFolderFromExcelUser));
                        }
                    }

                    // Filter and create rectangles from Excel data (support alternate column names)
                    const validRows = jsonData.filter((row: any) => {
                        const mNr = getRowVal(row, [ 'M. Nr', 'M.Nr', 'M Nr', 'M. Nr.' ]);
                        if (mNr === 'M. Nr' || mNr === 'M.Nr' || String(mNr).toLowerCase() === 'm. nr') return false;
                        const xVal = getRowVal(row, [ 'X', 'x' ]);
                        const yVal = getRowVal(row, [ 'Y', 'y' ]);
                        const wVal = getRowVal(row, [ 'Width', 'width' ]);
                        const hVal = getRowVal(row, [ 'Height', 'height' ]);
                        return mNr != null && xVal != null && yVal != null && wVal != null && hVal != null;
                    });

                    const newRects = validRows.map((row: any, index: number) => {
                        const mNr = getRowVal(row, [ 'M. Nr', 'M.Nr', 'M Nr', 'M. Nr.' ]);
                        const number = parseInt(String(mNr || index + 1), 10);
                        let x = parseFloat(String(getRowVal(row, [ 'X', 'x' ]) || 0));
                        let y = parseFloat(String(getRowVal(row, [ 'Y', 'y' ]) || 0));
                        let width = parseFloat(String(getRowVal(row, [ 'Width', 'width' ]) || 50));
                        let height = parseFloat(String(getRowVal(row, [ 'Height', 'height' ]) || 50));

                        // Validate that all values are valid numbers
                        if (isNaN(number) || isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
                            return null;
                        }

                        // If we have reference dimensions from Excel and current canvas info, normalize and denormalize
                        if (excelRefDimensions && pdfSize.width > 0 && pdfSize.height > 0) {

                            // First normalize using Excel reference dimensions
                            const normalized = normalizeCoordinates(
                                { x, y, width, height },
                                excelRefDimensions
                            );

                            // Then denormalize using current canvas dimensions
                            const denormalized = denormalizeCoordinates(
                                normalized,
                                { width: pdfSize.width, height: pdfSize.height }
                            );

                            x = denormalized.x;
                            y = denormalized.y;
                            width = denormalized.width;
                            height = denormalized.height;
                        }

                        // Use M. Nr as rect.number
                        return {
                            x,
                            y,
                            width,
                            height,
                            number: number,
                            text: '',
                            enteredNumber: getRowVal(row, [ 'Tool', 'tool' ]) ? parseInt(String(getRowVal(row, [ 'Tool', 'tool' ])), 10) : undefined,
                            tolerancePlus: String(getRowVal(row, [ 'Upper', 'upper' ]) || ''),
                            toleranceMinus: String(getRowVal(row, [ 'Lower', 'lower' ]) || ''),
                            nominalDimension: String(getRowVal(row, [ 'Nominal Dim.', 'Nominal Dim', 'Nominal' ]) || ''),
                            toleranceType: (getRowVal(row, [ 'Type', 'type' ]) as 'plusminus' | 'plus' | 'minus' | undefined) || undefined,
                            isConfirmed: true,
                            toleranceFromStandard: false,
                            hasIso286LetterDesignation: false,
                            toleranceFromIso286Standard: false,
                            toleranceFromDrawingRecognition: false,
                        };
                    }).filter(rect => rect !== null); // Remove null entries

                    // Only update if we have valid rectangles
                    if (newRects.length > 0) {
                        setRects(newRects);
                        setNextNumber(Math.max(...newRects.map(r => r.number), 0) + 1);

                        // Load company profile once to get toolWithoutMeasurement for preloaded tools
                        let companyTools: Tool[] = [];
                        if (currentUser) {
                            try {
                                const companyProfile = await getCompanyProfileCached(currentUser.uid);
                                if (companyProfile) {
                                    companyTools = companyProfile.tools || [];
                                }
                            } catch (error) {
                                logger.error('Error getting company profile for toolWithoutMeasurement:', error);
                            }
                        }

                        // Create circles at rectangle centers
                        const circlePoints = newRects.map((rect, index) => {
                            // Get NonMeasurement value from Excel data
                            const excelRow = validRows[ index ] as any;
                            const nonMeasurementFromExcel = getRowVal(excelRow, [ 'NonMeasurement', 'Non Measurement' ]) === 'Yes';

                            // First, check if this is a preloaded standard tool from company profile
                            let toolWithoutMeasurement: boolean | undefined = undefined;
                            
                            if (rect.enteredNumber && companyTools.length > 0) {
                                // Find tool by sequential number (enteredNumber corresponds to sequential position in company tools)
                                const tool = companyTools[ rect.enteredNumber - 1 ];
                                if (tool && tool.toolWithoutMeasurement !== undefined) {
                                    toolWithoutMeasurement = tool.toolWithoutMeasurement;
                                }
                            }

                            // If not found in company profile, use Excel value if present
                            if (toolWithoutMeasurement === undefined) {
                                toolWithoutMeasurement = nonMeasurementFromExcel;

                                // If Excel doesn't have NonMeasurement column (backward compatibility)
                                if (getRowVal(excelRow, [ 'NonMeasurement' ]) === undefined) {
                                    const hasNominal = rect.nominalDimension && rect.nominalDimension.trim() !== '';
                                    const hasTolerance = (rect.tolerancePlus && rect.tolerancePlus.trim() !== '') ||
                                        (rect.toleranceMinus && rect.toleranceMinus.trim() !== '');
                                    toolWithoutMeasurement = !hasNominal && !hasTolerance;
                                }
                            }

                            return {
                                x: rect.x + Math.abs(rect.width) / 2,
                                y: rect.y + Math.abs(rect.height) / 2,
                                number: rect.number,
                                enteredNumber: rect.enteredNumber,
                                toolWithoutMeasurement: toolWithoutMeasurement
                            };
                        });

                        setUsernameRects(circlePoints.map(circle => ({ ...circle, isSubmitted: false })));

                        // Store reference dimensions for future use
                        if (excelRefDimensions) {
                            setReferenceDimensions(excelRefDimensions);
                        } else if (pdfSize.width > 0 && pdfSize.height > 0) {
                            // If no reference dimensions in Excel, use current canvas as reference
                            setReferenceDimensions({ width: pdfSize.width, height: pdfSize.height });
                        }
                    } else {

                        setRects([]);
                        setUsernameRects([]);
                        setNextNumber(1);
                    }
            } catch (error) {
                // Error reading Excel file
                setRects([]);
                setUsernameRects([]);
                setNextNumber(1);
                setSequenceMeasurement(false);
            }
        };
        reader.readAsArrayBuffer(excelFile);
    }, [ role, excelFile, pdfSize.width, pdfSize.height, strings ]);

    // Update rectangle positions when canvas dimensions change
    useEffect(() => {
        if (referenceDimensions && pdfSize.width > 0 && pdfSize.height > 0 &&
            (referenceDimensions.width !== pdfSize.width || referenceDimensions.height !== pdfSize.height)) {




            // Add a small delay to ensure canvas is fully updated
            const timeoutId = setTimeout(() => {
                // Update setter rectangles
                setRects(currentRects => {
                    if (currentRects.length > 0) {

                        const updatedRects = currentRects.map(rect => {
                            // Normalize using reference dimensions, then denormalize using current dimensions
                            const normalized = normalizeCoordinates(rect, referenceDimensions);
                            const denormalized = denormalizeCoordinates(normalized, pdfSize);



                            return {
                                ...rect,
                                x: denormalized.x,
                                y: denormalized.y,
                                width: denormalized.width,
                                height: denormalized.height
                            };
                        });

                        return updatedRects;
                    }
                    return currentRects;
                });

                // Update username rectangles
                setUsernameRects(currentUsernameRects => {
                    if (currentUsernameRects.length > 0) {

                        const updatedUsernameRects = currentUsernameRects.map(usernameRect => {
                            // Normalize the username position using reference dimensions
                            const normalized = normalizeCoordinates(
                                { x: usernameRect.x, y: usernameRect.y, width: 0, height: 0 },
                                referenceDimensions
                            );
                            // Denormalize using current dimensions
                            const denormalized = denormalizeCoordinates(normalized, pdfSize);



                            return {
                                ...usernameRect,
                                x: denormalized.x,
                                y: denormalized.y
                            };
                        });

                        return updatedUsernameRects;
                    }
                    return currentUsernameRects;
                });

                // Update reference dimensions to current canvas dimensions
                setReferenceDimensions({ width: pdfSize.width, height: pdfSize.height });

            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [ pdfSize.width, pdfSize.height, referenceDimensions ]);

    // Clear circles when role changes
    useEffect(() => {
        if (effectiveRole !== 'user') {
            setUsernameRects([]);
            if ((effectiveRole !== 'editor' || !editorExcelFile) && role !== 'editor') {
                setRects([]);
                setNextNumber(1);
            }
        } else if (effectiveRole === 'user' && !excelFile && role === 'user') {
            setUsernameRects([]);
            setRects([]);
            setNextNumber(1);
        }
    }, [ role, effectiveRole, excelFile, editorExcelFile ]);

    // When editor switches to user mode, create usernameRects from existing rects
    useEffect(() => {
        if (role !== 'editor' || effectiveRole !== 'user' || rects.length === 0) return;

        const circlePoints = rects.map((rect) => {
            const tool = rect.enteredNumber != null
                ? companyToolsForLegend.find(t => t.number === rect.enteredNumber)
                : null;
            const toolWithoutMeasurement = tool?.toolWithoutMeasurement ?? (
                !(rect.nominalDimension && rect.nominalDimension.trim() !== '') &&
                !((rect.tolerancePlus && rect.tolerancePlus.trim() !== '') || (rect.toleranceMinus && rect.toleranceMinus.trim() !== ''))
            );

            return {
                x: rect.x + Math.abs(rect.width) / 2,
                y: rect.y + Math.abs(rect.height) / 2,
                number: rect.number,
                enteredNumber: rect.enteredNumber,
                toolWithoutMeasurement,
                isSubmitted: false
            };
        });

        setUsernameRects(circlePoints);
    }, [ role, effectiveRole, rects, companyToolsForLegend ]);

    useEffect(() => {
        if (effectiveRole === 'user' && sequenceMeasurement && !examMode && usernameRects.length > 0 &&
            !showUserMenu && selectedCircleIndex === null) {
            const nextIdx = getFirstUnmeasuredVisibleIndex();
            if (nextIdx !== null) {
                activateSequenceRect(nextIdx);
            }
        }
    }, [ effectiveRole, sequenceMeasurement, examMode, usernameRects, showUserMenu, selectedCircleIndex ]);

    // Helper function to adjust menu position if it goes off screen
    const adjustMenuPositionForViewport = (pdfX: number, pdfY: number, menuWidth: number, menuHeight: number, rect?: { x: number, y: number, width: number, height: number }) => {
        const centerer = centererRef.current;
        if (!centerer) return { x: pdfX, y: pdfY };

        // Get viewport boundaries (visible area)
        const viewportLeft = centerer.scrollLeft;
        const viewportRight = centerer.scrollLeft + centerer.clientWidth;
        const viewportTop = centerer.scrollTop;
        const viewportBottom = centerer.scrollTop + centerer.clientHeight;

        let adjustedX = pdfX;
        let adjustedY = pdfY;

        // If rectangle info is provided, position menu based on rectangle corner
        if (rect) {
            // Calculate rectangle boundaries
            const rectRight = rect.x + (rect.width < 0 ? 0 : rect.width);
            const rectBottom = rect.y + (rect.height < 0 ? 0 : rect.height);
            const rectLeft = rect.x + (rect.width < 0 ? rect.width : 0);
            const rectTop = rect.y + (rect.height < 0 ? rect.height : 0);

            // Calculate rectangle center
            const rectCenterX = (rectLeft + rectRight) / 2;
            const rectCenterY = (rectTop + rectBottom) / 2;

            // Calculate viewport center points
            const viewportCenterX = viewportLeft + centerer.clientWidth / 2;
            const viewportCenterY = viewportTop + centerer.clientHeight / 2;

            // Determine rectangle CENTER position relative to viewport center
            const isInRightHalf = rectCenterX > viewportCenterX;
            const isInBottomHalf = rectCenterY > viewportCenterY;

            // Position menu at rectangle corner based on which quadrant it's in
            if (isInRightHalf && !isInBottomHalf) {
                // Rectangle center is in TOP RIGHT - menu at LEFT BOTTOM corner
                adjustedX = rectLeft - menuWidth;
                adjustedY = rectBottom;
            } else if (isInRightHalf && isInBottomHalf) {
                // Rectangle center is in BOTTOM RIGHT - menu at LEFT TOP corner
                adjustedX = rectLeft - menuWidth;
                adjustedY = rectTop - menuHeight;
            } else if (!isInRightHalf && !isInBottomHalf) {
                // Rectangle center is in TOP LEFT - menu at RIGHT BOTTOM corner
                adjustedX = rectRight;
                adjustedY = rectBottom;
            } else {
                // Rectangle center is in BOTTOM LEFT - menu at RIGHT TOP corner
                adjustedX = rectRight;
                adjustedY = rectTop - menuHeight;
            }
        }

        // Check if menu goes off right edge
        if (adjustedX + menuWidth > viewportRight) {
            adjustedX = viewportRight - menuWidth - 10;
        }

        // Check if menu goes off left edge (after adjustment)
        if (adjustedX < viewportLeft) {
            adjustedX = viewportLeft + 10; // Small margin from left edge
        }

        // Check if menu goes off bottom edge
        if (adjustedY + menuHeight > viewportBottom) {
            adjustedY = viewportBottom - menuHeight - 10;
        }

        // Check if menu goes off top edge (after adjustment)
        if (adjustedY < viewportTop) {
            adjustedY = viewportTop + 10; // Small margin from top edge
        }

        return { x: adjustedX, y: adjustedY };
    };

    // Drag-to-scroll handlers
    const handleDragStart = (clientX: number, clientY: number, stageX?: number, stageY?: number) => {
        if (!centererRef.current) return;

        setIsDragging(true);
        dragStartRef.current = {
            x: clientX,
            y: clientY,
            scrollLeft: centererRef.current.scrollLeft,
            scrollTop: centererRef.current.scrollTop
        };
        if (stageX !== undefined && stageY !== undefined) {
            dragStartStagePosRef.current = { x: stageX, y: stageY };
        }
    };

    const handleDragMove = (clientX: number, clientY: number) => {
        if (!isDragging || !dragStartRef.current || !centererRef.current) return;

        const deltaX = dragStartRef.current.x - clientX;
        const deltaY = dragStartRef.current.y - clientY;

        centererRef.current.scrollLeft = dragStartRef.current.scrollLeft + deltaX;
        centererRef.current.scrollTop = dragStartRef.current.scrollTop + deltaY;
    };

    const handleDragEnd = () => {
        setIsDragging(false);
        dragStartRef.current = null;
        dragStartStagePosRef.current = null;
    };

    const handleLegendBarDragStart = (clientX: number) => {
        if (!legendBarRef.current) return;
        if (legendBarMomentumRef.current !== null) {
            cancelAnimationFrame(legendBarMomentumRef.current);
            legendBarMomentumRef.current = null;
        }
        legendBarVelocityRef.current = [];
        setIsLegendBarDragging(true);
        legendBarDragStartRef.current = {
            x: clientX,
            scrollLeft: legendBarRef.current.scrollLeft
        };
    };

    const handleLegendBarDragMove = (clientX: number) => {
        if (!isLegendBarDragging || !legendBarDragStartRef.current || !legendBarRef.current) return;

        const deltaX = legendBarDragStartRef.current.x - clientX;
        legendBarRef.current.scrollLeft = legendBarDragStartRef.current.scrollLeft + deltaX;

        const samples = legendBarVelocityRef.current;
        samples.push({ x: clientX, t: performance.now() });
        if (samples.length > 10) samples.shift();
    };

    const handleLegendBarDragEnd = () => {
        setIsLegendBarDragging(false);
        legendBarDragStartRef.current = null;

        const el = legendBarRef.current;
        const samples = legendBarVelocityRef.current;
        legendBarVelocityRef.current = [];

        if (!el || samples.length < 2) return;

        const recent = samples.slice(-5);
        let vSum = 0;
        let count = 0;
        for (let i = 1; i < recent.length; i++) {
            const dt = recent[i].t - recent[i - 1].t;
            if (dt > 0) {
                vSum += (recent[i - 1].x - recent[i].x) / dt;
                count++;
            }
        }
        let velocity = count > 0 ? vSum / count : 0;
        velocity = Math.max(-2, Math.min(2, velocity)) * 0.5;

        const FRICTION = 1;
        const MIN_VELOCITY = 0.1;

        const tick = () => {
            if (!el || Math.abs(velocity) < MIN_VELOCITY) return;
            const maxScroll = el.scrollWidth - el.clientWidth;
            if (maxScroll <= 0) return;

            let next = el.scrollLeft + velocity;
            if (next <= 0 || next >= maxScroll) {
                velocity *= 0.5;
                next = Math.max(0, Math.min(maxScroll, next));
            }
            el.scrollLeft = next;
            velocity *= FRICTION;
            legendBarMomentumRef.current = requestAnimationFrame(tick);
        };
        legendBarMomentumRef.current = requestAnimationFrame(tick);
    };

    const stopLegendBarMomentum = () => {
        if (legendBarMomentumRef.current !== null) {
            cancelAnimationFrame(legendBarMomentumRef.current);
            legendBarMomentumRef.current = null;
        }
    };

    const handleLegendBarMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Right mouse button for drag
        if (e.button === 2) {
            e.preventDefault();
            e.stopPropagation();
            handleLegendBarDragStart(e.clientX);
        }
    };

    // Global mouse handlers for drag-to-scroll
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                handleDragMove(e.clientX, e.clientY);
            } else if (isLegendBarDragging) {
                handleLegendBarDragMove(e.clientX);
            }
            // Drawing logic is handled by handleStageMouseMove
        };

        const handleMouseUp = (e: MouseEvent) => {
            if (isDragging) {
                handleDragEnd();
            } else if (isLegendBarDragging) {
                handleLegendBarDragEnd();
            } else if (isMainPointerButton(e) && rightMouseButtonDown) {
                // Reset left mouse button state if released outside stage
                setRightMouseButtonDown(false);
                setHasMovedOnRightClick(false);
                if (isDrawing) {
                    setIsDrawing(false);
                    setNewSetterRect(null);
                }
            }
        };

        if (isDragging || isLegendBarDragging || rightMouseButtonDown) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            if (isDragging || isLegendBarDragging) {
                document.body.style.cursor = 'grabbing';
                document.body.style.userSelect = 'none';
            }
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [ isDragging, isLegendBarDragging, isDrawing, role, rightMouseButtonDown ]);

    useEffect(() => {
        return () => {
            if (legendBarMomentumRef.current !== null) {
                cancelAnimationFrame(legendBarMomentumRef.current);
            }
        };
    }, []);

    // Legend bar: container has pointer-events: none, items have auto — clicks on transparent
    // padding hit the Stage underneath; treat the legend's viewport rect as non-drawable.
    const LEGEND_DRAW_EXCLUSION_TOP_MARGIN_PX = -60;

    const isClientPointOverMeasurementLegendBar = (clientX: number, clientY: number): boolean => {
        const el = legendBarRef.current;
        if (!el || effectiveRole !== 'editor') {
            return false;
        }
        const r = el.getBoundingClientRect();
        const top = r.top - LEGEND_DRAW_EXCLUSION_TOP_MARGIN_PX;
        return clientX >= r.left && clientX <= r.right && clientY >= top && clientY <= r.bottom;
    };

    const clampStagePointerYBelowLegendTop = (pointerY: number): number => {
        const legendEl = legendBarRef.current;
        const containerEl = containerRef.current;
        if (!legendEl || !containerEl || effectiveRole !== 'editor') {
            return pointerY;
        }
        const legendRect = legendEl.getBoundingClientRect();
        const containerRect = containerEl.getBoundingClientRect();
        const stageH = stageSize.height;
        if (stageH <= 0 || containerRect.height <= 0) {
            return pointerY;
        }
        const legendTop = legendRect.top - LEGEND_DRAW_EXCLUSION_TOP_MARGIN_PX;
        const maxLogicalY = ((legendTop - containerRect.top) / containerRect.height) * stageH;
        if (maxLogicalY <= 0 || maxLogicalY >= stageH) {
            return pointerY;
        }
        return Math.min(pointerY, maxLogicalY);
    };

    // Step 4: Use Konva's relative pointer position

    const handleStageMouseDown = (e: any) => {
        const stage = e.target.getStage();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        if (effectiveRole === 'user') {
            // Left click / touch for username rect click
            if (isMainPointerButton(e.evt)) {
                const sequenceActive = sequenceMeasurement && !examMode;
                const allMeasured = sequenceActive && getFirstUnmeasuredVisibleIndex() === null;
                if (sequenceActive && !allMeasured) {
                    return;
                }
                // Check if click is inside any visible usernameRect
                const clickedUsernameRectIndex = usernameRects.findIndex(usernameRect => {
                    // Find the corresponding setterRect to get the full rectangle dimensions
                    const setterRect = rects.find(r => r.number === usernameRect.number);
                    if (!setterRect) return false;

                    // Check if this rectangle should be visible based on measurement density
                    if (!shouldShowRectangle(setterRect, effectiveCounter || '', density || '')) {
                        return false;
                    }

                    // Calculate rectangle boundaries in original PDF coordinates
                    const rectRight = setterRect.x + (setterRect.width < 0 ? 0 : setterRect.width);
                    const rectBottom = setterRect.y + (setterRect.height < 0 ? 0 : setterRect.height);
                    const rectLeft = setterRect.x + (setterRect.width < 0 ? setterRect.width : 0);
                    const rectTop = setterRect.y + (setterRect.height < 0 ? setterRect.height : 0);

                    // Use pointer coordinates directly since PDF and canvas have same size
                    const originalPointerX = pointer.x;
                    const originalPointerY = pointer.y;

                    // Check if click is inside the rectangle (allow both submitted and non-submitted)
                    return originalPointerX >= rectLeft && originalPointerX <= rectRight &&
                        originalPointerY >= rectTop && originalPointerY <= rectBottom;
                });

                if (clickedUsernameRectIndex !== -1) {
                    // If menu is already open, ignore click on other rectangles
                    if (showUserMenu) {
                        return;
                    }

                    // pointer.x/y are already relative to Stage/PDF content
                    // Use larger initial estimates (will be adjusted by useLayoutEffect after render)
                    const menuWidth = 300; // Max possible menu width
                    const menuHeight = 300; // Max possible menu height

                    // Get the corresponding setterRect for positioning
                    const currentUsernameRect = usernameRects[ clickedUsernameRectIndex ];
                    const setterRect = rects.find(r => r.number === currentUsernameRect.number);

                    // Store rect data for position adjustment after menu renders
                    currentUserMenuRectRef.current = setterRect || null;

                    // Position user context menu centered at the top
                    const centerer = centererRef.current;
                    if (centerer) {
                        const viewportLeft = centerer.scrollLeft;
                        const viewportTop = centerer.scrollTop;
                        const viewportWidth = centerer.clientWidth;

                        setCircleMenuPosition({
                            x: viewportLeft + (viewportWidth - menuWidth) / 2, // Center horizontally
                            y: viewportTop + 10  // 10px margin from top edge
                        });
                    } else {
                        // Fallback to original positioning if centerer not available
                        const adjustedPos = adjustMenuPositionForViewport(pointer.x, pointer.y, menuWidth, menuHeight, setterRect);
                        setCircleMenuPosition({
                            x: adjustedPos.x,
                            y: adjustedPos.y
                        });
                    }

                    // Store PDF-relative coordinates (use original pointer position for scroll tracking)
                    circleMenuPdfCoordsRef.current = { x: pointer.x, y: pointer.y };
                    setShowUserMenu(true);
                    setSelectedCircleIndex(clickedUsernameRectIndex);
                    const currentRect = usernameRects[ clickedUsernameRectIndex ];
                    setCircleTextInput(currentRect.text || '');
                    setCircleOkNo(null);
                    setExamSelectedTool(null);
                    setExamToolDropdownOpen(false);
                    e.evt.preventDefault();
                    e.evt.stopPropagation();
                    return;
                }
            }

            // Right click for drag-to-scroll
            if (e.evt.button === 2) {
                e.evt.preventDefault();
                e.evt.stopPropagation();

                // Right button - always start drag-to-scroll (even on rectangles)
                handleDragStart(e.evt.clientX, e.evt.clientY, pointer.x, pointer.y);
            }
        } else if (effectiveRole === 'editor') {
            // Left click / touch for drawing rectangle or context menu
            if (isMainPointerButton(e.evt)) {
                // Check if click is inside any setterRect
                const clickedSetterRectIndex = rects.findIndex(setterRect => {
                    // Calculate rectangle boundaries in original PDF coordinates
                    const rectRight = setterRect.x + (setterRect.width < 0 ? 0 : setterRect.width);
                    const rectBottom = setterRect.y + (setterRect.height < 0 ? 0 : setterRect.height);
                    const rectLeft = setterRect.x + (setterRect.width < 0 ? setterRect.width : 0);
                    const rectTop = setterRect.y + (setterRect.height < 0 ? setterRect.height : 0);

                    // Use pointer coordinates directly since PDF and canvas have same size
                    const originalPointerX = pointer.x;
                    const originalPointerY = pointer.y;

                    return originalPointerX >= rectLeft && originalPointerX <= rectRight &&
                        originalPointerY >= rectTop && originalPointerY <= rectBottom;
                });

                if (clickedSetterRectIndex !== -1) {
                    // If menu is already open, ignore click on other rectangles
                    if (showEditorMenu) {
                        return;
                    }

                    // Click on existing rectangle - show context menu
                    const setterRect = rects[ clickedSetterRectIndex ];
                    // pointer.x/y are already relative to Stage/PDF content
                    const menuWidth = 200; // Initial approximate width (will be adjusted after render)
                    const menuHeight = 180; // Initial approximate height (will be adjusted after render)

                    // Store rect data for position adjustment after menu renders
                    currentMenuRectRef.current = setterRect;

                    // Position editor context menu at top right corner of screen
                    const centerer = centererRef.current;
                    if (centerer) {
                        const viewportLeft = centerer.scrollLeft;
                        const viewportTop = centerer.scrollTop;
                        const viewportWidth = centerer.clientWidth;

                        setMenuPosition({
                            x: viewportLeft + viewportWidth - menuWidth - 10, // 10px margin from right edge
                            y: viewportTop + 10 // 10px margin from top edge
                        });
                    } else {
                        // Fallback to original positioning if centerer not available
                        const adjustedPos = adjustMenuPositionForViewport(pointer.x, pointer.y, menuWidth, menuHeight, setterRect);
                        setMenuPosition({
                            x: adjustedPos.x,
                            y: adjustedPos.y
                        });
                    }

                    // Store PDF-relative coordinates (use original pointer position for scroll tracking)
                    editorMenuPdfCoordsRef.current = { x: pointer.x, y: pointer.y };
                    setShowEditorMenu(true);
                    setSelectedRectIndex(clickedSetterRectIndex);
                    // Update refs for menu state
                    showEditorMenuRef.current = true;
                    selectedRectIndexRef.current = clickedSetterRectIndex;
                    // Set selected tool from legend based on existing enteredNumber or default to 1
                    const existingToolNumber = setterRect.enteredNumber || selectedLegendToolNumber || 1;
                    setSelectedLegendToolNumber(existingToolNumber);
                    setTextInput(existingToolNumber.toString());
                    if (companyToolsForLegend.length > 0 && existingToolNumber > companyToolsForLegend.length) {
                        setToolInputError(`${strings.PDFVIEWER_MAX_TOOL_NUMBER || 'Maksimaalne number on'} ${companyToolsForLegend.length}`);
                    } else {
                        setToolInputError('');
                    }
                    const clickedRect = rects[ clickedSetterRectIndex ];
                    setTolerancePlus(clickedRect.tolerancePlus || '');
                    setToleranceMinus(clickedRect.toleranceMinus || '');
                    setNominalDimension(nominalDimensionForEditorFromRect(clickedRect));
                    setRecognizedValue(recognizedValueForEditorFromRect(clickedRect));
                    setSelectedToleranceType(clickedRect.toleranceType || 'plusminus');
                    toleranceSourceFromAutoFillRef.current = (clickedRect.toleranceFromStandard ?? false) || (clickedRect.toleranceFromIso286Standard ?? false);
                    toleranceHadIso286LetterRef.current = clickedRect.hasIso286LetterDesignation ?? false;
                    toleranceManuallyCorrectedRef.current = clickedRect.toleranceManuallySet ?? false;
                    drawingToleranceFromOcrRef.current = clickedRect.toleranceFromDrawingRecognition ?? false;
                    if (premiumUltimatePlanAllowed && (clickedRect.hasIso286LetterDesignation ?? false)) {
                        const recOpen = recognizedValueForEditorFromRect(clickedRect).trim();
                        const afterOpen = recOpen.replace(/^[Øø⌀∅oOφϕΦ]?\s*[\d.,]+/, '').trim();
                        ocrIso286DesignationRef.current = extractIso286Designation(afterOpen);
                    } else {
                        ocrIso286DesignationRef.current = null;
                    }
                    mDesignationRef.current = clickedRect.mDesignation || null;
                    if (clickedRect.mDesignation) {
                        const savedPart = clickedRect.mDesignationActivePart === 'hole' || clickedRect.mDesignationActivePart === 'shaft'
                            ? clickedRect.mDesignationActivePart
                            : 'shaft';
                        setIso286ActiveButton(
                            isIso286MPairHoleToleranceOnly(clickedRect.mDesignation) ? 'hole' : savedPart
                        );
                    }
                    return;
                }

                // Block rectangle creation when context menu is open
                if (showEditorMenu) {
                    return;
                }

                // Do not start a new rect when the click is on the legend strip (padding passes through)
                if (isClientPointOverMeasurementLegendBar(e.evt.clientX, e.evt.clientY)) {
                    return;
                }

                // Click not on existing rectangle - start drawing rectangle
                if (e.target === e.target.getStage()) {
                    setRightMouseButtonDown(true);
                    setHasMovedOnRightClick(false);
                    setIsDrawing(true);
                    setNewSetterRect({
                        x: pointer.x,
                        y: pointer.y,
                        width: 0,
                        height: 0,
                        number: nextNumber
                    });
                    dragStartStagePosRef.current = { x: pointer.x, y: pointer.y };
                }
            }

            // Right click for drag-to-scroll
            if (e.evt.button === 2) {
                e.evt.preventDefault();
                e.evt.stopPropagation();

                if (e.target !== e.target.getStage()) {
                    // Click on a shape - don't start dragging
                    return;
                }

                // Right button - always start drag-to-scroll
                handleDragStart(e.evt.clientX, e.evt.clientY, pointer.x, pointer.y);
            }
        }
    };

    const handleStageMouseMove = (e: any) => {
        const stage = e.target.getStage();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        // Drawing logic - only when right mouse button is down
        if (isDrawing && newSetterRect && rightMouseButtonDown) {
            setHasMovedOnRightClick(true);

            // Use pointer coordinates directly since PDF and canvas have same size
            const originalPointerX = pointer.x;
            const originalPointerY = clampStagePointerYBelowLegendTop(pointer.y);

            let width = originalPointerX - newSetterRect.x;
            let height = originalPointerY - newSetterRect.y;

            // Calculate drag distance in original coordinates
            const dragDistance = Math.sqrt(width * width + height * height);

            // Only allow rectangle creation if drag distance is greater than minimum
            if (dragDistance < MIN_DRAG_DISTANCE) {
                return;
            }

            // Enforce minimum size constraints
            if (Math.abs(width) < MIN_RECT_SIZE) {
                width = width < 0 ? -MIN_RECT_SIZE : MIN_RECT_SIZE;
            }
            if (Math.abs(height) < MIN_RECT_SIZE) {
                height = height < 0 ? -MIN_RECT_SIZE : MIN_RECT_SIZE;
            }

            setNewSetterRect({
                ...newSetterRect,
                width,
                height,
            });
        }
    };

    const handleStageMouseUp = (e: any) => {
        // Handle left mouse / touch release for rectangle creation
        if (isMainPointerButton(e.evt) && isDrawing && newSetterRect) {
            if (hasMovedOnRightClick && Math.abs(newSetterRect.width) >= MIN_RECT_SIZE && Math.abs(newSetterRect.height) >= MIN_RECT_SIZE) {
                // Normalize coordinates to ensure x,y is always top-left corner and width,height are always positive
                const normalizedX = newSetterRect.width < 0 ? newSetterRect.x + newSetterRect.width : newSetterRect.x;
                const normalizedY = newSetterRect.height < 0 ? newSetterRect.y + newSetterRect.height : newSetterRect.y;
                const normalizedWidth = Math.abs(newSetterRect.width);
                const normalizedHeight = Math.abs(newSetterRect.height);

                // Auto-assign tool number if a tool is selected from legend
                const finalSetterRect: {
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                    number: number;
                    enteredNumber?: number;
                    isConfirmed?: boolean;
                } = {
                    x: normalizedX,
                    y: normalizedY,
                    width: normalizedWidth,
                    height: normalizedHeight,
                    number: nextNumber,
                    isConfirmed: false
                };

                // If tool is selected, auto-assign it
                if (selectedLegendToolNumber !== null) {
                    finalSetterRect.enteredNumber = selectedLegendToolNumber;
                }

                const updatedSetterRects = [ ...rects, finalSetterRect ];
                setRects(updatedSetterRects);
                setNextNumber(nextNumber + 1);

                const stage = e.target.getStage();
                const pointer = stage.getPointerPosition();
                if (pointer) {
                    // pointer.x/y are already relative to Stage/PDF content
                    const menuWidth = 200; // Initial approximate width (will be adjusted after render)
                    const menuHeight = 180; // Initial approximate height (will be adjusted after render)

                    // Store rect data for position adjustment after menu renders
                    currentMenuRectRef.current = finalSetterRect;

                    // Position editor context menu at top right corner of screen
                    const centerer = centererRef.current;
                    if (centerer) {
                        const viewportLeft = centerer.scrollLeft;
                        const viewportTop = centerer.scrollTop;
                        const viewportWidth = centerer.clientWidth;

                        setMenuPosition({
                            x: viewportLeft + viewportWidth - menuWidth - 10, // 10px margin from right edge
                            y: viewportTop + 10 // 10px margin from top edge
                        });
                    } else {
                        // Fallback to original positioning if centerer not available
                        const adjustedPos = adjustMenuPositionForViewport(pointer.x, pointer.y, menuWidth, menuHeight, finalSetterRect);
                        setMenuPosition({
                            x: adjustedPos.x,
                            y: adjustedPos.y
                        });
                    }

                    // Store PDF-relative coordinates (use original pointer position for scroll tracking)
                    editorMenuPdfCoordsRef.current = { x: pointer.x, y: pointer.y };
                    setSelectedRectIndex(updatedSetterRects.length - 1);
                    // Update refs for menu state
                    showEditorMenuRef.current = true;
                    selectedRectIndexRef.current = updatedSetterRects.length - 1;
                    // Set tool input to selected tool number or default to 1
                    const defaultToolNumber = selectedLegendToolNumber !== null ? selectedLegendToolNumber : 1;
                    setTextInput(defaultToolNumber.toString());
                    if (selectedLegendToolNumber === null) {
                        setSelectedLegendToolNumber(1);
                    }
                    setSelectedToleranceType('plusminus');
                    setTolerancePlus('');
                    setToleranceMinus('');
                    setNominalDimension('');
                    setRecognizedValue('');
                    toleranceSourceFromAutoFillRef.current = true;
                    toleranceHadIso286LetterRef.current = false;
                    toleranceManuallyCorrectedRef.current = false;
                    drawingToleranceFromOcrRef.current = false;
                    ocrIso286DesignationRef.current = null;
                    setShowEditorMenu(true);

                    void runRegionOcrForBounds({
                        x: normalizedX,
                        y: normalizedY,
                        width: normalizedWidth,
                        height: normalizedHeight
                    });
                }
            }
            setIsDrawing(false);
            setNewSetterRect(null);
            setRightMouseButtonDown(false);
            setHasMovedOnRightClick(false);
        }
    };

    const applyRecognizedPdfTextToEditorForm = (rawText: string, geminiTextType?: DrawingTextType) => {
        // Strip leading minus / hyphen / dash variants so nominal can never be negative
        const sanitizedRawText = rawText.replace(/^\s*[-\u2010-\u2015\u2212]+\s*/, '');
        const trimmed = sanitizedRawText.trim();
        if (!trimmed) return;

        const hints = premiumUltimatePlanAllowed ? parseIsoOcrHints(trimmed) : null;
        if (premiumUltimatePlanAllowed && hints) {
            const sel = getMenuSelectionsFromIsoOcrHints(hints);
            if (sel.linear) setG2UserSelectedStandard(sel.linear);
            if (sel.shape) setG2UserSelectedShapeStandard(sel.shape);
        }

        const labelsToRecord = premiumUltimatePlanAllowed
            ? getStandardLabelsRecognizedFromRegionOcr(hints, trimmed)
            : [];

        if (labelsToRecord.length > 0) {
            logger.log('[OCR] Recording standard(s) parsed from editor region OCR');
            setIsoRecognizedSentences(prev => {
                let next = [ ...prev ];
                for (const label of labelsToRecord) {
                    const low = label.trim().toLowerCase();
                    if (!next.some(x => x.trim().toLowerCase() === low)) next.push(label);
                }
                return next;
            });
        }

        const isoNoteWithoutLeadingDimension =
            premiumUltimatePlanAllowed && !!hints && !ocrTrimLooksLikeLeadingDimension(trimmed);
        if (isoNoteWithoutLeadingDimension) {
            setRecognizedValue('');
            setNominalDimension('');
            setTolerancePlus('');
            setToleranceMinus('');
            setSelectedToleranceType('plusminus');
            toleranceSourceFromAutoFillRef.current = false;
            toleranceHadIso286LetterRef.current = false;
            toleranceManuallyCorrectedRef.current = false;
            drawingToleranceFromOcrRef.current = false;
            ocrIso286DesignationRef.current = null;
            return;
        }

        const textType = geminiTextType ?? classifyDrawingText(trimmed);
        logger.log('[OCR] Text classified:', { text: `"${trimmed}"`, geminiTextType, resolvedType: textType });

        if (textType === 'text') {
            setRecognizedValue(trimmed);
            setNominalDimension(trimmed);
            setTolerancePlus('');
            setToleranceMinus('');
            setSelectedToleranceType('plusminus');
            toleranceSourceFromAutoFillRef.current = false;
            toleranceHadIso286LetterRef.current = false;
            toleranceManuallyCorrectedRef.current = false;
            drawingToleranceFromOcrRef.current = false;
            ocrIso286DesignationRef.current = null;
            return;
        }

        const displayRecognized = normalizeOcrMisreadDiameterQ(trimmed);
        const parsed = parseRecognizedTextToNominalAndTolerance(sanitizedRawText, premiumUltimatePlanAllowed);
        const isDiam = hasDiameterMark(parsed.nominal);
        logger.log('[OCR] Region parsed:', {
            recognizedText: `"${displayRecognized}"`,
            charCodes: [...displayRecognized].map(c => c.charCodeAt(0).toString(16).toUpperCase()).join(' '),
            nominal: `"${parsed.nominal}"`,
            nominalCharCodes: [...parsed.nominal].map(c => c.charCodeAt(0).toString(16).toUpperCase()).join(' '),
            hasDiameterMark: isDiam,
            tolerancePlus: parsed.tolerancePlus,
            toleranceMinus: parsed.toleranceMinus
        });
        const dispTrim = displayRecognized.trim();
        const recognizedShort =
            getIso2768ChamferNominalDisplayCore(dispTrim) ?? displayRecognized;
        setRecognizedValue(recognizedShort);
        const nominalFromOcr = getIso2768ChamferNominalDisplayCore(dispTrim) ?? parsed.nominal;
        setNominalDimension(nominalFromOcr);
        setTolerancePlus(parsed.tolerancePlus);
        setToleranceMinus(parsed.toleranceMinus);
        setSelectedToleranceType(parsed.toleranceType);
        toleranceSourceFromAutoFillRef.current =
            ocrLinearHintsAllowStandardTolerance(hints, effectiveGeneralStandard) &&
            ocrParsedTolerancesMatchStandards({
                parsed,
                displayRecognized,
                premiumUltimatePlanAllowed,
                effectiveGeneralStandard,
                bothHoleShaftSelected,
                iso286ActiveButton,
                effectiveIso286Hole,
                effectiveIso286Shaft,
                iso22081LinearSizes,
                iso22081ShapeSizes,
                iso22081AngularSizes,
                iso22081RadiusChamferSizes
            });
        const afterNominal = displayRecognized.replace(/^[Øø⌀∅oOφϕΦ]?\s*[\d.,]+/, '').trim();
        const desFromOcr =
            premiumUltimatePlanAllowed ? extractIso286Designation(afterNominal) : null;
        toleranceHadIso286LetterRef.current = !!desFromOcr;
        ocrIso286DesignationRef.current = desFromOcr;
        // OCR-provided tolerance values are an auto-source, not a manual correction
        toleranceManuallyCorrectedRef.current = false;
        const hasParsedTolerance =
            !!(parsed.tolerancePlus && parsed.tolerancePlus.trim()) ||
            !!(parsed.toleranceMinus && parsed.toleranceMinus.trim());
        drawingToleranceFromOcrRef.current =
            parsed.toleranceFromStandardTable === true || hasParsedTolerance;
    };

    /**
     * Region text recognition for the editor form.
     * Order: (1) PDF.js text layer in the region — use vector text from the PDF if any item overlaps
     * the box. (2) Only if PDF.js returns no items, run Gemini vision OCR — but only while the
     * editor is in editor mode ({@link effectiveRole} === 'editor'), not in user preview mode ("U").
     * When forceOcr=true: skip PDF.js and run Gemini only (editor "eye" button, editor mode only).
     */
    const runRegionOcrForBounds = async (
        rectBounds: { x: number; y: number; width: number; height: number },
        options?: { forceOcr?: boolean }
    ) => {
        if (!file) return;
        const forceOcr = options?.forceOcr === true;
        setRegionOcrLoading(true);
        setRegionOcrError('');
        try {
            let recognizedText = '';
            let geminiTextType: DrawingTextType | undefined;

            const runGemini = async (): Promise<{ text: string; textType?: DrawingTextType } | null> => {
                if (effectiveRole !== 'editor' || !premiumUltimatePlanAllowed) {
                    setRegionOcrError(strings.PDFVIEWER_REGION_OCR_EMPTY);
                    return null;
                }
                const canvasEl = getPdfPageCanvasFromViewerContainer(containerRef.current);
                if (!canvasEl) {
                    setRegionOcrError(strings.PDFVIEWER_REGION_OCR_PAGE_NOT_READY);
                    return null;
                }
                const geminiResult = await extractTextFromGeminiInRegion(
                    canvasEl,
                    stageSize.width,
                    stageSize.height,
                    rectBounds
                );
                if (!geminiResult.success) {
                    setRegionOcrError(strings.PDFVIEWER_REGION_OCR_FAILED);
                    if (geminiResult.error) {
                        logger.warn('[OCR] Gemini region:', geminiResult.error);
                    }
                    return null;
                }
                if (geminiResult.items.length === 0) {
                    setRegionOcrError(strings.PDFVIEWER_REGION_OCR_EMPTY);
                    return null;
                }
                return {
                    text: geminiResult.items.map(i => i.text).join(' '),
                    textType: geminiResult.items[0].textType
                };
            };

            if (forceOcr) {
                const geminiData = await runGemini();
                if (geminiData) {
                    recognizedText = geminiData.text;
                    geminiTextType = geminiData.textType;
                }
            } else {
                const pdfJsResult = await extractTextFromPdfJsInRegion(
                    file,
                    pageNumber,
                    stageSize.width,
                    stageSize.height,
                    rectBounds
                );

                if (pdfJsResult.success && pdfJsResult.items.length > 0) {
                    recognizedText = pdfJsResult.items.map(i => i.text).join(' ');
                } else if (effectiveRole === 'editor' && premiumUltimatePlanAllowed) {
                    const geminiData = await runGemini();
                    if (geminiData) {
                        recognizedText = geminiData.text;
                        geminiTextType = geminiData.textType;
                    }
                } else {
                    setRegionOcrError(strings.PDFVIEWER_REGION_OCR_EMPTY);
                }
            }

            if (recognizedText) {
                setRegionOcrError('');
                applyRecognizedPdfTextToEditorForm(recognizedText, geminiTextType);
            }
        } catch (error) {
            logger.error('[OCR] Region detection failed:', error);
            setRegionOcrError(strings.PDFVIEWER_REGION_OCR_FAILED);
        } finally {
            setRegionOcrLoading(false);
        }
    };

    const handleDetectedMeasurementEditorClick = (item: DetectedTextItem, pointer: { x: number; y: number }) => {
        if (effectiveRole !== 'editor' || !showDetectedMeasurements || isDrawing) return;

        const rawText = item.text || '';
        if (showEditorMenu && selectedRectIndex !== null) {
            applyRecognizedPdfTextToEditorForm(rawText);
            return;
        }

        const w = Math.max(item.width, MIN_RECT_SIZE);
        const h = Math.max(item.height, MIN_RECT_SIZE);
        const finalSetterRect: {
            x: number;
            y: number;
            width: number;
            height: number;
            number: number;
            enteredNumber?: number;
            isConfirmed?: boolean;
        } = {
            x: item.x,
            y: item.y,
            width: w,
            height: h,
            number: nextNumber,
            isConfirmed: false
        };
        if (selectedLegendToolNumber !== null) {
            finalSetterRect.enteredNumber = selectedLegendToolNumber;
        }

        const updatedSetterRects = [ ...rects, finalSetterRect ];
        setRects(updatedSetterRects);
        setNextNumber(nextNumber + 1);

        const menuWidth = 200;
        const menuHeight = 180;
        currentMenuRectRef.current = finalSetterRect;
        const centerer = centererRef.current;
        if (centerer) {
            const viewportLeft = centerer.scrollLeft;
            const viewportTop = centerer.scrollTop;
            const viewportWidth = centerer.clientWidth;
            setMenuPosition({
                x: viewportLeft + viewportWidth - menuWidth - 10,
                y: viewportTop + 10
            });
        } else {
            const adjustedPos = adjustMenuPositionForViewport(pointer.x, pointer.y, menuWidth, menuHeight, finalSetterRect);
            setMenuPosition({ x: adjustedPos.x, y: adjustedPos.y });
        }

        editorMenuPdfCoordsRef.current = { x: pointer.x, y: pointer.y };
        setSelectedRectIndex(updatedSetterRects.length - 1);
        showEditorMenuRef.current = true;
        selectedRectIndexRef.current = updatedSetterRects.length - 1;

        const defaultToolNumber = selectedLegendToolNumber !== null ? selectedLegendToolNumber : 1;
        setTextInput(defaultToolNumber.toString());
        if (selectedLegendToolNumber === null) {
            setSelectedLegendToolNumber(1);
        }
        setSelectedToleranceType('plusminus');
        setTolerancePlus('');
        setToleranceMinus('');
        setToolInputError('');
        setHoleShaftError('');
        mDesignationRef.current = null;
        setNominalDimension('');
        setRecognizedValue('');
        toleranceSourceFromAutoFillRef.current = true;
        toleranceHadIso286LetterRef.current = false;
        toleranceManuallyCorrectedRef.current = false;
        drawingToleranceFromOcrRef.current = false;
        ocrIso286DesignationRef.current = null;
        setShowEditorMenu(true);

        applyRecognizedPdfTextToEditorForm(rawText);
    };

    const handleRegionOcrClick = async () => {
        if (regionOcrLoading || selectedRectIndex === null || !file) return;
        const rect = rects[selectedRectIndex];
        if (!rect) return;
        // Eye button forces OCR (Gemini) over any PDF.js text present in the region.
        await runRegionOcrForBounds(
            {
                x: rect.x,
                y: rect.y,
                width: rect.width,
                height: rect.height
            },
            { forceOcr: true }
        );
    };


    const handleDelete = () => {
        if (selectedRectIndex !== null) {
            const newRects = [ ...rects ];
            newRects.splice(selectedRectIndex, 1);

            // Reorder numbers for remaining rectangles
            const reorderedRects = newRects.map((rect, index) => ({
                ...rect,
                number: index + 1
            }));

            setRects(reorderedRects);
            setNextNumber(reorderedRects.length + 1);
            setShowEditorMenu(false);
            showEditorMenuRef.current = false;
            selectedRectIndexRef.current = null;
            editorMenuPdfCoordsRef.current = null;
            setToolInputError('');
            setHoleShaftError('');
            setRegionOcrLoading(false);
            setRegionOcrError('');
        }
    };

    // Handle Delete and Escape keys to delete rectangle when in edit mode
    useEffect(() => {
        const handleDeleteKey = (e: KeyboardEvent) => {
            if (showEditorMenu && selectedRectIndex !== null && effectiveRole === 'editor') {
                if (e.key === 'Delete' || e.key === 'F4') {
                    e.preventDefault();
                    handleDelete();
                } else if (e.key === 'Escape') {
                    const currentRect = rects[selectedRectIndex];
                    if (!currentRect?.isConfirmed) {
                        e.preventDefault();
                        handleDelete();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleDeleteKey);

        return () => {
            document.removeEventListener('keydown', handleDeleteKey);
        };
    }, [ showEditorMenu, selectedRectIndex, role, handleDelete, rects ]);

    // Apply tool to rectangle when selected from legend while context menu is open
    const applyToolToRectangle = (toolNumber: number, rectIndex: number | null) => {
        if (rectIndex === null || rectIndex < 0) {
            return;
        }

        // Use functional update to get latest rects state
        setRects(currentRects => {
            if (rectIndex >= currentRects.length) {
                return currentRects;
            }

            // Create new array with updated rectangle
            return currentRects.map((rect, index) => {
                if (index === rectIndex) {
                    return {
                        ...rect,
                        enteredNumber: toolNumber,
                    };
                }
                return rect;
            });
        });

        // Also update selectedLegendToolNumber to match
        setSelectedLegendToolNumber(toolNumber);

        // Close menu
        setShowEditorMenu(false);
        showEditorMenuRef.current = false;
        selectedRectIndexRef.current = null;
        editorMenuPdfCoordsRef.current = null;
        setToolInputError('');
        setHoleShaftError('');
    };

    const handleApplyHoleTolerance = () => {
        if (!premiumUltimatePlanAllowed) return;
        setIso286ActiveButton('hole');
        setHoleShaftError('');
        if (mDesignationRef.current) {
            const pairM = parseIso286MSelectionPair(mDesignationRef.current);
            const nominal = (nominalDimension || recognizedValue || '').trim();
            if (pairM && hasDiameterMark(nominal)) {
                const nominalMm = parseNominalToMm(nominal);
                if (nominalMm !== null) {
                    const isoResult = lookupIso286Tolerance(pairM.hole, nominalMm);
                    if (isoResult) {
                        setTolerancePlus(isoResult.tolerancePlus);
                        setToleranceMinus(isoResult.toleranceMinus);
                        setSelectedToleranceType(isoResult.toleranceType);
                        toleranceSourceFromAutoFillRef.current = true;
                        toleranceHadIso286LetterRef.current = false;
                    }
                }
            }
            return;
        }
        const holeSelected = effectiveIso286Hole && effectiveIso286Hole.trim() !== '';
        const shaftSelected = effectiveIso286Shaft && effectiveIso286Shaft.trim() !== '';
        if (!holeSelected || !shaftSelected) {
            setHoleShaftError((strings as Record<string, string>).PDFVIEWER_HOLE_SHAFT_STANDARD_REQUIRED || 'Select shaft and hole standard in standard corner at the left...');
            return;
        }
        const nominal = (nominalDimension || recognizedValue || '').trim();
        if (!hasDiameterMark(nominal)) return;
        const nominalMm = parseNominalToMm(nominal);
        if (nominalMm === null) return;
        const designation = effectiveIso286Hole || 'H7';
        const isoResult = lookupIso286Tolerance(designation, nominalMm);
        if (isoResult) {
            setTolerancePlus(isoResult.tolerancePlus);
            setToleranceMinus(isoResult.toleranceMinus);
            setSelectedToleranceType(isoResult.toleranceType);
            toleranceSourceFromAutoFillRef.current = true;
            toleranceHadIso286LetterRef.current = false;
        }
    };

    const handleApplyShaftTolerance = () => {
        if (!premiumUltimatePlanAllowed) return;
        if (mDesignationRef.current && isIso286MPairHoleToleranceOnly(mDesignationRef.current)) {
            return;
        }
        setIso286ActiveButton('shaft');
        setHoleShaftError('');
        if (mDesignationRef.current) {
            const pairM = parseIso286MSelectionPair(mDesignationRef.current);
            const nominal = (nominalDimension || recognizedValue || '').trim();
            if (pairM && hasDiameterMark(nominal)) {
                const nominalMm = parseNominalToMm(nominal);
                if (nominalMm !== null) {
                    const isoResult = lookupIso286Tolerance(pairM.shaft, nominalMm);
                    if (isoResult) {
                        setTolerancePlus(isoResult.tolerancePlus);
                        setToleranceMinus(isoResult.toleranceMinus);
                        setSelectedToleranceType(isoResult.toleranceType);
                        toleranceSourceFromAutoFillRef.current = true;
                        toleranceHadIso286LetterRef.current = false;
                    }
                }
            }
            return;
        }
        const holeSelected = effectiveIso286Hole && effectiveIso286Hole.trim() !== '';
        const shaftSelected = effectiveIso286Shaft && effectiveIso286Shaft.trim() !== '';
        if (!holeSelected || !shaftSelected) {
            setHoleShaftError((strings as Record<string, string>).PDFVIEWER_HOLE_SHAFT_STANDARD_REQUIRED || 'Select shaft and hole standard in standard corner at the left...');
            return;
        }
        const nominal = (nominalDimension || recognizedValue || '').trim();
        if (!hasDiameterMark(nominal)) return;
        const nominalMm = parseNominalToMm(nominal);
        if (nominalMm === null) return;
        const designation = effectiveIso286Shaft || 'h6';
        const isoResult = lookupIso286Tolerance(designation, nominalMm);
        if (isoResult) {
            setTolerancePlus(isoResult.tolerancePlus);
            setToleranceMinus(isoResult.toleranceMinus);
            setSelectedToleranceType(isoResult.toleranceType);
            toleranceSourceFromAutoFillRef.current = true;
            toleranceHadIso286LetterRef.current = false;
        }
    };

    const handleMSelectTolerance = (value: string) => {
        if (!premiumUltimatePlanAllowed) return;
        setHoleShaftError('');
        setIso286MOpen(false);
        if (!value || !value.trim()) {
            mDesignationRef.current = null;
            setTolerancePlus('');
            setToleranceMinus('');
            toleranceSourceFromAutoFillRef.current = false;
            prevEffectiveGeneralStandardRef.current = null;
            prevEffectiveIso286DesignationRef.current = null;
            return;
        }
        mDesignationRef.current = value;
        const forceHoleSideForM = isIso286MPairHoleToleranceOnly(value);
        if (forceHoleSideForM) {
            setIso286ActiveButton('hole');
        }
        const pair = parseIso286MSelectionPair(value);
        const designation = forceHoleSideForM || iso286ActiveButton === 'hole'
            ? (pair?.hole ?? value)
            : (pair?.shaft ?? value);
        const nominal = (nominalDimension || recognizedValue || '').trim();
        if (hasDiameterMark(nominal)) {
            const nominalMm = parseNominalToMm(nominal);
            if (nominalMm !== null) {
                const isoResult = lookupIso286Tolerance(designation, nominalMm);
                if (isoResult) {
                    setTolerancePlus(isoResult.tolerancePlus);
                    setToleranceMinus(isoResult.toleranceMinus);
                    setSelectedToleranceType(isoResult.toleranceType);
                    toleranceSourceFromAutoFillRef.current = true;
                    toleranceHadIso286LetterRef.current = false;
                }
            }
        }
    };

    useEffect(() => {
        if (!iso286MOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (iso286MDropdownRef.current && !iso286MDropdownRef.current.contains(e.target as Node)) {
                setIso286MOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [ iso286MOpen ]);

    const handleInsertDiameter = () => {
        if (!premiumUltimatePlanAllowed) {
            let valBasic = nominalDimension;
            const hasDiamBasic = /^[Øø⌀∅oOφϕΦ]/.test(valBasic.trimStart());
            if (hasDiamBasic) {
                valBasic = valBasic.trimStart().replace(/^[Øø⌀∅oOφϕΦ]\s*/, '');
            } else {
                valBasic = valBasic.replace(/°\s*$/, '').trimEnd();
                valBasic = 'Ø' + valBasic;
            }
            setNominalDimension(valBasic);
            setTimeout(() => {
                if (nominalInputRef.current) {
                    nominalInputRef.current.focus();
                    const pos = hasDiamBasic ? 0 : 1;
                    nominalInputRef.current.setSelectionRange(pos, pos);
                }
            }, 0);
            return;
        }
        let val = nominalDimension;
        const hasDiam = /^[Øø⌀∅oOφϕΦ]/.test(val.trimStart());
        if (hasDiam) {
            val = val.trimStart().replace(/^[Øø⌀∅oOφϕΦ]\s*/, '');
        } else {
            val = val.replace(/°\s*$/, '').trimEnd();
            val = 'Ø' + val;
        }
        setNominalDimension(val);
        if (!hasDiam) {
            const holeSelected = effectiveIso286Hole && effectiveIso286Hole.trim() !== '';
            const shaftSelected = effectiveIso286Shaft && effectiveIso286Shaft.trim() !== '';
            if (holeSelected && shaftSelected) {
                const nominalMm = parseNominalToMm(val);
                if (nominalMm !== null) {
                    const designation = iso286ActiveButton === 'hole'
                        ? (effectiveIso286Hole || 'H7')
                        : (effectiveIso286Shaft || 'h6');
                    const isoResult = lookupIso286Tolerance(designation, nominalMm);
                    if (isoResult) {
                        setTolerancePlus(isoResult.tolerancePlus);
                        setToleranceMinus(isoResult.toleranceMinus);
                        setSelectedToleranceType(isoResult.toleranceType);
                        toleranceSourceFromAutoFillRef.current = true;
                        toleranceHadIso286LetterRef.current = false;
                    }
                }
            }
        } else if (effectiveGeneralStandard && val.trim() !== '') {
            const rec = recognizedValue.trim();
            const isCh = isIso2768ChamferHeightCallout(val.trim()) || isIso2768ChamferHeightCallout(rec);
            const nominalMm = parseNominalToMm(val);
            const legMm = isCh ? (getIso2768ChamferHeightLegMm(val.trim()) ?? getIso2768ChamferHeightLegMm(rec)) : null;
            const mmEff = legMm ?? nominalMm;
            if (mmEff !== null) {
                let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
                if (effectiveGeneralStandard === 'ISO 22081') {
                    result = getIso22081Tolerance(val, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
                } else {
                    result = lookupIso2768ToleranceForNominalDisplay(
                        effectiveGeneralStandard,
                        mmEff,
                        val,
                        rec
                    );
                }
                if (result) {
                    setTolerancePlus(result.tolerancePlus);
                    setToleranceMinus(result.toleranceMinus);
                    setSelectedToleranceType('plusminus');
                    toleranceSourceFromAutoFillRef.current = true;
                }
            }
        }
        setTimeout(() => {
            if (nominalInputRef.current) {
                nominalInputRef.current.focus();
                const pos = hasDiam ? 0 : 1;
                nominalInputRef.current.setSelectionRange(pos, pos);
            }
        }, 0);
    };

    const handleInsertAngle = () => {
        if (!premiumUltimatePlanAllowed) {
            let valBasic = nominalDimension;
            const hasAngleBasic = valBasic.trimEnd().endsWith('°');
            if (hasAngleBasic) {
                valBasic = valBasic.replace(/°\s*$/, '').trimEnd();
            } else {
                valBasic = valBasic.trimStart().replace(/^[Øø⌀∅oOφϕΦ]\s*/, '') + '°';
            }
            setNominalDimension(valBasic);
            setTimeout(() => {
                if (nominalInputRef.current) {
                    nominalInputRef.current.focus();
                    nominalInputRef.current.setSelectionRange(valBasic.length, valBasic.length);
                }
            }, 0);
            return;
        }
        let val = nominalDimension;
        const hasAngle = val.trimEnd().endsWith('°');
        if (hasAngle) {
            val = val.replace(/°\s*$/, '').trimEnd();
        } else {
            val = val.trimStart().replace(/^[Øø⌀∅oOφϕΦ]\s*/, '') + '°';
        }
        setNominalDimension(val);
        if (hasAngle && effectiveGeneralStandard && val.trim() !== '') {
            const rec = recognizedValue.trim();
            const isCh = isIso2768ChamferHeightCallout(val.trim()) || isIso2768ChamferHeightCallout(rec);
            const nominalMm = parseNominalToMm(val);
            const legMm = isCh ? (getIso2768ChamferHeightLegMm(val.trim()) ?? getIso2768ChamferHeightLegMm(rec)) : null;
            const mmEff = legMm ?? nominalMm;
            if (mmEff !== null) {
                let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
                if (effectiveGeneralStandard === 'ISO 22081') {
                    result = getIso22081Tolerance(val, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
                } else {
                    result = lookupIso2768ToleranceForNominalDisplay(
                        effectiveGeneralStandard,
                        mmEff,
                        val,
                        rec
                    );
                }
                if (result) {
                    setTolerancePlus(result.tolerancePlus);
                    setToleranceMinus(result.toleranceMinus);
                    setSelectedToleranceType('plusminus');
                    toleranceSourceFromAutoFillRef.current = true;
                }
            }
        } else if (!hasAngle && effectiveGeneralStandard && val.trim() !== '') {
            let result: { tolerancePlus: string; toleranceMinus: string } | null = null;
            if (effectiveGeneralStandard === 'ISO 22081') {
                result = getIso22081Tolerance(val, iso22081LinearSizes, iso22081ShapeSizes, iso22081AngularSizes, iso22081RadiusChamferSizes);
            } else {
                result = lookupIso2768AngularTolerance(effectiveGeneralStandard);
            }
            if (result) {
                setTolerancePlus(result.tolerancePlus);
                setToleranceMinus(result.toleranceMinus);
                setSelectedToleranceType('plusminus');
                toleranceSourceFromAutoFillRef.current = true;
            }
        }
        setTimeout(() => {
            if (nominalInputRef.current) {
                nominalInputRef.current.focus();
                nominalInputRef.current.setSelectionRange(val.length, val.length);
            }
        }, 0);
    };

    const handleEditorMarkRadius = () => {
        if (!premiumUltimatePlanAllowed) return;
        toleranceSourceFromAutoFillRef.current = true;
        const n = nominalDimension.trim();
        if (nominalLeadingIsRadiusLetterR(n)) {
            const plain = n.replace(/\s/g, '').replace(/^[Rr]/, '');
            setNominalDimension(plain);
            return;
        }
        let v = n.replace(/^\s*[Cc]\s*/i, '');
        if (isIso2768ChamferHeightCallout(n)) {
            const leg = parseIso2768ChamferHeightLegString(n);
            if (leg) {
                setNominalDimension('R' + leg);
                return;
            }
        }
        v = v.replace(/\s/g, '').replace(/^[Rr]/, '');
        setNominalDimension(v ? 'R' + v : 'R');
    };

    const handleEditorMarkChamfer = () => {
        if (!premiumUltimatePlanAllowed) return;
        toleranceSourceFromAutoFillRef.current = true;
        const n = nominalDimension.trim();
        if (/^\s*[Cc]\s*\S/.test(n)) {
            setNominalDimension(stripIso2768ChamferClassPrefix(n));
            return;
        }
        const rec = recognizedValue.trim();
        let raw = n || rec;
        raw = raw.replace(/^\s*[Rr]\s*/i, '');
        const body = stripIso2768ChamferClassPrefix(raw);
        if (isIso2768ChamferHeightCallout(rec) && !isIso2768ChamferHeightCallout(body)) {
            const coreRec = getIso2768ChamferNominalDisplayCore(rec);
            if (coreRec) {
                setNominalDimension(
                    /^\s*[Cc]/i.test(coreRec.trim())
                        ? coreRec
                        : 'C' + stripIso2768ChamferClassPrefix(coreRec)
                );
            } else {
                setNominalDimension('C' + stripIso2768ChamferClassPrefix(rec));
            }
            return;
        }
        if (isIso2768ChamferHeightCallout(body)) {
            const coreRaw = getIso2768ChamferNominalDisplayCore(raw);
            if (coreRaw) {
                setNominalDimension(
                    /^\s*[Cc]/i.test(coreRaw.trim())
                        ? coreRaw
                        : 'C' + stripIso2768ChamferClassPrefix(coreRaw)
                );
            } else {
                setNominalDimension('C' + body);
            }
            return;
        }
        const tail = raw.replace(/^\s*[Cc]\s*/i, '').trim();
        setNominalDimension(tail ? 'C' + tail : 'C');
    };

    const handleTextSave = () => {
        if (selectedRectIndex !== null) {
            const newRects = [ ...rects ];
            const nominal = (nominalDimension || recognizedValue || '').trim();
            const isDiameter = hasDiameterMark(nominal);

            // Validate tool number from input
            const toolNum = parseInt(textInput, 10);
            if (isNaN(toolNum) || toolNum < 1) {
                setToolInputError(strings.PDFVIEWER_MIN_TOOL_NUMBER || 'Number peab olema vähemalt 1');
                return;
            }
            if (toolNum > companyToolsForLegend.length) {
                setToolInputError(`${strings.PDFVIEWER_MAX_TOOL_NUMBER || 'Maksimaalne number on'} ${companyToolsForLegend.length}`);
                return;
            }

            // Clear error if validation passes
            setToolInputError('');

            // Update selectedLegendToolNumber to match input
            setSelectedLegendToolNumber(toolNum);

            const rv = recognizedValue.trim();
            const nomDimOnly = nominalDimension.trim();
            const nomBody = stripIso2768ChamferClassPrefix(nomDimOnly);
            const chamferCore =
                getIso2768ChamferNominalDisplayCore(rv) ?? getIso2768ChamferNominalDisplayCore(nomDimOnly);
            const chamferHintToSave = chamferCore ?? undefined;
            const nominalForRect = chamferCore ?? nominal;
            const prevSaved = newRects[ selectedRectIndex ];
            const persistDrawingTol =
                !toleranceManuallyCorrectedRef.current &&
                !mDesignationRef.current &&
                (drawingToleranceFromOcrRef.current || prevSaved.toleranceFromDrawingRecognition === true);
            newRects[ selectedRectIndex ] = {
                ...newRects[ selectedRectIndex ],
                enteredNumber: toolNum,
                tolerancePlus: tolerancePlus || undefined,
                toleranceMinus: toleranceMinus || undefined,
                nominalDimension: nominalForRect || undefined,
                iso2768ChamferCalloutHint: chamferHintToSave,
                toleranceType: selectedToleranceType,
                isConfirmed: true,
                toleranceFromStandard: toleranceSourceFromAutoFillRef.current && !isDiameter ? true : false,
                hasIso286LetterDesignation: isDiameter ? toleranceHadIso286LetterRef.current : undefined,
                toleranceFromIso286Standard: toleranceSourceFromAutoFillRef.current && isDiameter ? true : false,
                toleranceManuallySet:
                    !!(mDesignationRef.current) || toleranceManuallyCorrectedRef.current,
                toleranceFromDrawingRecognition: persistDrawingTol,
                mDesignation: isDiameter ? (mDesignationRef.current || undefined) : undefined,
                mDesignationActivePart: isDiameter && mDesignationRef.current && (iso286ActiveButton === 'hole' || iso286ActiveButton === 'shaft')
                    ? (isIso286MPairHoleToleranceOnly(mDesignationRef.current) ? 'hole' : iso286ActiveButton)
                    : undefined
            };
            setRects(newRects);
            setShowEditorMenu(false);
            showEditorMenuRef.current = false;
            selectedRectIndexRef.current = null;
            editorMenuPdfCoordsRef.current = null;
            mDesignationRef.current = null;
            setToolInputError('');
            setHoleShaftError('');
            setTolerancePlus('');
            setToleranceMinus('');
            setNominalDimension('');
            setRecognizedValue('');
            setSelectedToleranceType('plusminus');
            drawingToleranceFromOcrRef.current = false;
            ocrIso286DesignationRef.current = null;
        }
    };

    // Validate measurement input for user role - must match minToolPrecision decimal places
    const validateMeasurementInput = (value: string, minToolPrecision?: string): { isValid: boolean; error: string } => {
        if (!value || !value.trim()) {
            return { isValid: false, error: 'Palun sisestage mõõtmistulemus' };
        }

        // Replace comma with dot for parsing
        const normalizedValue = value.replace(',', '.');

        // Check if it's a valid number
        const numValue = parseFloat(normalizedValue);
        if (isNaN(numValue)) {
            return { isValid: false, error: 'Palun sisestage kehtiv number' };
        }

        // Check if value is at least 0
        if (numValue < 0) {
            return { isValid: false, error: 'Väärtus peab olema vähemalt 0' };
        }

        // Determine required decimal places from minToolPrecision
        let requiredDecimalPlaces = 2; // Default to 2 decimal places
        if (minToolPrecision) {
            const precisionParts = minToolPrecision.split('.');
            if (precisionParts.length === 2) {
                requiredDecimalPlaces = precisionParts[1].length;
            } else if (precisionParts.length === 1 && precisionParts[0] === '0') {
                // If minToolPrecision is "0", no decimal places required
                requiredDecimalPlaces = 0;
            }
        }

        // Check if value has required decimal places
        const decimalParts = normalizedValue.split('.');
        if (requiredDecimalPlaces === 0) {
            // No decimal places required - value should be integer
            if (decimalParts.length === 2 && decimalParts[1] !== '0' && parseFloat(decimalParts[1]) !== 0) {
                return { isValid: false, error: 'Palun sisestage täisarv (nt. 0)' };
            }
        } else {
            // Decimal places required
            if (decimalParts.length === 2) {
                const decimalPlaces = decimalParts[1].length;
                if (decimalPlaces < requiredDecimalPlaces) {
                    const example = `0.${'0'.repeat(requiredDecimalPlaces)}`;
                    return { isValid: false, error: `Palun sisestage vähemalt ${requiredDecimalPlaces} numbrit pärast koma (nt. ${example})` };
                }
            } else {
                // No decimal point - need to add decimal places
                const example = `0.${'0'.repeat(requiredDecimalPlaces)}`;
                return { isValid: false, error: `Palun sisestage vähemalt ${requiredDecimalPlaces} numbrit pärast koma (nt. ${example})` };
            }
        }

        return { isValid: true, error: '' };
    };

    const handleCircleTextSave = () => {
        if (selectedCircleIndex !== null) {
            const currentRect = usernameRects[ selectedCircleIndex ];
            if (examMode && examSelectedTool == null) return;
            const examToolToSave = examMode ? (examSelectedTool ?? undefined) : undefined;
            if (currentRect?.toolWithoutMeasurement) {
                const okNoPending = circleOkNoResultRef.current;
                if (okNoPending) {
                    setUsernameRects(prev => prev.map((rect, index) =>
                        index === selectedCircleIndex
                            ? { ...rect, text: okNoPending === 'ok' ? 'ok' : 'no', isSubmitted: true, examSelectedToolNumber: examToolToSave }
                            : rect
                    ));
                    setCircleOkNo(null);
                }
                setShowUserMenu(false);
                circleMenuPdfCoordsRef.current = null;
                setSelectedCircleIndex(null);
                setExamSelectedTool(null);
                setExamToolDropdownOpen(false);
                return;
            } else if (circleTextInput.trim()) {
                if (effectiveRole === 'user' && !examMode) {
                    const toolNumber = currentRect?.enteredNumber;
                    const selectedTool = toolNumber !== null && toolNumber !== undefined
                        ? companyToolsForLegend.find(t => t.number === toolNumber)
                        : null;
                    const minToolPrecision = selectedTool?.minToolPrecision;
                    const validation = validateMeasurementInput(circleTextInput, minToolPrecision);
                    if (!validation.isValid) {
                        return;
                    }
                }

                setUsernameRects(prev => prev.map((rect, index) =>
                    index === selectedCircleIndex
                        ? { ...rect, text: circleTextInput.trim(), isSubmitted: true, examSelectedToolNumber: examToolToSave }
                        : rect
                ));
                setCircleTextInput('');
            }
            setShowUserMenu(false);
            circleMenuPdfCoordsRef.current = null;
            setSelectedCircleIndex(null);
            setExamSelectedTool(null);
            setExamToolDropdownOpen(false);
        }
    };

    // Handle Enter key for all context menus - always active when menu is open
    useEffect(() => {
        const handleEnterKey = (e: KeyboardEvent) => {
            // Don't handle if focus is on an input field (let input handle it)
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            // Handle + and - for non-measurement OK/No buttons (without Ctrl to avoid zoom conflict)
            if (!e.ctrlKey && !e.metaKey && (e.key === '+' || e.key === '=' || e.key === '-')) {
                if (showUserMenu && selectedCircleIndex !== null) {
                    const currentRect = usernameRects[ selectedCircleIndex ];
                    const isToolWithoutMeasurement = currentRect?.toolWithoutMeasurement || false;
                    const isSubmitted = currentRect?.isSubmitted || false;

                        if (isToolWithoutMeasurement && !isSubmitted) {
                        if (e.key === '+' || e.key === '=') {
                            setCircleOkNo('ok');
                            e.preventDefault();
                            e.stopPropagation();
                        } else if (e.key === '-') {
                            setCircleOkNo('no');
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }
                }
            }

            if (e.key === 'Enter' || e.key === 'F2') {
                // Handle user menu
                if (showUserMenu && selectedCircleIndex !== null) {
                    const currentRect = usernameRects[ selectedCircleIndex ];
                    const isToolWithoutMeasurement = currentRect?.toolWithoutMeasurement || false;
                    const isSubmitted = currentRect?.isSubmitted || false;

                    if (!isSubmitted) {
                        const examToolForEnter = examMode ? (examSelectedTool ?? undefined) : undefined;
                        if (isToolWithoutMeasurement) {
                            const pendingOkNo = circleOkNoResultRef.current;
                            if (pendingOkNo !== null) {
                                setUsernameRects(prev => prev.map((rect, index) =>
                                    index === selectedCircleIndex
                                        ? { ...rect, text: pendingOkNo === 'ok' ? 'ok' : 'no', isSubmitted: true, examSelectedToolNumber: examToolForEnter }
                                        : rect
                                ));
                                setCircleOkNo(null);
                                setShowUserMenu(false);
                                circleMenuPdfCoordsRef.current = null;
                                setSelectedCircleIndex(null);
                                setExamSelectedTool(null);
                                setExamToolDropdownOpen(false);
                            } else {
                                setShowUserMenu(false);
                                circleMenuPdfCoordsRef.current = null;
                                setSelectedCircleIndex(null);
                                setCircleOkNo(null);
                                setExamSelectedTool(null);
                                setExamToolDropdownOpen(false);
                            }
                        } else {
                            if (circleTextInput.trim()) {
                                if (effectiveRole === 'user' && !examMode) {
                                    const toolNumber = currentRect?.enteredNumber;
                                    const selectedTool = toolNumber !== null && toolNumber !== undefined
                                        ? companyToolsForLegend.find(t => t.number === toolNumber)
                                        : null;
                                    const minToolPrecision = selectedTool?.minToolPrecision;
                                    const validation = validateMeasurementInput(circleTextInput, minToolPrecision);
                                    if (!validation.isValid) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        return;
                                    }
                                }
                                handleCircleTextSave();
                            } else {
                                setShowUserMenu(false);
                                circleMenuPdfCoordsRef.current = null;
                                setSelectedCircleIndex(null);
                                setCircleTextInput('');
                                setExamSelectedTool(null);
                                setExamToolDropdownOpen(false);
                            }
                        }
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }

                // Handle editor menu
                if (showEditorMenu && selectedRectIndex !== null) {
                    // Try to save - handleTextSave will validate and close menu if valid
                    // If validation fails, menu stays open (error shown)
                    handleTextSave();
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };

        document.addEventListener('keydown', handleEnterKey);

        return () => {
            document.removeEventListener('keydown', handleEnterKey);
        };
    }, [ showUserMenu, showEditorMenu, selectedCircleIndex, selectedRectIndex, circleTextInput, usernameRects ]);

    const handleToleranceTypeClick = (type: 'plusminus' | 'plus' | 'minus') => {
        toleranceSourceFromAutoFillRef.current = false;
        setSelectedToleranceType(type);
    };

    const sanitizeToleranceInput = (value: string): string => {
        let result = '';
        let hasDecimal = false;
        for (let i = 0; i < value.length; i++) {
            const c = value[i];
            if (c >= '0' && c <= '9') {
                result += c;
            } else if ((c === '.' || c === ',') && !hasDecimal) {
                result += c;
                hasDecimal = true;
            }
        }
        return result;
    };

    // Helper functions to get dynamic tolerance labels based on selected type
    const getTolerancePlusLabel = () => {
        if (!selectedToleranceType) return strings.TOLERANCE_PLUS_LABEL;

        switch (selectedToleranceType) {
            case 'plusminus': return '+';
            case 'plus': return '+';
            case 'minus': return '-';
            default: return strings.TOLERANCE_PLUS_LABEL;
        }
    };

    const getToleranceMinusLabel = () => {
        switch (selectedToleranceType) {
            case 'plusminus': return '-';
            case 'plus': return '+';
            case 'minus': return '-';
            default: return '-';
        }
    };

    const handleSave = async (orderOverride?: string, pdfMeasuredDetailNumberOverride?: string) => {
        if (effectiveRole === 'user' && role === 'editor' && orderOverride === undefined) {
            setOrderNumberInputForSave('');
            setEditorPdfMeasuredDetailNumberForSave('');
            setOrderNumberModalForEditor(true);
            return;
        }
        if (effectiveRole === 'user') {
            let localServerActive = false;
            let cloudDbActive = false;
            try {
                const mode = await getStorageMode();
                localServerActive = mode === 'localServer';
                cloudDbActive = mode === 'cloudDb';
            } catch {
                localServerActive = false;
                cloudDbActive = false;
            }

            let folderDisplayPath = '';
            if (!localServerActive && !cloudDbActive) {
                const { success: hasFolder, displayPath } = await ensureDownloadFolderHandle();
                folderDisplayPath = displayPath;
                if (!hasFolder) {
                    logger.warn('[PdfViewer] No download folder available, will use browser default');
                }
            }

            const orderToUse = orderOverride ?? order;
            const pdfMeasuredDetailNumber =
                role === 'editor'
                    ? (pdfMeasuredDetailNumberOverride !== undefined && pdfMeasuredDetailNumberOverride.trim() !== ''
                        ? pdfMeasuredDetailNumberOverride.trim().replace(/\D/g, '')
                        : '')
                    : effectiveCounter;
            // Get report strings early for use in worksheetData
            const reportStrings = getStrings(reportLanguage);

            // Helper function to convert hex color to lightened RGB array [r, g, b]
            // Creates a pastel/light version of the color for better readability
            const hexToLightRgbArray = (hex: string): [ number, number, number ] => {
                const cleanHex = hex.replace('#', '');
                const r = parseInt(cleanHex.substring(0, 2), 16);
                const g = parseInt(cleanHex.substring(2, 4), 16);
                const b = parseInt(cleanHex.substring(4, 6), 16);
                // Lighten the color by mixing with white (factor 0.65 = 65% towards white)
                const factor = 0.65;
                const lightR = Math.round(r + (255 - r) * factor);
                const lightG = Math.round(g + (255 - g) * factor);
                const lightB = Math.round(b + (255 - b) * factor);
                return [ lightR, lightG, lightB ];
            };

            // Create username data in the same structure as Excel export
            const worksheetData = usernameRects.map(usernameRect => {
                const setterRect = rects.find(r => r.number === usernameRect.number);

                let toleranceSymbol = '';
                let toleranceStatusColor: string | null = null;

                // Check if this is a non-measurement tool
                if (usernameRect.toolWithoutMeasurement && usernameRect.isSubmitted) {
                    // For non-measurement tools, use ok/no result
                    if (usernameRect.text === 'ok') {
                        toleranceSymbol = '✔️';
                        toleranceStatusColor = TOLERANCE_COLORS.CORRECT;
                    } else if (usernameRect.text === 'no') {
                        toleranceSymbol = '❌';
                        toleranceStatusColor = TOLERANCE_COLORS.OUT;
                    }
                } else if (setterRect?.tolerancePlus && setterRect?.toleranceMinus && setterRect?.nominalDimension) {
                    // For measurement tools with tolerance, calculate tolerance status
                    const result = parseNumberWithComma(usernameRect.text || usernameRect.enteredNumber?.toString());
                    const nominal = parseSetterRectNominalToComparableNumber(setterRect);

                    if (!isNaN(result) && !isNaN(nominal)) {
                        const { upperLimit, lowerLimit } = calculateToleranceLimits(
                            nominal,
                            setterRect.tolerancePlus,
                            setterRect.toleranceMinus,
                            setterRect.toleranceType
                        );
                        const isInTolerance = isValueInTolerance(result, lowerLimit, upperLimit);

                        if (usernameRect.isSubmitted) {
                            toleranceSymbol = isInTolerance ? '✔️' : '❌';
                            // Calculate tolerance status for color
                            const status = getToleranceStatus(
                                result,
                                nominal,
                                setterRect.tolerancePlus,
                                setterRect.toleranceMinus,
                                setterRect.toleranceType
                            );
                            if (status === 'in') {
                                toleranceStatusColor = TOLERANCE_COLORS.IN;
                            } else if (status === 'warning') {
                                toleranceStatusColor = TOLERANCE_COLORS.WARNING;
                            } else {
                                toleranceStatusColor = TOLERANCE_COLORS.OUT;
                            }
                        }
                    }
                } else if (usernameRect.isSubmitted && setterRect?.nominalDimension) {
                    // For measurement tools without tolerance values, use ±0.1 tolerance
                    const result = parseNumberWithComma(usernameRect.text || usernameRect.enteredNumber?.toString());
                    const nominal = parseSetterRectNominalToComparableNumber(setterRect);

                    if (!isNaN(result) && !isNaN(nominal)) {
                        const isInTolerance = isValueInTolerance(result, nominal - 0.1, nominal + 0.1);
                        toleranceSymbol = isInTolerance ? '✔️' : '❌';
                        // Calculate tolerance status for color using ±0.1
                        const status = getToleranceStatus(
                            result,
                            nominal,
                            '0.1',
                            '0.1',
                            'plusminus'
                        );
                        if (status === 'in') {
                            toleranceStatusColor = TOLERANCE_COLORS.IN;
                        } else if (status === 'warning') {
                            toleranceStatusColor = TOLERANCE_COLORS.WARNING;
                        } else {
                            toleranceStatusColor = TOLERANCE_COLORS.OUT;
                        }
                    }
                }

                const localeMap: { [ key in Language ]: string } = {
                    'et': 'et-EE',
                    'en': 'en-US',
                    'ru': 'ru-RU'
                };
                const locale = localeMap[ reportLanguage ];

                // Use English keys internally for consistency, but we'll translate headers later
                // Format tolerance values with signs according to tolerance type:
                // plusminus: upper +, lower -
                // plus: both +
                // minus: both -
                const upperValue = formatToleranceForReport(setterRect?.tolerancePlus, setterRect?.toleranceMinus, setterRect?.toleranceType, true);
                const lowerValue = formatToleranceForReport(setterRect?.tolerancePlus, setterRect?.toleranceMinus, setterRect?.toleranceType, false);
                // Combine upper and lower tolerance in one column with two lines
                // Always show both values: upper at top, lower at bottom
                const toleranceValue = (upperValue || lowerValue) ? `${upperValue}\n${lowerValue}` : '';

                // Calculate min and max values based on nominal dimension and tolerance
                let minValue = '';
                let maxValue = '';
                if (setterRect?.nominalDimension && setterRect?.tolerancePlus && setterRect?.toleranceMinus) {
                    const nominal = parseSetterRectNominalToComparableNumber(setterRect);
                    if (!isNaN(nominal)) {
                        const { upperLimit, lowerLimit } = calculateToleranceLimits(
                            nominal,
                            setterRect.tolerancePlus,
                            setterRect.toleranceMinus,
                            setterRect.toleranceType
                        );
                        minValue = lowerLimit.toFixed(4).replace(/\.?0+$/, '');
                        maxValue = upperLimit.toFixed(4).replace(/\.?0+$/, '');
                    }
                }

                // Get tool image URL for this measurement
                const toolNumber = setterRect?.enteredNumber;
                let toolImageUrl = '';
                if (toolNumber != null) {
                    const tool = companyToolsForLegend.find(t => t.number === toolNumber);
                    if (tool && tool.imageUrl) {
                        toolImageUrl = tool.imageUrl;
                    }
                }

                const examInstrCorrect = examMode && usernameRect.examSelectedToolNumber != null && setterRect?.enteredNumber != null
                    ? usernameRect.examSelectedToolNumber === setterRect.enteredNumber
                    : null;

                let toolCellValue: string;
                if (examMode && usernameRect.examSelectedToolNumber != null && setterRect?.enteredNumber != null) {
                    toolCellValue = `${usernameRect.examSelectedToolNumber} - ${setterRect.enteredNumber}`;
                } else {
                    toolCellValue = setterRect?.enteredNumber != null ? String(setterRect.enteredNumber) : '';
                }

                const data: Record<string, unknown> = {
                    'M. Nr': setterRect ? String(setterRect.number) : '',
                    'Nominal Dim.': setterRect?.nominalDimension || '',
                    Tolerance: toleranceValue,
                    'Min': minValue,
                    'Max': maxValue,
                    Measured: usernameRect.text || usernameRect.enteredNumber || '',
                    Tool: toolCellValue,
                };
                data._toolImageUrl = toolImageUrl;
                data._toleranceColor = toleranceStatusColor;
                data._isSubmitted = usernameRect.isSubmitted || false;
                data._examInstrCorrect = examInstrCorrect;
                return data;
            });

            if (worksheetData.length === 0) {
                return;
            }

            setIsSavingReport(true);
            try {
            // Get report strings for translations (will be loaded later, but we need column mapping)
            // We'll load reportStrings after creating doc, but define mapping here
            // Exclude internal fields (_toleranceColor, _toolImageUrl, _isSubmitted) from column keys
            const englishColumns = Object.keys(worksheetData[ 0 ]).filter(key => !key.startsWith('_'));

            // Create rows with English column keys (we'll translate headers later)
            // Also store tolerance colors, tool image URLs and submission status for each row
            const rowToleranceColors: (string | null)[] = [];
            const rowToolImageUrls: (string | null)[] = [];
            const rowIsSubmitted: boolean[] = [];
            const rowExamInstrCorrect: (boolean | null)[] = [];
            const rows = worksheetData.map(row => {
                rowToleranceColors.push((row as any)._toleranceColor || null);
                rowToolImageUrls.push((row as any)._toolImageUrl || null);
                rowIsSubmitted.push((row as any)._isSubmitted || false);
                rowExamInstrCorrect.push((row as any)._examInstrCorrect ?? null);
                return englishColumns.map(columnKey => {
                    const value = (row as any)[ columnKey ];
                    return value != null ? String(value) : '';
                });
            });

            // Preload tool images as data URLs for PDF rendering
            const toolImageDataUrls: Map<string, string> = new Map();
            const uniqueToolImageUrls = Array.from(new Set(rowToolImageUrls.filter(url => url !== null))) as string[];

            logger.log(`[PDF Report] Preloading ${uniqueToolImageUrls.length} unique tool images`);

            for (const imageUrl of uniqueToolImageUrls) {
                try {
                    const dataUrl = await new Promise<string>((resolve, reject) => {
                        const img = new Image();
                        // Try with CORS first, but don't fail if CORS is not supported
                        img.crossOrigin = 'anonymous';

                        img.onload = () => {
                            try {
                                const canvas = document.createElement('canvas');
                                canvas.width = img.width;
                                canvas.height = img.height;
                                const ctx = canvas.getContext('2d');
                                if (ctx) {
                                    ctx.drawImage(img, 0, 0);
                                    const dataUrl = canvas.toDataURL('image/png');
                                    logger.log(`[PDF Report] Successfully loaded image: ${imageUrl}`);
                                    resolve(dataUrl);
                                } else {
                                    reject(new Error('Failed to get canvas context'));
                                }
                            } catch (error) {
                                reject(error);
                            }
                        };

                        img.onerror = (error) => {
                            logger.error(`[PDF Report] Failed to load image: ${imageUrl}`, error);
                            reject(new Error(`Failed to load image: ${imageUrl}`));
                        };

                        img.src = imageUrl;
                    });
                    toolImageDataUrls.set(imageUrl, dataUrl);
                } catch (error) {
                    logger.error(`[PDF Report] Failed to preload tool image ${imageUrl}:`, error);
                    // Continue even if one image fails
                }
            }

            logger.log(`[PDF Report] Preloaded ${toolImageDataUrls.size} tool images successfully`);

            const orientation = 'portrait';
            const doc = new jsPDF({
                orientation,
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            // Initialize custom fonts (Lato with Cyrillic support)
            initializeFonts(doc);

            // Set font based on report language
            setFontForLanguage(doc, reportLanguage);

            // Draw company logo in report header if available (cached once per viewer mount)
            if (companyLogoDataUrl) {
                try {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    // Fit logo into a bounding box while preserving aspect ratio
                    const maxLogoWidth = 60; // mm
                    const maxLogoHeight = 60; // mm
                    const marginRight = 14; // mm
                    const basePosY = 0; // mm from top

                    const imgProps = doc.getImageProperties(companyLogoDataUrl);
                    const imgWidth = imgProps.width || 1;
                    const imgHeight = imgProps.height || 1;
                    const aspect = imgWidth / imgHeight;

                    let drawWidth = maxLogoWidth;
                    let drawHeight = drawWidth / aspect;
                    if (drawHeight > maxLogoHeight) {
                        drawHeight = maxLogoHeight;
                        drawWidth = drawHeight * aspect;
                    }

                    const posX = pageWidth - drawWidth - marginRight;
                    const posY = basePosY + (maxLogoHeight - drawHeight) / 2;
                    doc.addImage(companyLogoDataUrl, 'PNG', posX, posY, drawWidth, drawHeight);
                } catch (logoError) {
                    logger.error('Error drawing company logo in report:', logoError);
                }
            }

            // Basic title and meta information (using report language)
            // reportStrings is already loaded at the beginning of handleSave
            logger.log('[PdfViewer] Generating PDF with report language:', reportLanguage);
            logger.log('[PdfViewer] Report strings loaded:', {
                title: reportStrings.PDF_REPORT_TITLE,
                order: reportStrings.PDF_REPORT_ORDER,
                detail: reportStrings.PDF_REPORT_DETAIL,
                revisionLabel: reportStrings.PDF_REPORT_REVISION_LABEL,
                measurer: reportStrings.PDF_REPORT_MEASURER,
                time: reportStrings.PDF_REPORT_TIME
            });
            const localeMap: { [ key in Language ]: string } = {
                'et': 'et-EE',
                'en': 'en-US',
                'ru': 'ru-RU'
            };
            const locale = localeMap[ reportLanguage ];

            // Map English column keys to translated headers
            const columnKeyToHeaderMap: { [ key: string ]: string } = {
                'M. Nr': reportStrings.PDF_REPORT_TABLE_M_NR,
                'Nominal Dim.': reportStrings.PDF_REPORT_TABLE_NOMINAL_DIM,
                'Tolerance': reportStrings.PDF_REPORT_TABLE_TOLERANCE,
                'Min': reportStrings.PDF_REPORT_TABLE_MIN,
                'Max': reportStrings.PDF_REPORT_TABLE_MAX,
                'Measured': reportStrings.PDF_REPORT_TABLE_MEASURED,
                'Tool': reportStrings.PDF_REPORT_TABLE_TOOL
            };

            // Translate column headers
            const columns = englishColumns.map(key => columnKeyToHeaderMap[ key ] || key);

            // Find Tool column index for drawing images
            const toolColumnIndex = englishColumns.findIndex(key => key === 'Tool');

            // Helper function to add text with Unicode support (for Cyrillic characters)
            // Font is already set based on language, so just add text
            const addText = (text: string, x: number, y: number) => {
                doc.text(text, x, y);
            };

            doc.setFontSize(16);
            addText(reportStrings.PDF_REPORT_TITLE, 14, 15);
            doc.setFontSize(12);

            const metaLines: string[] = [];
            metaLines.push('');
            metaLines.push(`${reportStrings.PDF_REPORT_ORDER} ${orderToUse || '-'}`);
            metaLines.push('');
            const revisionForReport = revision && revision.trim() ? revision.trim() : '-';
            metaLines.push(
                `${reportStrings.PDF_REPORT_DETAIL} ${detail || '-'} ` +
                    `( ${reportStrings.PDF_REPORT_REVISION_LABEL} ${revisionForReport} )`
            );
            metaLines.push('');
            if (pdfMeasuredDetailNumber) {
                const detailNumberPdfLabel = role === 'editor'
                    ? reportStrings.PDF_REPORT_MEASURABLE_DETAIL_NUMBER
                    : reportStrings.PDF_REPORT_MEASURED_DETAIL_NUMBER;
                metaLines.push(`${detailNumberPdfLabel} ${pdfMeasuredDetailNumber}`);
            }

            // Add empty lines before 'Measurer'
            metaLines.push('');
            metaLines.push('');

            let metaY = 22;
            metaLines.forEach(line => {
                // If line is empty, add spacing
                if (line === '') {
                    metaY += 5;
                } else {
                    addText(line, 14, metaY);
                    metaY += 5;
                }
            });

            // Add 'Measurer' and 'Time' on the same line: Measurer on left, Time on right
            metaY += 5;
            // Ensure font is set before adding text (should already be set, but ensure it)
            setFontForLanguage(doc, reportLanguage);
            const measurerText = `${reportStrings.PDF_REPORT_MEASURER} ${engineerName || '-'}`;
            const now = new Date();
            const formatDateTimeWithLeadingZeros = (d: Date) => {
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                const hh = String(d.getHours()).padStart(2, '0');
                const min = String(d.getMinutes()).padStart(2, '0');
                const ss = String(d.getSeconds()).padStart(2, '0');
                return `${dd}.${mm}.${yyyy} ${hh}:${min}:${ss}`;
            };
            const timeText = `${reportStrings.PDF_REPORT_TIME} ${formatDateTimeWithLeadingZeros(now)}`;
            const pageWidth = doc.internal.pageSize.getWidth();
            const marginRight = 14; // mm

            // Calculate text width and position for right alignment
            const timeTextWidth = doc.getTextWidth(timeText);
            const timeX = pageWidth - marginRight - timeTextWidth;

            addText(measurerText, 14, metaY);
            addText(timeText, timeX, metaY);
            metaY += 5;

            const startTableY = metaY + 3;

            // Define column widths for user report (in mm)
            // Map translated column headers to their widths
            const columnWidthMap: { [ key: string ]: number } = {
                [ reportStrings.PDF_REPORT_TABLE_M_NR ]: 21,
                [ reportStrings.PDF_REPORT_TABLE_NOMINAL_DIM ]: 24,
                [ reportStrings.PDF_REPORT_TABLE_TOLERANCE ]: 24,
                [ reportStrings.PDF_REPORT_TABLE_MIN ]: 20,
                [ reportStrings.PDF_REPORT_TABLE_MAX ]: 20,
                [ reportStrings.PDF_REPORT_TABLE_MEASURED ]: 24,
                [ reportStrings.PDF_REPORT_TABLE_TOOL ]: 15
            };

            // Create columnStyles object using column indices
            const columnStyles: { [ key: number ]: { cellWidth: number, fontSize?: number, halign?: string, valign?: string } } = {};
            columns.forEach((col, index) => {
                let halign = 'center'; // Default alignment

                // Set alignment for specific columns
                if (col === reportStrings.PDF_REPORT_TABLE_NOMINAL_DIM) {
                    halign = 'center'; // Nominal aligned to the right
                } else if (col === reportStrings.PDF_REPORT_TABLE_MIN || col === reportStrings.PDF_REPORT_TABLE_MAX) {
                    halign = 'center'; // Min and Max aligned to the right
                } else if (col === reportStrings.PDF_REPORT_TABLE_MEASURED) {
                    halign = 'center'; // Measured (Reaalne) aligned to the left
                }

                columnStyles[ index ] = {
                    cellWidth: columnWidthMap[ col ] || 25,
                    halign: halign,
                    valign: 'middle' // Center numbers vertically in cells
                };
                // Set font size for Tolerance column
                if (col === reportStrings.PDF_REPORT_TABLE_TOLERANCE) {
                    columnStyles[ index ].fontSize = 9;
                }
            });

            (doc as any).autoTable({
                head: [ columns ],
                body: rows,
                startY: startTableY,
                styles: {
                    fontSize: 11,
                    cellPadding: 3,
                    font: FONT_NAMES.LATO,
                    fontStyle: 'normal',
                    halign: 'center',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [ 230, 230, 230 ],
                    textColor: 0,
                    fontSize: 11,
                    cellPadding: 3,
                    font: FONT_NAMES.LATO,
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: columnStyles,
                // Format Tolerance column with labels
                didParseCell: (data: any) => {
                    if (data.section === 'body') {
                        const columnIndex = data.column.index;
                        const columnKey = columns[ columnIndex ];
                        const rowIndex = data.row.index;

                        // Guard against out-of-bounds rowIndex (can happen when autoTable splits table across pages)
                        if (rowIndex >= rows.length) {
                            return;
                        }

                        if (columnKey === reportStrings.PDF_REPORT_TABLE_TOOL && !examMode) {
                            data.cell.text = [ '' ];
                        }
                        // Format Tolerance column with smaller font - show both upper and lower values
                        if (columnKey === reportStrings.PDF_REPORT_TABLE_TOLERANCE) {
                            const raw = rows[ rowIndex ][ columnIndex ];
                            const value = typeof raw === 'string' ? raw : '';
                            // Always format tolerance column
                            if (value) {
                                // Split by newline to show upper at top and lower at bottom
                                const lines = value.split('\n');
                                data.cell.text = lines;
                                // Set font size for Tolerance column
                                data.cell.styles = data.cell.styles || {};
                                data.cell.styles.fontSize = 9;
                            }
                        }
                        if (columnKey === reportStrings.PDF_REPORT_TABLE_MEASURED) {
                            const toleranceColor = rowToleranceColors[ rowIndex ];
                            const isSubmitted = rowIsSubmitted[ rowIndex ];
                            if (toleranceColor && isSubmitted) {
                                data.cell.styles = data.cell.styles || {};
                                const rgbArray = hexToLightRgbArray(toleranceColor);
                                data.cell.styles.fillColor = rgbArray;
                                data.cell.styles.lineColor = [ 200, 200, 200 ];
                                data.cell.styles.lineWidth = 0.2;
                            }
                        }
                        if (columnKey === reportStrings.PDF_REPORT_TABLE_TOOL) {
                            const instrCorrect = rowExamInstrCorrect[ rowIndex ];
                            if (instrCorrect === false) {
                                data.cell.styles = data.cell.styles || {};
                                data.cell.styles.fillColor = hexToLightRgbArray(TOLERANCE_COLORS.OUT);
                                data.cell.styles.lineColor = [ 200, 200, 200 ];
                                data.cell.styles.lineWidth = 0.2;
                            } else if (instrCorrect === true) {
                                data.cell.styles = data.cell.styles || {};
                                data.cell.styles.fillColor = hexToLightRgbArray(TOLERANCE_COLORS.IN);
                                data.cell.styles.lineColor = [ 200, 200, 200 ];
                                data.cell.styles.lineWidth = 0.2;
                            }
                        }
                    }
                },
                didDrawCell: (data: any) => {
                    if (data.section === 'body') {
                        const columnIndex = data.column.index;
                        const rowIndex = data.row.index;

                        // Guard against out-of-bounds rowIndex (can happen when autoTable splits table across pages)
                        const rowData = rows[ rowIndex ];
                        if (!rowData) {
                            return;
                        }

                        if (!examMode && toolColumnIndex !== -1 && columnIndex === toolColumnIndex) {
                            const cell = data.cell;
                            const toolImageUrl = rowToolImageUrls[ rowIndex ];
                            const toolNumber = rowData[ columnIndex ]; // Get tool number from row data

                            const cellPadding = 0.5; // mm (reduced for bigger images)
                            const numberPaddingLeft = 5; // mm - padding from left edge for number
                            const gapBetween = 5; // mm - gap between number and image
                            const availableHeight = cell.height - cellPadding * 2;
                            const centerX = cell.x + cell.width / 2;
                            const centerY = cell.y + cell.height / 2;

                            // Calculate number text width first (needed for image size calculation)
                            let numberWidth = 0;
                            if (toolNumber) {
                                doc.setFontSize(11); // Same as other table cells
                                doc.setFont(FONT_NAMES.LATO, 'normal');
                                numberWidth = doc.getTextWidth(toolNumber);
                            }

                            // Calculate image size first to determine total width
                            let imageWidth = 0;
                            let imageHeight = 0;
                            let imageDataUrl: string | null = null;

                            if (toolImageUrl) {
                                imageDataUrl = toolImageDataUrls.get(toolImageUrl) || null;
                                if (imageDataUrl) {
                                    try {
                                        // Calculate available width for image (cell width minus number width, padding and gap)
                                        const numberWidthEstimate = toolNumber ? (numberWidth + gapBetween) : 0;
                                        const availableWidth = cell.width - numberPaddingLeft - numberWidthEstimate - cellPadding;

                                        // Use maximum available space - consider both width and height
                                        const maxHeight = availableHeight * 0.995;
                                        const maxWidth = availableWidth * 0.95;

                                        const imgProps = doc.getImageProperties(imageDataUrl);
                                        const imgWidth = imgProps.width || 1;
                                        const imgHeight = imgProps.height || 1;
                                        const aspect = imgWidth / imgHeight;

                                        // Calculate size based on aspect ratio to use maximum available space
                                        imageHeight = maxHeight;
                                        imageWidth = imageHeight * aspect;

                                        // If image is too wide, scale based on width instead
                                        if (imageWidth > maxWidth) {
                                            imageWidth = maxWidth;
                                            imageHeight = imageWidth / aspect;
                                        }

                                        // Ensure we don't exceed height limit
                                        if (imageHeight > maxHeight) {
                                            imageHeight = maxHeight;
                                            imageWidth = imageHeight * aspect;
                                        }
                                    } catch (error) {
                                        logger.error(`Failed to get image properties:`, error);
                                    }
                                }
                            }

                            // Draw tool number text
                            if (toolNumber) {
                                doc.setFontSize(11); // Same as other table cells
                                doc.setFont(FONT_NAMES.LATO, 'normal'); // Ensure number is not bold
                                doc.setTextColor(0, 0, 0);
                                
                                // For user role, center the number; for other roles, position on the left
                                if (effectiveRole === 'user') {
                                    // Center the number in the cell
                                    const textX = cell.x + cell.width / 2;
                                    doc.text(toolNumber, textX, centerY, { align: 'center', baseline: 'middle' });
                                } else {
                                    // Position on the left side with padding
                                    const textX = cell.x + numberPaddingLeft + numberWidth / 2;
                                    doc.text(toolNumber, textX, centerY, { align: 'center', baseline: 'middle' });
                                }
                            }

                            // Draw tool image next to number on the left side (only for non-user roles)
                            if (effectiveRole !== 'user' && toolImageUrl && imageDataUrl && imageWidth > 0 && imageHeight > 0) {
                                try {
                                    // Position image next to number on the left side
                                    const imageX = cell.x + numberPaddingLeft + numberWidth + gapBetween;
                                    const imageY = centerY - imageHeight / 2;

                                    doc.addImage(imageDataUrl, 'PNG', imageX, imageY, imageWidth, imageHeight);
                                } catch (error) {
                                    logger.error(`Failed to draw tool image in PDF:`, error);
                                }
                            } else if (effectiveRole !== 'user' && toolImageUrl) {
                                logger.warn(`Image data URL not found for tool image URL: ${toolImageUrl}, row: ${rowIndex}`);
                            }
                        }
                    }
                }
            });

            // For user role, collect only used tools from worksheetData
            let toolsForTable = companyToolsForLegend;
            if (effectiveRole === 'user') {
                // Extract unique tool numbers from worksheetData
                const usedToolNumbers = new Set<number>();
                worksheetData.forEach(row => {
                    const toolNumber = (row as any).Tool;
                    if (toolNumber && toolNumber !== '') {
                        const num = parseInt(String(toolNumber), 10);
                        if (!isNaN(num)) {
                            usedToolNumbers.add(num);
                        }
                    }
                });
                
                // Filter companyToolsForLegend to include only used tools
                toolsForTable = companyToolsForLegend.filter(tool => usedToolNumbers.has(tool.number));
                logger.log(`[PDF Report] Filtered tools table: ${toolsForTable.length} used tools out of ${companyToolsForLegend.length} total`);
            }

            // Preload all company tool images for tools table
            const allCompanyToolImageUrls = toolsForTable
                .map(tool => tool.imageUrl)
                .filter(url => url !== null && url !== '') as string[];
            const uniqueCompanyToolImageUrls = Array.from(new Set(allCompanyToolImageUrls));

            logger.log(`[PDF Report] Preloading ${uniqueCompanyToolImageUrls.length} company tool images for tools table`);

            for (const imageUrl of uniqueCompanyToolImageUrls) {
                // Skip if already loaded
                if (toolImageDataUrls.has(imageUrl)) {
                    continue;
                }

                try {
                    const dataUrl = await new Promise<string>((resolve, reject) => {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';

                        img.onload = () => {
                            try {
                                const canvas = document.createElement('canvas');
                                canvas.width = img.width;
                                canvas.height = img.height;
                                const ctx = canvas.getContext('2d');
                                if (ctx) {
                                    ctx.drawImage(img, 0, 0);
                                    const dataUrl = canvas.toDataURL('image/png');
                                    logger.log(`[PDF Report] Successfully loaded company tool image: ${imageUrl}`);
                                    resolve(dataUrl);
                                } else {
                                    reject(new Error('Failed to get canvas context'));
                                }
                            } catch (error) {
                                reject(error);
                            }
                        };

                        img.onerror = (error) => {
                            logger.error(`[PDF Report] Failed to load company tool image: ${imageUrl}`, error);
                            reject(new Error(`Failed to load image: ${imageUrl}`));
                        };

                        img.src = imageUrl;
                    });
                    toolImageDataUrls.set(imageUrl, dataUrl);
                } catch (error) {
                    logger.error(`[PDF Report] Failed to preload company tool image ${imageUrl}:`, error);
                    // Continue even if one image fails
                }
            }

            logger.log(`[PDF Report] Preloaded ${toolImageDataUrls.size} total tool images (including company tools)`);

            // Drawing snapshot after measurement report, before tools list
            if (file && rects.length > 0) {
                try {
                    const markupPng = await buildEditorMarkupSnapshotPng({
                        file,
                        pageNumber,
                        stageWidth: stageSize.width,
                        stageHeight: stageSize.height,
                        rects,
                        getToolColorByNumber
                    });
                    if (markupPng && markupPng.length > 0) {
                        const markupDataUrl = uint8ToPngDataUrl(markupPng);
                        const snapProps = doc.getImageProperties(markupDataUrl);
                        const iw = snapProps.width;
                        const ih = snapProps.height;
                        // Match page orientation to snapshot (same as embedExcelInPdf / editor settings PDF).
                        doc.addPage('a4', iw > ih ? 'l' : 'p');
                        const marginMm = 10;
                        const pageWmm = doc.internal.pageSize.getWidth();
                        const pageHmm = doc.internal.pageSize.getHeight();
                        const innerW = pageWmm - 2 * marginMm;
                        const innerH = pageHmm - 2 * marginMm;
                        const k = Math.min(innerW / iw, innerH / ih);
                        const dw = iw * k;
                        const dh = ih * k;
                        const imgX = marginMm + (innerW - dw) / 2;
                        const imgY = marginMm + (innerH - dh) / 2;
                        doc.addImage(markupDataUrl, 'PNG', imgX, imgY, dw, dh);
                    }
                } catch (markupPageErr) {
                    logger.error('[PdfViewer] User report markup page failed:', markupPageErr);
                }
            }

            // New page for company tools table (portrait; snapshot page above may be landscape)
            doc.addPage('a4', 'p');

            // Add title for company tools table
            doc.setFontSize(16);
            setFontForLanguage(doc, reportLanguage);
            const toolsTableTitle = reportStrings.PDF_REPORT_TOOLS_TABLE_TITLE;
            addText(toolsTableTitle, 14, 15);

            // Helper function to truncate tool name if too long
            const truncateToolName = (name: string, maxLength: number = 20): string => {
                if (!name) return '';
                if (name.length <= maxLength) return name;
                return name.substring(0, maxLength - 3) + '...';
            };

            // Prepare data for company tools table with Picture column
            const toolsTableColumns = [
                reportStrings.PDF_REPORT_TOOLS_TABLE_NUMBER,
                reportStrings.PDF_REPORT_TOOLS_TABLE_NAME,
                reportStrings.PDF_REPORT_TOOLS_TABLE_PICTURE,
                reportStrings.PDF_REPORT_TOOLS_TABLE_PRECISION_CLASS
            ];

            // Store tool image URLs for each row
            const toolsTableToolImageUrls: (string | null)[] = [];
            const toolsTableRows = toolsForTable.map(tool => {
                toolsTableToolImageUrls.push(tool.imageUrl || null);
                return [
                    String(tool.number),
                    truncateToolName(tool.name || '', 20), // Truncate long names
                    String(tool.number), // Tool column - will be hidden and drawn with image
                    tool.precisionClass || '-' // Precision class
                ];
            });

            // Determine if we should use two columns (if more than 12 tools)
            const useTwoColumns = toolsForTable.length > 12;
            const toolsPerColumn = useTwoColumns ? Math.ceil(toolsForTable.length / 2) : toolsForTable.length;

            // Find Picture column index for drawing images
            const toolsTableToolColumnIndex = toolsTableColumns.findIndex(col => col === reportStrings.PDF_REPORT_TOOLS_TABLE_PICTURE);

            // Define column widths for tools table (in mm)
            // Adjust widths for two-column layout if needed
            const toolsPageWidth = doc.internal.pageSize.getWidth();
            const toolsMarginLeft = 14;
            const toolsMarginRight = 14;
            const toolsAvailableWidth = toolsPageWidth - toolsMarginLeft - toolsMarginRight;
            const gapBetweenTables = 10; // mm gap between two tables

            let toolsColumnWidthMap: { [ key: string ]: number };
            let tableWidth: number;

            if (useTwoColumns) {
                // Two columns layout: each table takes ~45% of available width
                tableWidth = (toolsAvailableWidth - gapBetweenTables) / 2;

                toolsColumnWidthMap = {
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_NUMBER ]: 15,
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_NAME ]: 25,
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_PRECISION_CLASS ]: 20,
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_PICTURE ]: 40
                };
            } else {
                // Single column layout
                tableWidth = toolsAvailableWidth;
                toolsColumnWidthMap = {
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_NUMBER ]: 20,
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_NAME ]: 40,
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_PRECISION_CLASS ]: 35,
                    [ reportStrings.PDF_REPORT_TOOLS_TABLE_PICTURE ]: 45
                };
            }

            // Create columnStyles for tools table
            const toolsColumnStyles: { [ key: number ]: { cellWidth: number, halign?: string, valign?: string } } = {};
            toolsTableColumns.forEach((col, index) => {
                let halign = 'center';
                if (col === reportStrings.PDF_REPORT_TOOLS_TABLE_NAME) {
                    halign = 'center';
                } else if (col === reportStrings.PDF_REPORT_TOOLS_TABLE_PRECISION_CLASS) {
                    halign = 'center';
                }
                toolsColumnStyles[ index ] = {
                    cellWidth: toolsColumnWidthMap[ col ] || 95,
                    halign: halign,
                    valign: 'middle'
                };
            });

            // Function to draw a tools table
            const drawToolsTable = (rows: any[][], imageUrls: (string | null)[], startY: number, startX: number, tableW: number) => {
                (doc as any).autoTable({
                    head: [ toolsTableColumns ],
                    body: rows,
                    startY: startY,
                    startX: startX,
                    tableWidth: tableW,
                    styles: {
                        fontSize: 11,
                        cellPadding: 3,
                        font: FONT_NAMES.LATO,
                        fontStyle: 'normal',
                        halign: 'center',
                        valign: 'middle',
                        minCellHeight: 15 // Increased row height for bigger images
                    },
                    headStyles: {
                        fillColor: [ 230, 230, 230 ],
                        textColor: 0,
                        fontSize: 11,
                        cellPadding: 3,
                        font: FONT_NAMES.LATO,
                        fontStyle: 'bold',
                        halign: 'center',
                        valign: 'middle',
                        minCellHeight: 10
                    },
                    columnStyles: toolsColumnStyles,
                    didParseCell: (data: any) => {
                        if (data.section === 'body') {
                            const columnIndex = data.column.index;
                            const columnKey = toolsTableColumns[ columnIndex ];

                            // Hide text in Picture column - we will draw image in didDrawCell
                            if (columnKey === reportStrings.PDF_REPORT_TOOLS_TABLE_PICTURE) {
                                data.cell.text = [ '' ];
                            }
                        }
                    },
                    didDrawCell: (data: any) => {
                        if (data.section === 'body') {
                            const columnIndex = data.column.index;
                            const rowIndex = data.row.index;

                            // Draw tool image in Picture column (without number) - using same style as first table
                            if (toolsTableToolColumnIndex !== -1 && columnIndex === toolsTableToolColumnIndex) {
                                const cell = data.cell;
                                const toolImageUrl = imageUrls[ rowIndex ];

                                const cellPadding = 0.5; // mm (same as first table)
                                const availableHeight = cell.height - cellPadding * 2;
                                const centerX = cell.x + cell.width / 2;
                                const centerY = cell.y + cell.height / 2;

                                // Calculate image size - use same calculation as first table
                                let imageWidth = 0;
                                let imageHeight = 0;
                                let imageDataUrl: string | null = null;

                                if (toolImageUrl) {
                                    imageDataUrl = toolImageDataUrls.get(toolImageUrl) || null;
                                    if (imageDataUrl) {
                                        try {
                                            // Use same calculation as first table (0.995 for height, 0.95 for width)
                                            const availableWidth = cell.width - cellPadding * 2;
                                            const maxHeight = availableHeight * 0.995;
                                            const maxWidth = availableWidth * 0.95;

                                            const imgProps = doc.getImageProperties(imageDataUrl);
                                            const imgWidth = imgProps.width || 1;
                                            const imgHeight = imgProps.height || 1;
                                            const aspect = imgWidth / imgHeight;

                                            // Calculate size based on aspect ratio to use maximum available space
                                            imageHeight = maxHeight;
                                            imageWidth = imageHeight * aspect;

                                            // If image is too wide, scale based on width instead
                                            if (imageWidth > maxWidth) {
                                                imageWidth = maxWidth;
                                                imageHeight = imageWidth / aspect;
                                            }

                                            // Ensure we don't exceed height limit
                                            if (imageHeight > maxHeight) {
                                                imageHeight = maxHeight;
                                                imageWidth = imageHeight * aspect;
                                            }
                                        } catch (error) {
                                            logger.error(`Failed to get image properties for company tool:`, error);
                                        }
                                    }
                                }

                                // Draw tool image centered in the cell (same style as first table)
                                if (toolImageUrl && imageDataUrl && imageWidth > 0 && imageHeight > 0) {
                                    try {
                                        // Center image horizontally and vertically in cell
                                        const imageX = centerX - imageWidth / 2;
                                        const imageY = centerY - imageHeight / 2;

                                        doc.addImage(imageDataUrl, 'PNG', imageX, imageY, imageWidth, imageHeight);
                                    } catch (error) {
                                        logger.error(`Failed to draw company tool image in PDF:`, error);
                                    }
                                } else if (toolImageUrl) {
                                    logger.warn(`Image data URL not found for company tool image URL: ${toolImageUrl}, row: ${rowIndex}`);
                                }
                            }
                        }
                    }
                });
            };

            // Draw tools table(s)
            if (useTwoColumns) {
                // Split tools into two columns
                const firstColumnRows = toolsTableRows.slice(0, toolsPerColumn);
                const firstColumnImageUrls = toolsTableToolImageUrls.slice(0, toolsPerColumn);
                const secondColumnRows = toolsTableRows.slice(toolsPerColumn);
                const secondColumnImageUrls = toolsTableToolImageUrls.slice(toolsPerColumn);

                // Draw first column
                drawToolsTable(firstColumnRows, firstColumnImageUrls, 22, toolsMarginLeft, tableWidth);

                // Draw second column
                const secondTableStartX = toolsMarginLeft + tableWidth + gapBetweenTables;
                drawToolsTable(secondColumnRows, secondColumnImageUrls, 22, secondTableStartX, tableWidth);
            } else {
                // Single column
                drawToolsTable(toolsTableRows, toolsTableToolImageUrls, 22, toolsMarginLeft, tableWidth);
            }

            const sanitizeForFilename = (s: string) => (s || '').replace(/[^a-zA-Z0-9а-яА-ЯёЁ\-_]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'export';
            const revPart = revision && revision.trim() ? `_rev.${sanitizeForFilename(revision)}` : '';
            const reportNumberSuffixPart =
                pdfMeasuredDetailNumber
                    ? (role === 'editor' ? `_mdn${pdfMeasuredDetailNumber}` : `_rnum${pdfMeasuredDetailNumber}`)
                    : '';
            const reportBaseName = `${orderToUse || strings.UNKNOWN}_${detail || strings.UNKNOWN}${revPart}${reportNumberSuffixPart}.pdf`;
            const fileName = role === 'user' ? `R_${reportBaseName}` : reportBaseName;
            let pdfBlob = doc.output('blob') as Blob;

            // Always embed the measurement Excel into the PDF (works for both
            // editor and operator). The same data is also sent as JSON to the
            // local-server below for the report search engine.
            {
                const measurementReportCol = reportStrings.MEASURED_DETAIL_NUMBER_EXPORT;
                const excelSheetRows = worksheetData.map((row: Record<string, unknown>) => {
                    const obj: Record<string, string> = {};
                    for (const enKey of englishColumns) {
                        const header = columnKeyToHeaderMap[ enKey ] || enKey;
                        const val = row[ enKey ];
                        obj[ header ] = val != null ? String(val) : '';
                    }
                    if (role === 'editor') {
                        obj[ measurementReportCol ] = effectiveCounter;
                    }
                    return obj;
                });
                const excelWorkbook = XLSX.utils.book_new();
                const excelWorksheet = XLSX.utils.json_to_sheet(excelSheetRows);
                XLSX.utils.book_append_sheet(excelWorkbook, excelWorksheet, strings.USER_REPORT_DATA_SHEET);
                const excelBytes = new Uint8Array(XLSX.write(excelWorkbook, { bookType: 'xlsx', type: 'array' }));
                pdfBlob = await embedExcelInPdfBlob(pdfBlob, excelBytes);
            }

            // Local-server hard_fail upload: when configured, the PDF MUST be
            // accepted by the local-server before the operator gets the file.
            // No queueing, no offline fallback — by explicit business choice.
            if (localServerActive) {
                try {
                    // Extract structured measurement values for the searchable
                    // report history. The Excel is already embedded inside the
                    // PDF above, so we don't ship a separate xlsx file.
                    const measurementValues: MeasurementValuePayload[] = worksheetData
                        .map((row: Record<string, unknown>) => {
                            const nrRaw = row[ 'M. Nr' ];
                            const nr = parseInt(typeof nrRaw === 'string' ? nrRaw : String(nrRaw ?? ''), 10);
                            if (!Number.isFinite(nr) || nr <= 0) return null;
                            const colorRaw = (row as { _toleranceColor?: string | null })._toleranceColor || null;
                            const inSpec = colorRaw === TOLERANCE_COLORS.IN || colorRaw === TOLERANCE_COLORS.CORRECT
                                ? true
                                : (colorRaw === TOLERANCE_COLORS.OUT || colorRaw === TOLERANCE_COLORS.WARNING ? false : null);
                            const measured = row[ 'Measured' ];
                            return {
                                measurementNr: nr,
                                value: typeof measured === 'string' || typeof measured === 'number' ? measured : null,
                                inSpec,
                                notes: null
                            } as MeasurementValuePayload;
                        })
                        .filter((v): v is MeasurementValuePayload => v !== null);

                    await uploadOperatorReport(pdfBlob, {
                        detailNo: (detail || '').trim() || 'unknown',
                        revisionId: revisionId ?? null,
                        revisionLabel: revision || null,
                        orderNo: orderToUse || null,
                        measurementNumber: pdfMeasuredDetailNumber || null,
                        fileName,
                        values: measurementValues,
                        operatorRole: role === 'editor' ? 'editor' : 'user'
                    });

                    logger.log('[PdfViewer] Operator report uploaded to local server', {
                        detail, revision, fileName, measurements: measurementValues.length
                    });
                } catch (uploadErr: unknown) {
                    const reason = uploadErr instanceof LocalServerError
                        ? uploadErr.message
                        : (uploadErr instanceof Error ? uploadErr.message : 'Unknown error');
                    logger.error('[PdfViewer] Failed to upload operator report to local server', { uploadErr });
                    setSettingsFileError('');
                    setLocalServerSaveErrorModal({
                        title: strings.LOCAL_SERVER_SAVE_FAILED_MODAL_TITLE,
                        message: reason
                    });
                    return;
                }
            }

            if (cloudDbActive) {
                try {
                    const measurementValues: MeasurementValuePayload[] = worksheetData
                        .map((row: Record<string, unknown>) => {
                            const nrRaw = row[ 'M. Nr' ];
                            const nr = parseInt(typeof nrRaw === 'string' ? nrRaw : String(nrRaw ?? ''), 10);
                            if (!Number.isFinite(nr) || nr <= 0) return null;
                            const colorRaw = (row as { _toleranceColor?: string | null })._toleranceColor || null;
                            const inSpec = colorRaw === TOLERANCE_COLORS.IN || colorRaw === TOLERANCE_COLORS.CORRECT
                                ? true
                                : (colorRaw === TOLERANCE_COLORS.OUT || colorRaw === TOLERANCE_COLORS.WARNING ? false : null);
                            const measured = row[ 'Measured' ];
                            return {
                                measurementNr: nr,
                                value: typeof measured === 'string' || typeof measured === 'number' ? measured : null,
                                inSpec,
                                notes: null
                            } as MeasurementValuePayload;
                        })
                        .filter((v): v is MeasurementValuePayload => v !== null);

                    await uploadReportToCloud(pdfBlob, {
                        detailNo: (detail || '').trim() || 'unknown',
                        revisionId: revisionId ?? null,
                        revisionLabel: revision || null,
                        orderNo: orderToUse || null,
                        measurementNumber: pdfMeasuredDetailNumber || null,
                        fileName,
                        values: measurementValues,
                        operatorRole: role === 'editor' ? 'editor' : 'user'
                    });

                    logger.log('[PdfViewer] Operator report uploaded to Cloud DB', {
                        detail, revision, fileName, measurements: measurementValues.length
                    });
                } catch (uploadErr: unknown) {
                    const reason = uploadErr instanceof Error ? uploadErr.message : 'Unknown error';
                    logger.error('[PdfViewer] Failed to upload operator report to Cloud DB', { uploadErr });
                    setSettingsFileError('');
                    setLocalServerSaveErrorModal({
                        title: strings.LOCAL_SERVER_SAVE_FAILED_MODAL_TITLE,
                        message: reason
                    });
                    return;
                }
            }

            // Company local server / cloud DB: store only remotely — no local folder write or browser download.
            let savedToFolder = false;
            if (!localServerActive && !cloudDbActive) {
                savedToFolder = await saveBlobToChosenFolder(pdfBlob, fileName);
                if (!savedToFolder) {
                    const url = URL.createObjectURL(pdfBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    a.click();
                    URL.revokeObjectURL(url);
                } else if (downloadDirHandleRef.current) {
                    await saveDownloadDirHandle(downloadDirHandleRef.current, resolveReportFolderPersistKey());
                }
            }
            const s = strings as Record<string, string>;
            const remoteActive = localServerActive || cloudDbActive;
            const folderName = remoteActive
                ? ''
                : (savedToFolder ? (folderDisplayPath || downloadPathDisplayName || '') : (s.SAVE_SUCCESS_DEFAULT_FOLDER || ''));
            if (effectiveRole === 'user' && file?.name) {
                const nextSaveNumber = getPdfReportSaveCount(file.name) + 1;
                setPdfReportSaveCount(file.name, nextSaveNumber);
            }
            const pdfBlobUrl = URL.createObjectURL(pdfBlob);
            const successTitle = localServerActive
                ? (s.SAVE_SUCCESS_TITLE_LOCAL_SERVER || s.SAVE_SUCCESS_TITLE || '')
                : cloudDbActive
                    ? (s.SAVE_SUCCESS_TITLE_CLOUD_DB || s.SAVE_SUCCESS_TITLE || '')
                    : (s.SAVE_SUCCESS_TITLE || '');
            setSaveSuccessModal({
                title: successTitle,
                fileName,
                folderName,
                hideFolder: remoteActive,
                pdfBlobUrl,
                onClose: role === 'editor' && effectiveRole === 'user'
                    ? () => setEditorViewMode('editor')
                    : () => onBack(role === 'user' ? { preserveOperatorHomeInputs: true } : undefined)
            });
            } finally {
                setIsSavingReport(false);
            }

        } else if (role === 'editor') {
            if (!file || rects.length === 0) return;
            if (!editorExcelFile && (!revision || !revision.trim())) {
                setSettingsFileError(strings.REVISION_REQUIRED);
                return;
            }
            setSettingsFileError('');

            let settingsLocalServerOnly = false;
            let settingsCloudDbOnly = false;
            try {
                const mode = await getStorageMode();
                settingsLocalServerOnly = mode === 'localServer';
                settingsCloudDbOnly = mode === 'cloudDb';
            } catch {
                settingsLocalServerOnly = false;
                settingsCloudDbOnly = false;
            }

            let folderDisplayPath = '';
            if (!settingsLocalServerOnly && !settingsCloudDbOnly) {
                const { success: hasSettingsPdfFolder, displayPath } = await ensureSettingsPdfFolderHandle();
                folderDisplayPath = displayPath;
                if (!hasSettingsPdfFolder) {
                    logger.warn('[PdfViewer] No settings PDF folder available, will use browser default');
                }
            }

            try {
                setIsSavingReport(true);
                const localeMap: { [ key in Language ]: string } = {
                    'et': 'et-EE',
                    'en': 'en-US',
                    'ru': 'ru-RU'
                };
                const locale = localeMap[ reportLanguage ];
                const formatTolerance = (val: string | undefined) => val || '';
                const worksheetData = rects.map((rect, index) => {
                    const tool = companyToolsForLegend.find(t => t.number === rect.enteredNumber);
                    const isNonMeasurement = tool?.toolWithoutMeasurement || false;
                    const data: any = {
                        'M. Nr': String(index + 1),
                        X: String(Math.round(rect.x)),
                        Y: String(Math.round(rect.y)),
                        Width: String(Math.round(Math.abs(rect.width))),
                        Height: String(Math.round(Math.abs(rect.height))),
                        Tool: rect.enteredNumber != null ? String(rect.enteredNumber) : '',
                        DetailNumber: detail ? String(detail) : '',
                        Upper: formatTolerance(rect.tolerancePlus),
                        Lower: formatTolerance(rect.toleranceMinus),
                        Type: rect.toleranceType || '',
                        'Nominal Dim.': rect.nominalDimension || '',
                        'NonMeasurement': isNonMeasurement ? 'Yes' : 'No',
                        Time: new Date().toLocaleString(locale),
                    };
                    if (rect.number === rects[ 0 ]?.number && pdfSize.width > 0 && pdfSize.height > 0) {
                        data[ 'Ref. Width' ] = String(pdfSize.width);
                        data[ 'Ref. Height' ] = String(pdfSize.height);
                    }
                    if (rect.number === rects[ 0 ]?.number && revision && revision.trim()) {
                        data[ 'Revision' ] = revision.trim();
                    }
                    if (rect.number === rects[ 0 ]?.number) {
                        data[ 'SequenceMeasurement' ] = sequenceMeasurement ? 'Yes' : 'No';
                        data[ 'ExamMode' ] = examMode ? 'Yes' : 'No';
                        data[ 'SaveFolder' ] = downloadPathDisplayName || '';
                        data[ 'SettingsPdfFolder' ] = settingsPdfPathDisplayName || '';
                    }
                    return data;
                });
                const worksheet = XLSX.utils.json_to_sheet(worksheetData);
                worksheet[ '!cols' ] = [
                    { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 5 }, { wch: 14 },
                    { wch: 6 }, { wch: 6 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 10 }, { wch: 10 },
                    { wch: 18 }, { wch: 25 }, { wch: 22 },
                ];
                if (worksheet[ '!ref' ]) {
                    const range = XLSX.utils.decode_range(worksheet[ '!ref' ]);
                    for (let row = range.s.r; row <= range.e.r; row += 1) {
                        for (let col = range.s.c; col <= range.e.c; col += 1) {
                            const addr = XLSX.utils.encode_cell({ r: row, c: col });
                            const cell = worksheet[ addr ];
                            if (cell) {
                                cell.s = cell.s || {};
                                cell.s.alignment = { horizontal: 'left' };
                            }
                        }
                    }
                }
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, strings.SETUP_DATA_SHEET);
                const excelBytes = new Uint8Array(XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }));
                let markupSnapshotPng: Uint8Array | null = null;
                try {
                    markupSnapshotPng = await buildEditorMarkupSnapshotPng({
                        file,
                        pageNumber,
                        stageWidth: stageSize.width,
                        stageHeight: stageSize.height,
                        rects,
                        getToolColorByNumber
                    });
                } catch (snapshotErr) {
                    logger.error('[PdfViewer] Editor markup snapshot failed:', snapshotErr);
                }
                const blob = await embedExcelInPdf(file, excelBytes, markupSnapshotPng ?? undefined);

                let publishedToServer = false;
                if (settingsLocalServerOnly && detail && detail.trim() && revision && revision.trim()) {
                    try {
                        const payload: SettingsPayload = {
                            revision: revision.trim(),
                            mdn: null,
                            sequenceMeasurement,
                            examMode,
                            refDimensions: { width: pdfSize.width, height: pdfSize.height },
                            rects: rects.map((rect, index) => {
                                const tool = companyToolsForLegend.find(t => t.number === rect.enteredNumber);
                                return {
                                    measurementNr: index + 1,
                                    x: Math.round(rect.x),
                                    y: Math.round(rect.y),
                                    width: Math.round(Math.abs(rect.width)),
                                    height: Math.round(Math.abs(rect.height)),
                                    toolNumber: rect.enteredNumber ?? null,
                                    nonMeasurement: tool?.toolWithoutMeasurement || false,
                                    toleranceUpper: rect.tolerancePlus || null,
                                    toleranceLower: rect.toleranceMinus || null,
                                    toleranceType: rect.toleranceType || null,
                                    nominalDimension: rect.nominalDimension || null
                                };
                            })
                        };
                        await uploadRevision(detail.trim(), blob, payload, engineerName, revisionId);
                        publishedToServer = true;
                        logger.log('[PdfViewer] Revision published to local server', {
                            detail,
                            revision: revision.trim(),
                            rects: payload.rects.length
                        });
                    } catch (publishErr: unknown) {
                        logger.error('[PdfViewer] Failed to publish revision to local server:', publishErr);
                        const reason = publishErr instanceof LocalServerError
                            ? publishErr.message
                            : (publishErr instanceof Error ? publishErr.message : 'Unknown error');
                        setSettingsFileError('');
                        setLocalServerSaveErrorModal({
                            title: strings.LOCAL_SERVER_SAVE_FAILED_MODAL_TITLE,
                            message: reason
                        });
                        return;
                    }
                }

                if (settingsCloudDbOnly && detail && detail.trim() && revision && revision.trim()) {
                    try {
                        const payload: SettingsPayload = {
                            revision: revision.trim(),
                            mdn: null,
                            sequenceMeasurement,
                            examMode,
                            refDimensions: { width: pdfSize.width, height: pdfSize.height },
                            rects: rects.map((rect, index) => {
                                const tool = companyToolsForLegend.find(t => t.number === rect.enteredNumber);
                                return {
                                    measurementNr: index + 1,
                                    x: Math.round(rect.x),
                                    y: Math.round(rect.y),
                                    width: Math.round(Math.abs(rect.width)),
                                    height: Math.round(Math.abs(rect.height)),
                                    toolNumber: rect.enteredNumber ?? null,
                                    nonMeasurement: tool?.toolWithoutMeasurement || false,
                                    toleranceUpper: rect.tolerancePlus || null,
                                    toleranceLower: rect.toleranceMinus || null,
                                    toleranceType: rect.toleranceType || null,
                                    nominalDimension: rect.nominalDimension || null
                                };
                            })
                        };
                        await uploadSettingsToCloud(detail.trim(), blob, payload, engineerName, revisionId);
                        publishedToServer = true;
                        logger.log('[PdfViewer] Settings published to Cloud DB', {
                            detail,
                            revision: revision.trim(),
                            rects: payload.rects.length
                        });
                    } catch (publishErr: unknown) {
                        logger.error('[PdfViewer] Failed to publish settings to Cloud DB:', publishErr);
                        const reason = publishErr instanceof Error ? publishErr.message : 'Unknown error';
                        setSettingsFileError('');
                        setLocalServerSaveErrorModal({
                            title: strings.LOCAL_SERVER_SAVE_FAILED_MODAL_TITLE,
                            message: reason
                        });
                        return;
                    }
                }

                const sanitizeForFilename = (s: string) => (s || '').replace(/[^a-zA-Z0-9а-яА-ЯёЁ\-_]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'export';
                const detailPart = sanitizeForFilename(detail || '') || 'export';
                const revisionPart = revision && revision.trim() ? `rev.${sanitizeForFilename(revision)}` : 'rev';
                const editorName = sanitizeForFilename(engineerName || '') || 'editor';
                const dateStr = new Date().toISOString().slice(0, 10);
                const fileName = `S_${detailPart}_${revisionPart}_${editorName}_${dateStr}.pdf`;

                let savedToFolder = false;
                if (!publishedToServer) {
                    savedToFolder = await saveSettingsPdfBlobToChosenFolder(blob, fileName);
                    if (!savedToFolder) {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = fileName;
                        a.click();
                        URL.revokeObjectURL(url);
                    } else if (settingsPdfDirHandleRef.current) {
                        await saveDownloadDirHandle(settingsPdfDirHandleRef.current, settingsPdfFolderKey);
                    }
                }

                const s = strings as Record<string, string>;
                const hideFolder = publishedToServer;
                const folderName = hideFolder
                    ? ''
                    : (savedToFolder ? (folderDisplayPath || settingsPdfPathDisplayName || '') : (s.SAVE_SUCCESS_DEFAULT_FOLDER || ''));
                const pdfBlobUrl = URL.createObjectURL(blob);
                clearEditorWorkDraft(file, detail, revision);
                suppressEditorDraftPersistRef.current = true;
                const settingsSuccessTitle = settingsLocalServerOnly
                    ? (s.SAVE_SUCCESS_TITLE_LOCAL_SERVER || s.SAVE_SUCCESS_TITLE || '')
                    : settingsCloudDbOnly
                        ? (s.SAVE_SUCCESS_TITLE_CLOUD_DB || s.SAVE_SUCCESS_TITLE || '')
                        : (s.SAVE_SUCCESS_TITLE || '');
                setSaveSuccessModal({
                    title: hideFolder ? settingsSuccessTitle : (s.SAVE_SUCCESS_TITLE || ''),
                    fileName,
                    folderName,
                    hideFolder,
                    pdfBlobUrl
                });
            } catch (error) {
                logger.error('[PdfViewer] Failed to save PDF with settings:', error);
            } finally {
                setIsSavingReport(false);
            }
        } else {
            // Throw error when no role is selected
            throw new Error(strings.ROLE_SELECTION_ERROR);
        }
    };

    // Determine tool image for currently selected username circle (if any)
    const selectedCircleToolInfo = (() => {
        if (selectedCircleIndex === null) {
            return null;
        }
        const usernameRect = usernameRects[ selectedCircleIndex ];
        if (!usernameRect) {
            return null;
        }
        const setterRect = rects.find(r => r.number === usernameRect.number);
        const toolNumber = setterRect?.enteredNumber;
        if (!toolNumber) {
            return null;
        }

        // Find tool in company tools
        const companyTool = companyToolsForLegend.find(tool => tool.number === toolNumber);
        if (companyTool) {
            return {
                src: companyTool.imageUrl || '',
                alt: companyTool.name,
                name: companyTool.name,
            };
        }

        // Return null if tool not found in company tools
        return null;
    })();

    // Determine tool image orientation when user menu opens
    useEffect(() => {
        if (showUserMenu && selectedCircleToolInfo?.src) {
            const img = new Image();
            img.onload = () => {
                const aspectRatio = img.width / img.height;
                // Square if aspect ratio is close to 1 (between 0.85 and 1.15)
                if (aspectRatio >= 0.85 && aspectRatio <= 1.15) {
                    setToolImageOrientation('square');
                } else if (img.width > img.height) {
                    setToolImageOrientation('landscape');
                } else {
                    setToolImageOrientation('portrait');
                }
            };
            img.onerror = () => {
                setToolImageOrientation(null);
            };
            img.src = selectedCircleToolInfo.src;
        } else if (!showUserMenu) {
            // Reset when menu closes
            setToolImageOrientation(null);
        }
    }, [ showUserMenu, selectedCircleToolInfo?.src ]);

    useEffect(() => {
        if (!examToolDropdownOpen) return;
        const handleClickOutsideDropdown = (e: MouseEvent) => {
            if (examToolDropdownRef.current && !examToolDropdownRef.current.contains(e.target as Node)) {
                setExamToolDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideDropdown);
        return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
    }, [ examToolDropdownOpen ]);

    useEffect(() => {
        if (showUserMenu && selectedCircleIndex !== null) {
            const currentRect = usernameRects[ selectedCircleIndex ];
            const isToolWithoutMeasurement = currentRect?.toolWithoutMeasurement || false;
            const isSubmitted = currentRect?.isSubmitted || false;

            if (isToolWithoutMeasurement && !isSubmitted) {
                // Focus the first OK button by default
                setTimeout(() => {
                    firstOkButtonRef.current?.focus();
                }, 0);
            }
        }
    }, [ showUserMenu, selectedCircleIndex, usernameRects ]);

    useEffect(() => {
        if (!(sequenceMeasurement && !examMode && effectiveRole === 'user' && showUserMenu && selectedCircleIndex !== null)) {
            return;
        }
        const currentRect = usernameRects[ selectedCircleIndex ];
        const isSubmitted = currentRect?.isSubmitted || false;
        const isToolWithoutMeasurement = currentRect?.toolWithoutMeasurement || false;
        if (isSubmitted || isToolWithoutMeasurement) {
            return;
        }
        const handleMouseDownCapture = (e: MouseEvent) => {
            if (!isMainPointerButton(e)) return;
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                e.preventDefault();
                requestAnimationFrame(() => userMeasurementInputRef.current?.focus());
            }
        };
        document.addEventListener('mousedown', handleMouseDownCapture, true);
        return () => document.removeEventListener('mousedown', handleMouseDownCapture, true);
    }, [ sequenceMeasurement, examMode, effectiveRole, showUserMenu, selectedCircleIndex, usernameRects ]);

    // Close user menu with submitted value when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (showUserMenu && selectedCircleIndex !== null && userMenuRef.current) {
                const currentRect = usernameRects[ selectedCircleIndex ];
                const isSubmitted = currentRect?.isSubmitted || false;

                // Only close if showing submitted value and click is outside menu
                if (isSubmitted && !userMenuRef.current.contains(e.target as Node)) {
                    setShowUserMenu(false);
                    circleMenuPdfCoordsRef.current = null;
                    setSelectedCircleIndex(null);
                }
            }
        };

        if (showUserMenu && selectedCircleIndex !== null) {
            const currentRect = usernameRects[ selectedCircleIndex ];
            const isSubmitted = currentRect?.isSubmitted || false;

            if (isSubmitted) {
                // Add click listener when menu shows submitted value
                document.addEventListener('mousedown', handleClickOutside);
                return () => {
                    document.removeEventListener('mousedown', handleClickOutside);
                };
            }
        }
    }, [ showUserMenu, selectedCircleIndex, usernameRects ]);

    // Helper function to get tool color from company tools by tool number
    const getToolColorByNumber = (toolNumber: number): string => {
        const companyTool = companyToolsForLegend.find(tool => tool.number === toolNumber);
        return companyTool?.color || '#891f1f';
    };

    // Helper function to convert hex color to rgba with alpha
    const hexToRgba = (hex: string, alpha: number = 0.1): string => {
        const cleanHex = hex.replace('#', '');
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Detect measurements using PDF.js text extraction only
    const handleDetectMeasurements = async () => {
        if (!file || isDetecting) return;

        setIsDetecting(true);
        setDetectedMeasurements([]);

        try {
            const pdfJsResult = await extractTextFromPdfJs(
                file,
                pageNumber,
                stageSize.width,
                stageSize.height
            );

            if (pdfJsResult.success && pdfJsResult.items.length > 0) {
                setDetectedMeasurements(pdfJsResult.items);
                setShowDetectedMeasurements(true);
            }
        } catch (error) {
            logger.error('[OCR] PDF.js detection failed:', error);
        } finally {
            setIsDetecting(false);
        }
    };

    // Handle Ctrl+wheel zoom - prevent browser zoom, apply custom PDF zoom instead
    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            // Only intercept Ctrl+wheel (browser zoom gesture)
            if (e.ctrlKey) {
                e.preventDefault();

                // Calculate zoom delta (normalize for different browser wheel delta values)
                const delta = -e.deltaY * 0.001;

                setPdfZoom(prevZoom => {
                    // Limit zoom between 0.25 (25%) and 3 (300%)
                    const newZoom = Math.max(0.25, Math.min(3, prevZoom + delta));
                    return newZoom;
                });
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl+0 resets zoom to 100%
            if (e.ctrlKey && e.key === '0') {
                e.preventDefault();
                setPdfZoom(1);
            }
            // Ctrl++ zooms in
            if (e.ctrlKey && (e.key === '+' || e.key === '=')) {
                e.preventDefault();
                setPdfZoom(prevZoom => Math.min(3, prevZoom + 0.1));
            }
            // Ctrl+- zooms out
            if (e.ctrlKey && e.key === '-') {
                e.preventDefault();
                setPdfZoom(prevZoom => Math.max(0.25, prevZoom - 0.1));
            }
        };

        // Add wheel event listener with passive: false to allow preventDefault
        document.addEventListener('wheel', handleWheel, { passive: false });
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('wheel', handleWheel);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <div
            className="pdf-viewer"
            onContextMenu={(e) => e.preventDefault()}
        >
            {isSavingReport && (
                <EngineeringLoadingScreen variant="contained" ariaLabel={strings.LOADING} />
            )}
            <div className="pdf-info-bar">
                <button
                    className="pdf-back-btn"
                    onClick={() => {
                        if (sequenceMeasurement) {
                            try {
                                localStorage.removeItem(getSavedUsernameRectsStorageKey(detail, revision));
                            } catch {
                                // ignore
                            }
                        }
                        onBack();
                    }}
                    aria-label={strings.BACK_BUTTON}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                {effectiveRole === 'user' && (
                    <span className="info-item"><b>{strings.ORDER_NUMBER_LABEL}</b> {order || '-'}</span>
                )}
                <span className="info-center"><b>{strings.ENGINEER_NAME_LABEL}</b> {engineerName || strings.ENGINEER_NAME}</span>
                <span className="info-item"><b>{strings.DETAIL_NUMBER_LABEL}</b> {detail || '-'}</span>
                {(role === 'user' || role === 'editor') && (
                <button
                    className={`pdf-save-btn ${(() => {
                        if (isSavingReport) return 'btn-disabled';
                        if (effectiveRole === 'user') {
                            // For usernames, check if all visible rectangles have values
                            const visibleUsernameRects = usernameRects.filter((usernameRect) => {
                                const setterRect = rects.find(r => r.number === usernameRect.number);
                                if (!setterRect) return false;
                                return shouldShowRectangle(setterRect, effectiveCounter || '', density || '');
                            });
                            const allHaveValues = visibleUsernameRects.length > 0 && visibleUsernameRects.every(rect => rect.isSubmitted);
                            return !allHaveValues ? 'btn-disabled' : '';
                        } else {
                            // For setters, require at least 1 rectangle
                            return rects.length === 0 ? 'btn-disabled' : '';
                        }
                    })()}`}
                    onClick={() => handleSave()}
                    disabled={(() => {
                        if (isSavingReport) return true;
                        if (effectiveRole === 'user') {
                            // For usernames, check if all visible rectangles have values
                            const visibleUsernameRects = usernameRects.filter((usernameRect) => {
                                const setterRect = rects.find(r => r.number === usernameRect.number);
                                if (!setterRect) return false;
                                return shouldShowRectangle(setterRect, effectiveCounter || '', density || '');
                            });
                            return visibleUsernameRects.length === 0 || !visibleUsernameRects.every(rect => rect.isSubmitted);
                        } else {
                            // For setters, require at least 1 rectangle
                            return rects.length === 0;
                        }
                    })()}
                    aria-label={strings.SAVE_BUTTON}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                )}
                {effectiveRole === 'editor' && (
                    <button
                        className={`pdf-detect-btn${isDetecting ? ' btn-disabled' : ''}${detectedMeasurements.length > 0 && showDetectedMeasurements ? ' pdf-detect-btn-active' : ''}`}
                        onClick={() => {
                            if (detectedMeasurements.length > 0) {
                                // Toggle visibility if already detected
                                setShowDetectedMeasurements(!showDetectedMeasurements);
                            } else {
                                handleDetectMeasurements();
                            }
                        }}
                        disabled={isDetecting}
                        aria-label="Detect measurements"
                        title={detectedMeasurements.length > 0
                            ? (showDetectedMeasurements
                                ? 'Hide measurements'
                                : 'Show measurements')
                            : 'Detect measurements'}
                    >
                        {isDetecting ? (
                            <svg className="ocr-spinner" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 7V2H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M17 2H22V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M22 17V22H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 22H2V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <path d="M9 9H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <path d="M9 15H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        )}
                        {detectedMeasurements.length > 0 && (
                            <span className="ocr-badge">{detectedMeasurements.length}</span>
                        )}
                    </button>
                )}
                {role === 'editor' && (
                    <div className="pdf-role-switch" role="switch" aria-checked={effectiveRole === 'editor'} aria-label="Editor/User mode">
                        <button
                            type="button"
                            className={`pdf-role-switch-option${effectiveRole === 'editor' ? ' pdf-role-switch-active' : ''}`}
                            onClick={() => setEditorViewMode('editor')}
                            aria-pressed={effectiveRole === 'editor'}
                        >
                            E
                        </button>
                        <button
                            type="button"
                            className={`pdf-role-switch-option${effectiveRole === 'user' ? ' pdf-role-switch-active' : ''}`}
                            onClick={() => {
                                setShowEditorMenu(false);
                                setShowUserMenu(false);
                                setShowHelpMenu(false);
                                setShowSettingsMenu(false);
                                showEditorMenuRef.current = false;
                                setSelectedRectIndex(null);
                                setSelectedCircleIndex(null);
                                setCircleOkNo(null);
                                setEditorViewMode('user');
                            }}
                            aria-pressed={effectiveRole === 'user'}
                        >
                            U
                        </button>
                    </div>
                )}
                {role === 'editor' && effectiveRole === 'editor' && (
                    <button
                        className="pdf-settings-btn"
                        onClick={openPdfSettingsMenu}
                        aria-label={(strings as Record<string, string>).SETTINGS_BUTTON_TITLE}
                        title={(strings as Record<string, string>).SETTINGS_BUTTON_TITLE}
                    >
                        <svg className="pdf-settings-btn-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}
                {(role === 'user' || effectiveRole === 'editor') && (
                    <button
                        className="pdf-help-btn"
                        onClick={() => setShowHelpMenu(true)}
                        aria-label={(strings as Record<string, string>).HELP_BUTTON_TITLE}
                        title={(strings as Record<string, string>).HELP_BUTTON_TITLE}
                    >
                        <span className="pdf-help-btn-icon">?</span>
                    </button>
                )}
            </div>
            {settingsFileError && (role === 'user' || effectiveRole === 'editor') && (
                <div className="settings-file-error">
                    {settingsFileError}
                </div>
            )}
            {effectiveRole === 'editor' && premiumUltimatePlanAllowed && (
                <div className={`editor-standard-menu${editorRecognizedStandardPanelCollapsed ? ' editor-standard-menu--collapsed' : ''}`}>
                    <div className="editor-standard-menu-header">
                        <div className="editor-standard-menu-header-top">
                            <span className="editor-standard-label">{strings.G2_RECOGNIZED_LABEL}</span>
                            <button
                                type="button"
                                className={`editor-standard-menu-collapse-btn${editorRecognizedStandardPanelCollapsed ? ' editor-standard-menu-collapse-btn--collapsed-only' : ''}`}
                                onClick={() => setEditorRecognizedStandardPanelCollapsed(c => !c)}
                                aria-expanded={!editorRecognizedStandardPanelCollapsed}
                                title={editorRecognizedStandardPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
                                aria-label={editorRecognizedStandardPanelCollapsed ? 'Expand recognized standard panel' : 'Collapse recognized standard panel'}
                            >
                                <svg
                                    className="editor-standard-menu-collapse-btn-icon"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                >
                                    {editorRecognizedStandardPanelCollapsed ? (
                                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    ) : (
                                        <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div
                        className={`editor-standard-menu-expandable${editorRecognizedStandardPanelCollapsed ? ' editor-standard-menu-expandable--collapsed' : ''}`}
                        aria-hidden={editorRecognizedStandardPanelCollapsed}
                    >
                        {isoRecognizedSentences.length > 0 ? (
                            <div className="editor-standard-sentences">
                                {isoRecognizedSentences.map((s, i) => (
                                    <span key={i} className="editor-standard-sentence">{s}</span>
                                ))}
                            </div>
                        ) : (
                            <span className="editor-standard-value">—</span>
                        )}
                    <div className="editor-standard-row">
                        <span className="editor-standard-sublabel">Linear standart :</span>
                        <select
                            className="editor-standard-dropdown"
                            value={g2UserSelectedStandard || ISO2768_DEFAULT}
                            onChange={(e) => setG2UserSelectedStandard(e.target.value || null)}
                        >
                            {ISO2768_DROPDOWN_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                    {(g2UserSelectedStandard || ISO2768_DEFAULT) !== 'ISO 22081' ? (
                        <>
                            <div className="editor-standard-row">
                                <span className="editor-standard-sublabel">Shape standart :</span>
                                <select
                                    className="editor-standard-dropdown"
                                    value={g2UserSelectedShapeStandard || SHAPE_STANDARD_DEFAULT}
                                    onChange={(e) => setG2UserSelectedShapeStandard(e.target.value || null)}
                                >
                                    {SHAPE_STANDARD_DROPDOWN_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                            {(g2UserSelectedShapeStandard || SHAPE_STANDARD_DEFAULT) === 'ISO 22081' && (
                                <div className="editor-standard-iso22081-inputs">
                                    <div className="editor-standard-row">
                                        <span className="editor-standard-sublabel">{strings.ISO22081_RADIUS_CHAMFER_SIZES} ±</span>
                                        <input
                                            type="text"
                                            className="editor-standard-dropdown editor-standard-input"
                                            value={iso22081RadiusChamferSizes}
                                            onChange={(e) => setIso22081RadiusChamferSizes(e.target.value)}
                                            placeholder="..."
                                        />
                                    </div>
                                    <div className="editor-standard-row">
                                        <span className="editor-standard-sublabel">{strings.ISO22081_SHAPE_SIZES} ±</span>
                                        <input
                                            type="text"
                                            className="editor-standard-dropdown editor-standard-input"
                                            value={iso22081ShapeSizes}
                                            onChange={(e) => setIso22081ShapeSizes(e.target.value)}
                                            placeholder="..."
                                        />
                                    </div>
                                    <div className="editor-standard-row">
                                        <span className="editor-standard-sublabel">{strings.ISO22081_ANGULAR_SIZES} ±</span>
                                        <input
                                            type="text"
                                            className="editor-standard-dropdown editor-standard-input"
                                            value={iso22081AngularSizes}
                                            onChange={(e) => setIso22081AngularSizes(e.target.value)}
                                            placeholder="..."
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="editor-standard-iso22081-inputs">
                            <div className="editor-standard-row">
                                <span className="editor-standard-sublabel">{strings.ISO22081_LINEAR_SIZES} ±</span>
                                <input
                                    type="text"
                                    className="editor-standard-dropdown editor-standard-input"
                                    value={iso22081LinearSizes}
                                    onChange={(e) => setIso22081LinearSizes(e.target.value)}
                                    placeholder="..."
                                />
                            </div>
                            <div className="editor-standard-row">
                                <span className="editor-standard-sublabel">{strings.ISO22081_RADIUS_CHAMFER_SIZES} ±</span>
                                <input
                                    type="text"
                                    className="editor-standard-dropdown editor-standard-input"
                                    value={iso22081RadiusChamferSizes}
                                    onChange={(e) => setIso22081RadiusChamferSizes(e.target.value)}
                                    placeholder="..."
                                />
                            </div>
                            <div className="editor-standard-row">
                                <span className="editor-standard-sublabel">{strings.ISO22081_SHAPE_SIZES} ±</span>
                                <input
                                    type="text"
                                    className="editor-standard-dropdown editor-standard-input"
                                    value={iso22081ShapeSizes}
                                    onChange={(e) => setIso22081ShapeSizes(e.target.value)}
                                    placeholder="..."
                                />
                            </div>
                            <div className="editor-standard-row">
                                <span className="editor-standard-sublabel">{strings.ISO22081_ANGULAR_SIZES} ±</span>
                                <input
                                    type="text"
                                    className="editor-standard-dropdown editor-standard-input"
                                    value={iso22081AngularSizes}
                                    onChange={(e) => setIso22081AngularSizes(e.target.value)}
                                    placeholder="..."
                                />
                            </div>
                        </div>
                    )}
                    <div className="editor-standard-row editor-standard-row-hole-shaft">
                        <div className="editor-standard-row-item">
                            <span className="editor-standard-sublabel">Shaft ( Ø ) :</span>
                            <select
                                className="editor-standard-dropdown"
                                value={iso286UserSelectedShaft ?? parseShaftFromDesignation(iso286Designation) ?? ''}
                                onChange={(e) => { setIso286UserSelectedShaft(e.target.value || null); setHoleShaftError(''); }}
                            >
                                <option value="">—</option>
                                {ISO286_SHAFT_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div className="editor-standard-row-item">
                            <span className="editor-standard-sublabel">Hole ( Ø ) :</span>
                            <select
                                className="editor-standard-dropdown"
                                value={iso286UserSelectedHole ?? parseHoleFromDesignation(iso286Designation) ?? ''}
                                onChange={(e) => { setIso286UserSelectedHole(e.target.value || null); setHoleShaftError(''); }}
                            >
                                <option value="">—</option>
                                {ISO286_HOLE_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    </div>
                </div>
            )}
            <div
                className="pdf-canvas-centerer"
                ref={centererRef}
                tabIndex={-1}
                onMouseDown={(e) => {
                    // Start drag-to-scroll if clicking directly on centerer background (not on child elements)
                    if (e.target === centererRef.current && !isDragging && !isDrawing) {
                        handleDragStart(e.clientX, e.clientY);
                    }
                }}
            >
                <div
                    className="pdf-zoom-wrapper"
                    style={{
                        width: (pdfOrientation === 'landscape' ? 2000 : 1400) * pdfZoom,
                        height: 1600 * pdfZoom,
                        minWidth: (pdfOrientation === 'landscape' ? 2000 : 1400) * pdfZoom,
                        minHeight: 1200 * pdfZoom,
                        position: 'relative'
                    }}
                >
                    {isOcrLoading && effectiveRole === 'editor' && (
                        <EngineeringLoadingScreen variant="contained" ariaLabel={strings.LOADING} />
                    )}
                    <div
                        className="pdf-konva-container"
                        ref={containerRef}
                        style={{
                            transform: `scale(${pdfZoom})`,
                            transformOrigin: 'top left'
                        }}
                    >
                        {file ? (
                            <>
                                <div className="pdf-canvas-wrapper">
                                    <Document
                                        file={file}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        loading={strings.PDF_LOADING}
                                        error={strings.PDF_ERROR}
                                        onLoadError={(error) => {/* PDF load error */ }}
                                    >
                                        <Page
                                            pageNumber={pageNumber}
                                            width={stageSize.width}
                                            height={stageSize.height}
                                            renderTextLayer={false}
                                            onLoadSuccess={onPageLoadSuccess}
                                            scale={1}
                                        />
                                    </Document>
                                    {/* HTML overlay disabled for debug */}
                                </div>
                                <Stage
                                    ref={stageRef}
                                    width={stageSize.width}
                                    height={stageSize.height}
                                    className="stage-absolute"
                                    onMouseDown={role ? handleStageMouseDown : undefined}
                                    onMouseMove={effectiveRole === 'editor' ? handleStageMouseMove : undefined}
                                    onMouseUp={effectiveRole === 'editor' ? handleStageMouseUp : undefined}
                                    onContextMenu={(e) => {
                                        if (effectiveRole === 'user') {
                                            e.evt.preventDefault();
                                        }
                                    }}
                                >
                                    <Layer>
                                        {effectiveRole === 'editor' && rects.map((rect: any, i: number) => {
                                            // Determine fill and stroke colors based on enteredNumber
                                            const toolColor = rect.enteredNumber ? getToolColorByNumber(rect.enteredNumber) : null;
                                            const fillColor = toolColor ? hexToRgba(toolColor, 0.1) : 'rgba(128, 128, 128, 0.1)';
                                            const strokeColor = toolColor || '#808080';

                                            return (
                                                <React.Fragment key={`rect-${rect.number}-tool-${rect.enteredNumber || 'none'}`}>
                                                    <Rect
                                                        x={rect.x}
                                                        y={rect.y}
                                                        width={rect.width}
                                                        height={rect.height}
                                                        fill={fillColor}
                                                        stroke={strokeColor}
                                                        strokeWidth={2}
                                                        onTap={(e) => {
                                                            // If menu is already open, ignore tap on other rectangles
                                                            if (showEditorMenu) {
                                                                return;
                                                            }

                                                            const stage = e.target.getStage();
                                                            if (!stage) return;
                                                            const pointer = stage.getPointerPosition();
                                                            if (!pointer) return;

                                                            // Show context menu for this rectangle
                                                            const menuWidth = 200; // Initial approximate width (will be adjusted after render)
                                                            const menuHeight = 180; // Initial approximate height (will be adjusted after render)

                                                            // Store rect data for position adjustment after menu renders
                                                            currentMenuRectRef.current = rect;

                                                            // Position editor context menu at top right corner of screen
                                                            const centerer = centererRef.current;
                                                            if (centerer) {
                                                                const viewportLeft = centerer.scrollLeft;
                                                                const viewportTop = centerer.scrollTop;
                                                                const viewportWidth = centerer.clientWidth;

                                                                setMenuPosition({
                                                                    x: viewportLeft + viewportWidth - menuWidth - 10, // 10px margin from right edge
                                                                    y: viewportTop + 10 // 10px margin from top edge
                                                                });
                                                            } else {
                                                                // Fallback to original positioning if centerer not available
                                                                const adjustedPos = adjustMenuPositionForViewport(pointer.x, pointer.y, menuWidth, menuHeight, rect);
                                                                setMenuPosition({
                                                                    x: adjustedPos.x,
                                                                    y: adjustedPos.y
                                                                });
                                                            }

                                                            editorMenuPdfCoordsRef.current = { x: pointer.x, y: pointer.y };
                                                            setShowEditorMenu(true);
                                                            setSelectedRectIndex(i);
                                                            showEditorMenuRef.current = true;
                                                            selectedRectIndexRef.current = i;

                                                            // Set tool input to existing value or default to 1
                                                            const existingToolNum = rect.enteredNumber || selectedLegendToolNumber || 1;
                                                            setSelectedLegendToolNumber(existingToolNum);
                                                            setTextInput(existingToolNum.toString());
                                                            if (companyToolsForLegend.length > 0 && existingToolNum > companyToolsForLegend.length) {
                                                                setToolInputError(`${strings.PDFVIEWER_MAX_TOOL_NUMBER || 'Maksimaalne number on'} ${companyToolsForLegend.length}`);
                                                            } else {
                                                                setToolInputError('');
                                                            }
                                                            setTolerancePlus(rect.tolerancePlus || '');
                                                            setToleranceMinus(rect.toleranceMinus || '');
                                                            setNominalDimension(nominalDimensionForEditorFromRect(rect));
                                                            setRecognizedValue(recognizedValueForEditorFromRect(rect));
                                                            setSelectedToleranceType(rect.toleranceType || 'plusminus');
                                                            toleranceSourceFromAutoFillRef.current = (rect.toleranceFromStandard ?? false) || (rect.toleranceFromIso286Standard ?? false);
                                                            toleranceHadIso286LetterRef.current = rect.hasIso286LetterDesignation ?? false;
                                                            toleranceManuallyCorrectedRef.current = rect.toleranceManuallySet ?? false;
                                                            drawingToleranceFromOcrRef.current = rect.toleranceFromDrawingRecognition ?? false;
                                                            if (premiumUltimatePlanAllowed && (rect.hasIso286LetterDesignation ?? false)) {
                                                                const recR = recognizedValueForEditorFromRect(rect).trim();
                                                                const afterR = recR.replace(/^[Øø⌀∅oOφϕΦ]?\s*[\d.,]+/, '').trim();
                                                                ocrIso286DesignationRef.current = extractIso286Designation(afterR);
                                                            } else {
                                                                ocrIso286DesignationRef.current = null;
                                                            }
                                                            mDesignationRef.current = rect.mDesignation || null;
                                                            if (rect.mDesignation) {
                                                                const savedPartTap = rect.mDesignationActivePart === 'hole' || rect.mDesignationActivePart === 'shaft'
                                                                    ? rect.mDesignationActivePart
                                                                    : 'shaft';
                                                                setIso286ActiveButton(
                                                                    isIso286MPairHoleToleranceOnly(rect.mDesignation) ? 'hole' : savedPartTap
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    {Math.abs(rect.width) >= MIN_RECT_SIZE && Math.abs(rect.height) >= MIN_RECT_SIZE && (() => {
                                                        const normalizedX = rect.width < 0 ? rect.x + rect.width : rect.x;
                                                        const normalizedY = rect.height < 0 ? rect.y + rect.height : rect.y;
                                                        const brX = rect.width >= 0 ? rect.x + rect.width : rect.x;
                                                        const brY = rect.height >= 0 ? rect.y + rect.height : rect.y;
                                                        return (
                                                            <>
                                                                {rect.isConfirmed && (
                                                                    <Text
                                                                        x={normalizedX + 5}
                                                                        y={normalizedY + 5}
                                                                        text={rect.number.toString()}
                                                                        fontSize={14}
                                                                        fill="#3c2eb4"
                                                                        fontStyle="bold"
                                                                        listening={false}
                                                                    />
                                                                )}
                                                                {(rect.toleranceManuallySet || rect.toleranceFromDrawingRecognition) && (
                                                                    <Circle
                                                                        x={brX - 6}
                                                                        y={brY - 6}
                                                                        radius={4}
                                                                        fill="#ff8c00"
                                                                        listening={false}
                                                                    />
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </React.Fragment>
                                            );
                                        })}
                                        {effectiveRole === 'user' && usernameRects
                                            .filter((usernameRect) => {
                                                // Find the corresponding setterRect to check measurement density
                                                const setterRect = rects.find(r => r.number === usernameRect.number);
                                                if (!setterRect) return false;

                                                // Only show rectangles that match the current measurement frequency
                                                return shouldShowRectangle(setterRect, effectiveCounter || '', density || '');
                                            })
                                            .map((usernameRect, i) => {
                                                const setterRect = rects.find(r => r.number === usernameRect.number);
                                                const measurementNumber = setterRect?.enteredNumber;
                                                let fill = examMode ? '#808080' : getToolColorByNumber(measurementNumber || 1);
                                                let strokeColor: string | undefined = undefined;
                                                let strokeWidth = 0;

                                                if (usernameRect.isSubmitted) {
                                                    // For tools without measurement, same style as normal tools - transparent fill with colored border
                                                    if (usernameRect.toolWithoutMeasurement) {
                                                        fill = 'transparent';
                                                        // Add stroke based on ok/no result - green for ok (correct), red for no (out of tolerance)
                                                        const submittedText = usernameRect.text?.toLowerCase();
                                                        if (submittedText === 'ok') {
                                                            strokeColor = TOLERANCE_COLORS.CORRECT;
                                                            strokeWidth = 3;
                                                        } else if (submittedText === 'no') {
                                                            strokeColor = TOLERANCE_COLORS.OUT;
                                                            strokeWidth = 3;
                                                        }
                                                    } else {
                                                        fill = 'transparent';
                                                        const hasToleranceValues = setterRect?.tolerancePlus && setterRect?.toleranceMinus;
                                                        const enteredValue = parseNumberWithComma(usernameRect.text || '0');
                                                        const nominal = parseSetterRectNominalToComparableNumber(setterRect);
                                                        if (!isNaN(enteredValue) && !isNaN(nominal)) {
                                                            const toleranceStatus = getToleranceStatus(
                                                                enteredValue,
                                                                nominal,
                                                                hasToleranceValues ? (setterRect.tolerancePlus || '') : '0.1',
                                                                hasToleranceValues ? (setterRect.toleranceMinus || '') : '0.1',
                                                                hasToleranceValues ? setterRect.toleranceType : 'plusminus'
                                                            );
                                                            if (toleranceStatus === 'in') {
                                                                strokeColor = TOLERANCE_COLORS.IN;
                                                            } else if (toleranceStatus === 'warning') {
                                                                strokeColor = TOLERANCE_COLORS.WARNING;
                                                            } else {
                                                                strokeColor = TOLERANCE_COLORS.OUT;
                                                            }
                                                            strokeWidth = 3;
                                                        } else if (!hasToleranceValues) {
                                                            strokeColor = TOLERANCE_COLORS.CORRECT;
                                                            strokeWidth = 3;
                                                        }
                                                    }
                                                    if (examMode && usernameRect.examSelectedToolNumber != null) {
                                                        const examToolCorrect = usernameRect.examSelectedToolNumber === measurementNumber;
                                                        if (!examToolCorrect) {
                                                            strokeColor = TOLERANCE_COLORS.OUT;
                                                            strokeWidth = 3;
                                                        }
                                                    }
                                                }

                                                const rectX = setterRect?.x || usernameRect.x - Math.abs(setterRect?.width || 0) / 2;
                                                const rectY = setterRect?.y || usernameRect.y - Math.abs(setterRect?.height || 0) / 2;
                                                const rectWidth = Math.abs(setterRect?.width || 0);
                                                const rectHeight = Math.abs(setterRect?.height || 0);
                                                const isCurrentMeasurement = showUserMenu && selectedCircleIndex !== null && usernameRects[ selectedCircleIndex ]?.number === usernameRect.number;

                                                const isBlinking = isCurrentMeasurement && !usernameRect.isSubmitted;
                                                const baseOpacity = usernameRect.isSubmitted ? 1 : 0.3;

                                                if (isBlinking) {
                                                    const accentColor = getToolColorByNumber(measurementNumber || 1);
                                                    return (
                                                        <React.Fragment key={`usernameRect-${i}`}>
                                                            <Rect
                                                                x={rectX}
                                                                y={rectY}
                                                                width={rectWidth}
                                                                height={rectHeight}
                                                                cornerRadius={6}
                                                                fill="transparent"
                                                                stroke={accentColor}
                                                                strokeWidth={2.5}
                                                                dash={[ 10, 6 ]}
                                                                dashOffset={dashOffset}
                                                                shadowColor={accentColor}
                                                                shadowBlur={10 + 18 * blinkPhase}
                                                                shadowOpacity={0.35 + 0.55 * blinkPhase}
                                                                shadowOffsetX={0}
                                                                shadowOffsetY={0}
                                                                opacity={1}
                                                                listening={false}
                                                            />
                                                        </React.Fragment>
                                                    );
                                                }

                                                return (
                                                    <React.Fragment key={`usernameRect-${i}`}>
                                                        <Rect
                                                            x={rectX}
                                                            y={rectY}
                                                            width={rectWidth}
                                                            height={rectHeight}
                                                            fill={fill}
                                                            stroke={strokeColor}
                                                            strokeWidth={strokeWidth}
                                                            opacity={baseOpacity}
                                                        />
                                                    </React.Fragment>
                                                );
                                            })}
                                        {effectiveRole === 'editor' && newSetterRect && (() => {
                                            const drawingToolColor = selectedLegendToolNumber ? getToolColorByNumber(selectedLegendToolNumber) : '#808080';
                                            const drawingFillColor = selectedLegendToolNumber ? hexToRgba(drawingToolColor, 0.1) : 'transparent';
                                            return (
                                                <>
                                                    <Rect
                                                        x={newSetterRect.x}
                                                        y={newSetterRect.y}
                                                        width={newSetterRect.width}
                                                        height={newSetterRect.height}
                                                        fill={drawingFillColor}
                                                        stroke={drawingToolColor}
                                                        strokeWidth={2}
                                                        dash={selectedLegendToolNumber ? undefined : [ 4, 4 ]}
                                                    />
                                                    {/* Sequence number hidden during drawing - shown after editor submit */}
                                                </>
                                            );
                                        })()}
                                        {effectiveRole === 'editor' && showDetectedMeasurements && detectedMeasurements.map((item, index) => {
                                            const strokeColor = item.source === 'pdfjs' ? '#00b450' : '#3c82f6';
                                            const fillColor = item.source === 'pdfjs' ? 'rgba(0, 180, 80, 0.25)' : 'rgba(60, 130, 246, 0.25)';
                                            const w = Math.max(item.width, 12);
                                            const h = Math.max(item.height, 10);
                                            return (
                                                <React.Fragment key={`ocr-${index}`}>
                                                    <Rect
                                                        x={item.x}
                                                        y={item.y}
                                                        width={w}
                                                        height={h}
                                                        fill={fillColor}
                                                        stroke={strokeColor}
                                                        strokeWidth={2}
                                                        cornerRadius={2}
                                                        listening
                                                        onTap={(e) => {
                                                            e.cancelBubble = true;
                                                            const stage = e.target.getStage();
                                                            const pt = stage?.getPointerPosition() || { x: item.x, y: item.y };
                                                            handleDetectedMeasurementEditorClick(item, pt);
                                                        }}
                                                    />
                                                    <Text
                                                        x={item.x}
                                                        y={item.y + h + 2}
                                                        text={item.text}
                                                        fontSize={11}
                                                        fontFamily="Arial"
                                                        fill={strokeColor}
                                                        listening={false}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                    </Layer>
                                </Stage>
                            </>
                        ) : (
                            <div>{strings.NO_PDF_FILE_SELECTED}</div>
                        )}
                    </div>
                </div>
                {showEditorMenu && (
                    <div
                        ref={editorMenuRef}
                        className={`context-menu context-menu-positioned${effectiveRole === 'editor' ? ' context-menu-editor' : ''}`}
                        style={{
                            top: menuPosition.y,
                            left: menuPosition.x
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => {
                            // Allow right mouse button to start drag-to-scroll
                            if (e.button === 2 && effectiveRole === 'editor') {
                                e.stopPropagation();
                                e.preventDefault();
                                // Start drag-to-scroll directly
                                handleDragStart(e.clientX, e.clientY);
                            } else {
                                // Prevent left click from closing menu
                                e.stopPropagation();
                            }
                        }}
                    >
                        {effectiveRole === 'editor' && (() => {
                            // Check if tool for this rectangle is without measurement.
                            // Prefer the tool number from the live text input (draft) over saved
                            // enteredNumber so switching tools in the menu updates the form immediately.
                            const currentRect = selectedRectIndex !== null ? rects[ selectedRectIndex ] : null;
                            const maxLegendTools = companyToolsForLegend.length;
                            const rawToolInput = textInput.trim();
                            let previewToolNumber: number | null = null;
                            if (rawToolInput && /^\d+$/.test(rawToolInput)) {
                                const n = parseInt(rawToolInput, 10);
                                if (
                                    !isNaN(n) &&
                                    n >= 1 &&
                                    (maxLegendTools === 0 || n <= maxLegendTools)
                                ) {
                                    previewToolNumber = n;
                                }
                            }
                            const toolNumber =
                                previewToolNumber ??
                                currentRect?.enteredNumber ??
                                selectedLegendToolNumber ??
                                null;
                            const selectedTool = toolNumber !== null
                                ? companyToolsForLegend.find(t => t.number === toolNumber)
                                : null;
                            const isToolWithoutMeasurement = selectedTool?.toolWithoutMeasurement || false;

                            // Tool number input (always visible at top)
                            const toolInputSection = (
                                <div className="tolerance-input-container">
                                    <div className="tolerance-input-row-tool-block">
                                        <div className="tolerance-input-row-1">
                                            <div className="tolerance-input-label">
                                                {strings.TOOL}
                                            </div>
                                        </div>
                                        <div className="tolerance-input-row-tool-with-recognition">
                                            <div className="tolerance-input-tool-and-recognized">
                                                <div className="tolerance-input-tool-row-with-ocr">
                                                <input
                                                    id="tool-number-input"
                                                    type="text"
                                                value={textInput}
                                                onChange={e => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        setTextInput(value);
                                                        setToolInputError('');
                                                        return;
                                                    }
                                                    if (!/^\d+$/.test(value)) return;
                                                    const num = parseInt(value, 10);
                                                    if (companyToolsForLegend.length > 0 && num > companyToolsForLegend.length) {
                                                        setToolInputError(`${strings.PDFVIEWER_MAX_TOOL_NUMBER || 'Maksimaalne number on'} ${companyToolsForLegend.length}`);
                                                        return;
                                                    }
                                                    setTextInput(value);
                                                    setToolInputError('');
                                                    if (!isNaN(num) && num >= 1 && num <= companyToolsForLegend.length) {
                                                        setSelectedLegendToolNumber(num);
                                                    }
                                                }}
                                                className={`context-menu-input tolerantsi-sisend tolerance-input${toolInputError ? ' input-error' : ''}`}
                                                placeholder={strings.TOOL_NUMBER_PLACEHOLDER}
                                                autoComplete="off"
                                                ref={toolInputRef}
                                                onFocus={(e) => {
                                                    const el = e.target as HTMLInputElement;
                                                    window.setTimeout(() => el.select(), 0);
                                                }}
                                                onClick={(e) => {
                                                    const el = e.currentTarget;
                                                    window.setTimeout(() => el.select(), 0);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === 'F2') {
                                                        handleTextSave();
                                                    }
                                                    if (e.key === 'Delete' || e.key === 'F4') {
                                                        e.preventDefault();
                                                        handleDelete();
                                                    }
                                                }}
                                                />
                                                {premiumUltimatePlanAllowed && (
                                                    <button
                                                        type="button"
                                                        className={`context-menu-region-ocr-btn${regionOcrLoading ? ' context-menu-region-ocr-btn--loading' : ''}`}
                                                        onClick={() => { void handleRegionOcrClick(); }}
                                                        disabled={
                                                            regionOcrLoading ||
                                                            !file ||
                                                            selectedRectIndex === null
                                                        }
                                                        title={
                                                            regionOcrLoading
                                                                ? strings.PDFVIEWER_LOADING
                                                                : strings.PDFVIEWER_RECOGNIZE_REGION
                                                        }
                                                        aria-label={strings.PDFVIEWER_RECOGNIZE_REGION}
                                                        aria-busy={regionOcrLoading}
                                                    >
                                                        <svg
                                                            className="context-menu-region-ocr-btn-icon"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            aria-hidden="true"
                                                        >
                                                            <path
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                                            />
                                                            <circle
                                                                cx="12"
                                                                cy="12"
                                                                r="3"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {recognizedValue && (
                                        <span className="context-menu-recognized-value">{recognizedValue}</span>
                                    )}
                                    {toolInputError && (
                                        <div className="tool-input-error">{toolInputError}</div>
                                    )}
                                    {regionOcrError && (
                                        <div className="tool-input-error" role="alert">{regionOcrError}</div>
                                    )}
                                </div>
                            );

                            // For tools without measurement, show description input
                            if (isToolWithoutMeasurement) {
                                return (
                                    <>
                                        {toolInputSection}
                                        <div className="tolerance-input-container">
                                            <div className="tolerance-input-row-1 tolerance-input-row-full-width">
                                                <div className="tolerance-input-label">
                                                    {strings.DESCRIPTION_LABEL}
                                                </div>
                                                <div className="nominal-input-with-buttons nominal-input-with-buttons-stacked">
                                                    <input
                                                        id="nominal-dimension"
                                                        type="text"
                                                        value={nominalDimension}
                                                        onChange={e => {
                                                            // Allow text, numbers, dots and commas
                                                            setNominalDimension(e.target.value);
                                                        }}
                                                        className="context-menu-input tolerantsi-sisend tolerance-input"
                                                        placeholder={strings.NOMINAL_DIMENSION_PLACEHOLDER}
                                                        autoComplete="off"
                                                        ref={nominalInputRef}
                                                        onFocus={(e) => e.target.select()}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === 'F2') {
                                                                handleTextSave();
                                                            }
                                                            // Delete key deletes entire rectangle, not just text
                                                            if (e.key === 'Delete' || e.key === 'F4') {
                                                                e.preventDefault();
                                                                handleDelete();
                                                            }
                                                            // Backspace works normally to delete text in input
                                                        }}
                                                    />
                                                    <div className="nominal-symbol-buttons">
                                                        <button
                                                            type="button"
                                                            className={`nominal-symbol-btn ${/^[Øø⌀∅oOφϕΦ]/.test((nominalDimension || '').trimStart()) ? 'selected' : ''}`}
                                                            onClick={handleInsertDiameter}
                                                            title="Ø"
                                                            aria-label="Ø"
                                                        >
                                                            <img src="/assets/img-for-btns/diameter.png" alt="Ø" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`nominal-symbol-btn ${(nominalDimension || '').trimEnd().endsWith('°') ? 'selected' : ''}`}
                                                            onClick={handleInsertAngle}
                                                            title="°"
                                                            aria-label="°"
                                                        >
                                                            <img src="/assets/img-for-btns/angle.png" alt="°" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            }

                            // For tools with measurement, show tolerance fields
                            return (
                                <>
                                    {toolInputSection}
                                    <>
                                            <div className="tolerance-input-container">
                                                <div className="tolerance-input-row-1">
                                                    <div className="tolerance-input-label">
                                                        {strings.NOMINAL_DIMENSION_LABEL}
                                                    </div>
                                                    <div className="nominal-input-with-buttons">
                                                        <input
                                                            id="nominal-dimension"
                                                            type="text"
                                                            value={nominalDimension}
                                                            onChange={e => setNominalDimension(e.target.value)}
                                                            className="context-menu-input tolerantsi-sisend tolerance-input"
                                                            placeholder={strings.NOMINAL_DIMENSION_PLACEHOLDER}
                                                            autoComplete="off"
                                                            ref={nominalInputRef}
                                                            onFocus={(e) => e.target.select()}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === 'F2') {
                                                                    handleTextSave();
                                                                }
                                                                if (e.key === 'Delete' || e.key === 'F4') {
                                                                    e.preventDefault();
                                                                    handleDelete();
                                                                }
                                                            }}
                                                        />
                                                        <div className="nominal-symbol-buttons">
                                                        <button
                                                            type="button"
                                                            className={`nominal-symbol-btn ${/^[Øø⌀∅oOφϕΦ]/.test((nominalDimension || recognizedValue || '').trimStart()) ? 'selected' : ''}`}
                                                            onClick={handleInsertDiameter}
                                                            title="Ø"
                                                            aria-label="Ø"
                                                        >
                                                            <img src="/assets/img-for-btns/diameter.png" alt="Ø" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`nominal-symbol-btn ${(nominalDimension || recognizedValue || '').trimEnd().endsWith('°') ? 'selected' : ''}`}
                                                            onClick={handleInsertAngle}
                                                            title="°"
                                                            aria-label="°"
                                                        >
                                                            <img src="/assets/img-for-btns/angle.png" alt="°" />
                                                        </button>
                                                        </div>
                                                    </div>
                                                    {premiumUltimatePlanAllowed && hasDiameterMark((nominalDimension || recognizedValue || '').trim()) && (
                                                        <div className="nominal-hole-shaft-wrapper">
                                                            <div className="nominal-hole-shaft-buttons">
                                                                <button
                                                                    type="button"
                                                                    className={`context-menu-hole-shaft-btn ${bothHoleShaftSelected && iso286ActiveButton === 'shaft' ? 'selected' : ''} ${mDesignationRef.current && iso286ActiveButton === 'shaft' ? 'm-mode-active' : ''}`}
                                                                    onClick={handleApplyShaftTolerance}
                                                                    disabled={!!(mDesignationRef.current && isIso286MPairHoleToleranceOnly(mDesignationRef.current))}
                                                                    title={effectiveIso286Shaft ? `${effectiveIso286Shaft}` : 'h6'}
                                                                    aria-label="Shaft"
                                                                >
                                                                    <img src="/assets/img-for-btns/shaft.png" alt="Shaft" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`context-menu-hole-shaft-btn ${bothHoleShaftSelected && iso286ActiveButton === 'hole' ? 'selected' : ''} ${mDesignationRef.current && iso286ActiveButton === 'hole' ? 'm-mode-active' : ''}`}
                                                                    onClick={handleApplyHoleTolerance}
                                                                    title={effectiveIso286Hole ? `${effectiveIso286Hole}` : 'H7'}
                                                                    aria-label="Hole"
                                                                >
                                                                    <img src="/assets/img-for-btns/hole.png" alt="Hole" />
                                                                </button>
                                                                <div className="hole-shaft-m-btn-wrapper" ref={iso286MDropdownRef}>
                                                                    <button
                                                                        type="button"
                                                                        className={`context-menu-hole-shaft-btn context-menu-m-btn ${(iso286MOpen || mDesignationRef.current) ? 'm-open' : ''} ${mDesignationRef.current ? 'm-has-designation' : ''}`}
                                                                        onClick={() => setIso286MOpen(!iso286MOpen)}
                                                                        title="Manuaalne tolerants"
                                                                        aria-label="M"
                                                                        aria-expanded={iso286MOpen}
                                                                    >
                                                                        {mDesignationRef.current || 'M'}
                                                                    </button>
                                                                    {iso286MOpen && (
                                                                        <div className="hole-shaft-m-dropdown">
                                                                            {ISO286_M_FULL_TOLERANCES.map(opt => (
                                                                                <button
                                                                                    key={opt.value || '__empty__'}
                                                                                    type="button"
                                                                                    className="hole-shaft-m-dropdown-item"
                                                                                    onClick={() => handleMSelectTolerance(opt.value)}
                                                                                >
                                                                                    {opt.label}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {holeShaftError && (
                                                                <div className="hole-shaft-error">{holeShaftError}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tolerance-input-container">
                                                <div className="tolerance-input-row-2">
                                                    <div className="tolerance-type-label">{strings.TOLERANCE_TYPE}</div>
                                                </div>
                                                <div className="tolerance-input-row-3">
                                                    <div className="nominal-dimension-buttons">
                                                        <button
                                                            type="button"
                                                            className={`nominal-btn nominal-btn-small ${selectedToleranceType === 'plusminus' ? 'selected' : ''}`}
                                                            onClick={() => handleToleranceTypeClick('plusminus')}
                                                            tabIndex={-1}
                                                        >
                                                            {strings.SMALL_TOLERANCE_BUTTON}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`nominal-btn nominal-btn-medium ${selectedToleranceType === 'plus' ? 'selected' : ''}`}
                                                            onClick={() => handleToleranceTypeClick('plus')}
                                                            tabIndex={-1}
                                                        >
                                                            {strings.MEDIUM_TOLERANCE_BUTTON}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`nominal-btn nominal-btn-large ${selectedToleranceType === 'minus' ? 'selected' : ''}`}
                                                            onClick={() => handleToleranceTypeClick('minus')}
                                                            tabIndex={-1}
                                                        >
                                                            {strings.LARGE_TOLERANCE_BUTTON}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="tolerance-input-row-4 tolerance-inputs-row-compact iso2768-tolerance-row-with-actions">
                                                    <div className="iso2768-tolerance-inputs-stack">
                                                        <div className="tolerance-input-rida">
                                                            <label htmlFor="tolerance-plus" className="tolerantsi-silt">{getTolerancePlusLabel()}</label>
                                                            <input
                                                                id="tolerance-plus"
                                                                type="text"
                                                                value={tolerancePlus}
                                                                onChange={e => {
                                                                    if (tolerancePlus.trim().length > 0) {
                                                                        toleranceManuallyCorrectedRef.current = true;
                                                                    }
                                                                    toleranceSourceFromAutoFillRef.current = false;
                                                                    mDesignationRef.current = null;
                                                                    setTolerancePlus(sanitizeToleranceInput(e.target.value));
                                                                }}
                                                                className="context-menu-input tolerantsi-sisend"
                                                                placeholder="..."
                                                                autoComplete="off"
                                                                onFocus={(e) => e.target.select()}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === 'F2') {
                                                                        handleTextSave();
                                                                    }
                                                                    if (e.key === 'Delete' || e.key === 'F4') {
                                                                        e.preventDefault();
                                                                        handleDelete();
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="tolerance-input-rida">
                                                            <label htmlFor="tolerance-minus" className="tolerantsi-silt">{getToleranceMinusLabel()}</label>
                                                            <input
                                                                id="tolerance-minus"
                                                                type="text"
                                                                value={toleranceMinus}
                                                                onChange={e => {
                                                                    if (toleranceMinus.trim().length > 0) {
                                                                        toleranceManuallyCorrectedRef.current = true;
                                                                    }
                                                                    toleranceSourceFromAutoFillRef.current = false;
                                                                    mDesignationRef.current = null;
                                                                    setToleranceMinus(sanitizeToleranceInput(e.target.value));
                                                                }}
                                                                className="context-menu-input tolerantsi-sisend"
                                                                placeholder="..."
                                                                autoComplete="off"
                                                                onFocus={(e) => e.target.select()}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === 'F2') {
                                                                        handleTextSave();
                                                                    }
                                                                    if (e.key === 'Delete' || e.key === 'F4') {
                                                                        e.preventDefault();
                                                                        handleDelete();
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    {effectiveGeneralStandard && premiumUltimatePlanAllowed && (
                                                        <div className="iso2768-rc-buttons">
                                                            <button
                                                                type="button"
                                                                className={`nominal-btn nominal-btn-small iso2768-rc-btn ${nominalLeadingIsRadiusLetterR(nominalDimension.trim()) ? 'selected' : ''}`}
                                                                onClick={handleEditorMarkRadius}
                                                                title="ISO 2768-1 tabel 2 — raadius (R enne numbrit)"
                                                            >
                                                                R
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`nominal-btn nominal-btn-small iso2768-rc-btn ${editorIso2768ChamferClassMarkActive ? 'selected' : ''}`}
                                                                onClick={handleEditorMarkChamfer}
                                                                title="ISO 2768-1 tabel 2 — faas (C enne mõõtu või täis faasi kirje)"
                                                            >
                                                                C
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                </>
                            );
                        })()}
                        <div className="context-menu-btn-row">
                            <button
                                className="context-menu-btn-done"
                                onClick={() => {
                                    handleTextSave();
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M20 6L9 17l-5-5"></path>
                                </svg>
                            </button>
                            {effectiveRole === 'editor' && (
                                <button
                                    className="context-menu-btn-delete context-menu-btn-delete-small"
                                    onClick={handleDelete}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 6h18"></path>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {showUserMenu && effectiveRole === 'user' && (() => {
                    const currentRect = selectedCircleIndex !== null ? usernameRects[ selectedCircleIndex ] : null;
                    const setterRectForUserMenu = currentRect
                        ? rects.find(r => r.number === currentRect.number)
                        : null;
                    const userMenuRectangleSequence =
                        setterRectForUserMenu != null ? setterRectForUserMenu.number : null;
                    // Get toolWithoutMeasurement from Excel data (stored in usernameRects)
                    const isToolWithoutMeasurement = currentRect?.toolWithoutMeasurement || false;
                    const isSubmitted = currentRect?.isSubmitted || false;

                    return (
                        <div
                            ref={userMenuRef}
                            className={`context-menu context-menu-positioned context-menu-user${toolImageOrientation ? ` context-menu-user-${toolImageOrientation}` : ''}`}
                            style={{
                                top: circleMenuPosition.y,
                                left: circleMenuPosition.x
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                // Activate input field if clicking outside of input but not on submit button
                                const target = e.target;
                                if (!target || !(target instanceof HTMLElement)) return;

                                const isInput = target.tagName === 'INPUT' && target.classList.contains('user-measurement-input');
                                let isSubmitButton = false;
                                if (target.classList.contains('context-menu-btn-done')) {
                                    isSubmitButton = true;
                                } else if (target.parentElement && target.parentElement.classList.contains('context-menu-btn-done')) {
                                    isSubmitButton = true;
                                } else {
                                    let parent = target.parentElement;
                                    while (parent) {
                                        if (parent.classList && parent.classList.contains('context-menu-btn-done')) {
                                            isSubmitButton = true;
                                            break;
                                        }
                                        parent = parent.parentElement;
                                    }
                                }

                                if (!isToolWithoutMeasurement && !isSubmitted && !isInput && !isSubmitButton && userMeasurementInputRef.current) {
                                    const isExamDropdown = examToolDropdownRef.current?.contains(target);
                                    if (!isExamDropdown) {
                                        userMeasurementInputRef.current.focus();
                                    }
                                }
                            }}
                            onMouseDown={(e) => {
                                // Allow right mouse button to start drag-to-scroll
                                if (e.button === 2 && effectiveRole === 'user') {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    // Start drag-to-scroll directly
                                    handleDragStart(e.clientX, e.clientY);
                                } else {
                                    // Prevent left click from closing menu
                                    e.stopPropagation();
                                }
                            }}
                        >
                            {isToolWithoutMeasurement ? (
                                <>
                                    {isSubmitted ? (
                                        <div className="context-menu-user-submitted-with-sequence">
                                            {!examMode && userMenuRectangleSequence != null && (
                                                <span className="context-menu-user-sequence-number">
                                                    {userMenuRectangleSequence}
                                                </span>
                                            )}
                                            <div className="context-menu-submitted-value">
                                                {currentRect?.text === 'ok' ? '✓ ok' : currentRect?.text === 'no' ? '✗ no' : currentRect?.text || ''}
                                            </div>
                                            {examMode && currentRect?.examSelectedToolNumber != null && (() => {
                                                const editorToolNumber = setterRectForUserMenu?.enteredNumber;
                                                const isCorrect = currentRect.examSelectedToolNumber === editorToolNumber;
                                                const selectedToolName = companyToolsForLegend.find(t => t.number === currentRect.examSelectedToolNumber)?.name || '';
                                                const correctToolName = editorToolNumber ? (companyToolsForLegend.find(t => t.number === editorToolNumber)?.name || '') : '';
                                                return (
                                                    <div className={`exam-tool-result ${isCorrect ? 'exam-tool-correct' : 'exam-tool-incorrect'}`}>
                                                        {isCorrect
                                                            ? `✓ ${selectedToolName}`
                                                            : `✗ ${selectedToolName} → ${correctToolName}`}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div
                                            ref={toleranceInputGroupRef}
                                            className="context-menu-non-measurement-row"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    if (circleOkNoResultRef.current !== null) {
                                                        handleCircleTextSave();
                                                    } else {
                                                        setShowUserMenu(false);
                                                        circleMenuPdfCoordsRef.current = null;
                                                        setSelectedCircleIndex(null);
                                                        setCircleOkNo(null);
                                                    }
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }
                                            }}
                                        >
                                            {!examMode && userMenuRectangleSequence != null && (
                                                <span className="context-menu-user-sequence-number">
                                                    {userMenuRectangleSequence}
                                                </span>
                                            )}
                                            <div className="ok-no-buttons-group">
                                                <button
                                                    ref={firstOkButtonRef}
                                                    className={`context-menu-btn-ok-no ${circleOkNoResult === 'ok' ? 'selected' : ''}`}
                                                    onClick={() => setCircleOkNo('ok')}
                                                    type="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            if (circleOkNoResultRef.current === null) {
                                                                setShowUserMenu(false);
                                                                circleMenuPdfCoordsRef.current = null;
                                                                setSelectedCircleIndex(null);
                                                                setCircleOkNo(null);
                                                            } else {
                                                                handleCircleTextSave();
                                                            }
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }
                                                    }}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className={`context-menu-btn-ok-no ${circleOkNoResult === 'no' ? 'selected' : ''}`}
                                                    onClick={() => setCircleOkNo('no')}
                                                    type="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            if (circleOkNoResultRef.current === null) {
                                                                setShowUserMenu(false);
                                                                circleMenuPdfCoordsRef.current = null;
                                                                setSelectedCircleIndex(null);
                                                                setCircleOkNo(null);
                                                            } else {
                                                                handleCircleTextSave();
                                                            }
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }
                                                    }}
                                                >
                                                    ✗
                                                </button>
                                            </div>
                                            {!examMode && selectedCircleIndex !== null && selectedCircleToolInfo && (
                                                <div className="context-menu-tool-image-row">
                                                    {selectedCircleToolInfo.src ? (
                                                        <img
                                                            key={selectedCircleToolInfo.src}
                                                            className="measurement-legend-img"
                                                            src={selectedCircleToolInfo.src}
                                                            alt={selectedCircleToolInfo.alt}
                                                            ref={(el) => {
                                                                if (el?.complete) el.classList.add('measurement-legend-img-loaded');
                                                            }}
                                                            onLoad={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.classList.add('measurement-legend-img-loaded');
                                                            }}
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.style.display = 'none';
                                                                const parent = target.parentElement;
                                                                if (parent && !parent.querySelector('.measurement-legend-img-placeholder')) {
                                                                    const placeholder = document.createElement('div');
                                                                    placeholder.className = 'measurement-legend-img-placeholder';
                                                                    placeholder.textContent = ((selectedCircleToolInfo as any).name || selectedCircleToolInfo.alt || '?').charAt(0).toUpperCase();
                                                                    parent.insertBefore(placeholder, target);
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="measurement-legend-img-placeholder">
                                                            {((selectedCircleToolInfo as any).name || selectedCircleToolInfo.alt || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {examMode && (
                                                <div className="exam-tool-dropdown" ref={examToolDropdownRef}>
                                                    <button
                                                        type="button"
                                                        className="exam-tool-dropdown-trigger"
                                                        onClick={() => setExamToolDropdownOpen(!examToolDropdownOpen)}
                                                    >
                                                        {examSelectedTool != null ? (() => {
                                                            const t = companyToolsForLegend.find(tool => tool.number === examSelectedTool);
                                                            return t?.imageUrl
                                                                ? <img className="exam-tool-dropdown-img" src={t.imageUrl} alt={t.name} />
                                                                : <span className="exam-tool-dropdown-placeholder-letter">{(t?.name || '?').charAt(0).toUpperCase()}</span>;
                                                        })() : <span className="exam-tool-dropdown-placeholder">{strings.EXAM_SELECT_TOOL}</span>}
                                                    </button>
                                                    {examToolDropdownOpen && (
                                                        <div className="exam-tool-dropdown-panel">
                                                            {companyToolsForLegend.map((tool) => (
                                                                <div
                                                                    key={tool.number}
                                                                    className={`exam-tool-dropdown-item${examSelectedTool === tool.number ? ' exam-tool-dropdown-item-selected' : ''}`}
                                                                    onClick={() => { setExamSelectedTool(tool.number); setExamToolDropdownOpen(false); }}
                                                                >
                                                                    {tool.imageUrl
                                                                        ? <img className="exam-tool-dropdown-item-img" src={tool.imageUrl} alt={tool.name} />
                                                                        : <span className="exam-tool-dropdown-item-placeholder">{tool.name.charAt(0).toUpperCase()}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <button
                                                className={`context-menu-btn-done${examMode && examSelectedTool == null ? ' btn-disabled' : ''}`}
                                                onClick={handleCircleTextSave}
                                                disabled={examMode && examSelectedTool == null}
                                                type="button"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M20 6L9 17l-5-5"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                // For tools with measurement: show input field
                                <>
                                    <div className="tolerance-input-group">
                                        {isSubmitted ? (
                                            <div className="context-menu-user-submitted-with-sequence">
                                                {!examMode && userMenuRectangleSequence != null && (
                                                    <span className="context-menu-user-sequence-number">
                                                        {userMenuRectangleSequence}
                                                    </span>
                                                )}
                                                <div className="context-menu-submitted-value">
                                                    {currentRect?.text || currentRect?.enteredNumber || ''}
                                                </div>
                                                {examMode && currentRect?.examSelectedToolNumber != null && (() => {
                                                    const editorToolNumber = setterRectForUserMenu?.enteredNumber;
                                                    const isCorrect = currentRect.examSelectedToolNumber === editorToolNumber;
                                                    const selectedToolName = companyToolsForLegend.find(t => t.number === currentRect.examSelectedToolNumber)?.name || '';
                                                    const correctToolName = editorToolNumber ? (companyToolsForLegend.find(t => t.number === editorToolNumber)?.name || '') : '';
                                                    return (
                                                        <div className={`exam-tool-result ${isCorrect ? 'exam-tool-correct' : 'exam-tool-incorrect'}`}>
                                                            {isCorrect
                                                                ? `✓ ${selectedToolName}`
                                                                : `✗ ${selectedToolName} → ${correctToolName}`}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            <>
                                                {/* Show image and input field in same row when not submitted */}
                                                <div className="context-menu-user-image-input-row">
                                                    {!examMode && userMenuRectangleSequence != null && (
                                                        <span className="context-menu-user-sequence-number">
                                                            {userMenuRectangleSequence}
                                                        </span>
                                                    )}
                                                    {!examMode && selectedCircleIndex !== null && selectedCircleToolInfo && (
                                                        <div className="context-menu-tool-image-row">
                                                            {selectedCircleToolInfo.src ? (
                                                                <img
                                                                    key={selectedCircleToolInfo.src}
                                                                    className="measurement-legend-img"
                                                                    src={selectedCircleToolInfo.src}
                                                                    alt={selectedCircleToolInfo.alt}
                                                                    ref={(el) => {
                                                                        if (el?.complete) el.classList.add('measurement-legend-img-loaded');
                                                                    }}
                                                                    onLoad={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.classList.add('measurement-legend-img-loaded');
                                                                    }}
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        target.style.display = 'none';
                                                                        const parent = target.parentElement;
                                                                        if (parent && !parent.querySelector('.measurement-legend-img-placeholder')) {
                                                                            const placeholder = document.createElement('div');
                                                                            placeholder.className = 'measurement-legend-img-placeholder';
                                                                            placeholder.textContent = ((selectedCircleToolInfo as any).name || selectedCircleToolInfo.alt || '?').charAt(0).toUpperCase();
                                                                            parent.insertBefore(placeholder, target);
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="measurement-legend-img-placeholder">
                                                                    {((selectedCircleToolInfo as any).name || selectedCircleToolInfo.alt || '?').charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {(setterRectForUserMenu?.nominalDimension || setterRectForUserMenu?.tolerancePlus || setterRectForUserMenu?.toleranceMinus) && (
                                                        <div className="context-menu-user-dimension-info">
                                                            {setterRectForUserMenu?.nominalDimension && (
                                                                <span className="context-menu-user-dimension-nominal">
                                                                    {formatDecimalDotForDisplay(setterRectForUserMenu.nominalDimension)}
                                                                </span>
                                                            )}
                                                            {(setterRectForUserMenu?.tolerancePlus || setterRectForUserMenu?.toleranceMinus) && (
                                                                <span className="context-menu-user-dimension-tolerances">
                                                                    <span className="context-menu-user-dimension-tolerance-plus">
                                                                        {formatToleranceForReport(
                                                                            setterRectForUserMenu.tolerancePlus,
                                                                            setterRectForUserMenu.toleranceMinus,
                                                                            setterRectForUserMenu.toleranceType,
                                                                            true
                                                                        )}
                                                                    </span>
                                                                    <span className="context-menu-user-dimension-tolerance-minus">
                                                                        {formatToleranceForReport(
                                                                            setterRectForUserMenu.tolerancePlus,
                                                                            setterRectForUserMenu.toleranceMinus,
                                                                            setterRectForUserMenu.toleranceType,
                                                                            false
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    <input
                                                        ref={userMeasurementInputRef}
                                                        type="text"
                                                        value={circleTextInput}
                                                        onBlur={() => {
                                                            if (sequenceMeasurement && !examMode && !isSubmitted && userMeasurementInputRef.current) {
                                                                requestAnimationFrame(() => userMeasurementInputRef.current?.focus());
                                                            }
                                                        }}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            // For user role, accept only numbers (including decimals with . or ,)
                                                            if (effectiveRole === 'user') {
                                                                // Allow empty string or numbers (including decimal point or comma)
                                                                if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
                                                                    setCircleTextInput(value);
                                                                }
                                                            } else {
                                                                setCircleTextInput(value);
                                                            }
                                                        }}
                                                        className="context-menu-input user-measurement-input"
                                                        placeholder={(() => {
                                                            if (examMode) return strings.MEASUREMENT_RESULT_PLACEHOLDER;
                                                            const toolNumber = currentRect?.enteredNumber;
                                                            const selectedTool = toolNumber !== null && toolNumber !== undefined
                                                                ? companyToolsForLegend.find(t => t.number === toolNumber)
                                                                : null;
                                                            const minToolPrecision = selectedTool?.minToolPrecision;
                                                            return minToolPrecision || strings.MEASUREMENT_RESULT_PLACEHOLDER;
                                                        })()}
                                                        autoFocus={!examMode}
                                                        onKeyDown={(e) => {
                                                            // For user role, block non-numeric keys except Enter, Backspace, Delete, Arrow keys, Tab
                                                            if (effectiveRole === 'user') {
                                                                const allowedKeys = [ 'Enter', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End' ];
                                                                const isNumberKey = /^\d$/.test(e.key);
                                                                const hasDecimalSeparator = circleTextInput.includes('.') || circleTextInput.includes(',');
                                                                const isDecimalPoint = e.key === '.' && !hasDecimalSeparator;
                                                                const isDecimalComma = e.key === ',' && !hasDecimalSeparator;
                                                                const isControlKey = e.ctrlKey || e.metaKey;

                                                                if (!allowedKeys.includes(e.key) && !isNumberKey && !isDecimalPoint && !isDecimalComma && !isControlKey) {
                                                                    e.preventDefault();
                                                                    return;
                                                                }
                                                            }
                                                            if (e.key === 'Enter') {
                                                                handleCircleTextSave();
                                                            }
                                                        }}
                                                    />
                                                    {examMode && (
                                                        <div className="exam-tool-dropdown" ref={examToolDropdownRef}>
                                                            <button
                                                                type="button"
                                                                className="exam-tool-dropdown-trigger"
                                                                onClick={() => setExamToolDropdownOpen(!examToolDropdownOpen)}
                                                            >
                                                                {examSelectedTool != null ? (() => {
                                                                    const t = companyToolsForLegend.find(tool => tool.number === examSelectedTool);
                                                                    return t?.imageUrl
                                                                        ? <img className="exam-tool-dropdown-img" src={t.imageUrl} alt={t.name} />
                                                                        : <span className="exam-tool-dropdown-placeholder-letter">{(t?.name || '?').charAt(0).toUpperCase()}</span>;
                                                                })() : <span className="exam-tool-dropdown-placeholder">{strings.EXAM_SELECT_TOOL}</span>}
                                                            </button>
                                                            {examToolDropdownOpen && (
                                                                <div className="exam-tool-dropdown-panel">
                                                                    {companyToolsForLegend.map((tool) => (
                                                                        <div
                                                                            key={tool.number}
                                                                            className={`exam-tool-dropdown-item${examSelectedTool === tool.number ? ' exam-tool-dropdown-item-selected' : ''}`}
                                                                            onClick={() => { setExamSelectedTool(tool.number); setExamToolDropdownOpen(false); }}
                                                                        >
                                                                            {tool.imageUrl
                                                                                ? <img className="exam-tool-dropdown-item-img" src={tool.imageUrl} alt={tool.name} />
                                                                                : <span className="exam-tool-dropdown-item-placeholder">{tool.name.charAt(0).toUpperCase()}</span>}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    <button
                                                        className={`context-menu-btn-done ${effectiveRole === 'user' && circleTextInput.trim() && (() => {
                                                            if (examMode) return examSelectedTool != null;
                                                            const toolNumber = currentRect?.enteredNumber;
                                                            const selectedTool = toolNumber !== null && toolNumber !== undefined
                                                                ? companyToolsForLegend.find(t => t.number === toolNumber)
                                                                : null;
                                                            const minToolPrecision = selectedTool?.minToolPrecision;
                                                            return validateMeasurementInput(circleTextInput, minToolPrecision).isValid;
                                                        })() ? 'btn-enabled' : effectiveRole === 'user' ? 'btn-disabled' : ''}`}
                                                        onClick={handleCircleTextSave}
                                                        disabled={effectiveRole === 'user' && (!circleTextInput.trim() || (() => {
                                                            if (examMode) return examSelectedTool == null;
                                                            const toolNumber = currentRect?.enteredNumber;
                                                            const selectedTool = toolNumber !== null && toolNumber !== undefined
                                                                ? companyToolsForLegend.find(t => t.number === toolNumber)
                                                                : null;
                                                            const minToolPrecision = selectedTool?.minToolPrecision;
                                                            return !validateMeasurementInput(circleTextInput, minToolPrecision).isValid;
                                                        })())}
                                                    >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M20 6L9 17l-5-5"></path>
                                                    </svg>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })()}
            </div>
            {numPages !== null && numPages > 1 && (
                <div className="pdf-pagination">
                    <button
                        className={pageNumber <= 1 ? 'btn-disabled' : ''}
                        onClick={() => { setPageNumber((p: number) => Math.max(1, p - 1)); setDetectedMeasurements([]); }}
                        disabled={pageNumber <= 1}
                    >
                        {strings.PREVIOUS_PAGE}
                    </button>
                    <span>
                        {strings.PAGE} {pageNumber} / {numPages}
                    </span>
                    <button
                        className={pageNumber >= numPages ? 'btn-disabled' : ''}
                        onClick={() => { setPageNumber((p: number) => Math.min(numPages, p + 1)); setDetectedMeasurements([]); }}
                        disabled={pageNumber >= numPages}
                    >
                        {strings.NEXT_PAGE}
                    </button>
                </div>
            )}
            {effectiveRole === 'editor' && (
                <div
                    className="measurement-legend-bar"
                    ref={legendBarRef}
                    onMouseDown={handleLegendBarMouseDown}
                    onContextMenu={(e) => {
                        // Prevent context menu when right-clicking to drag legend
                        e.preventDefault();
                    }}
                >
                    {companyToolsForLegend.map((tool) => (
                        <div
                            key={tool.number}
                            data-tool-number={tool.number}
                            className={`measurement-legend-item${effectiveRole === 'editor' && selectedLegendToolNumber === tool.number ? ' measurement-legend-item-selected' : ''}${effectiveRole === 'editor' ? ' measurement-legend-item-clickable' : ''}`}
                            onClick={effectiveRole === 'editor' ? (e) => {
                                e.stopPropagation();
                                stopLegendBarMomentum();

                                // Don't change tool if legend bar is being dragged (right mouse button)
                                if (isLegendBarDragging) {
                                    return;
                                }

                                // Check if context menu is open - if so, apply tool to rectangle
                                if (showEditorMenu && selectedRectIndex !== null) {
                                    // Apply tool to rectangle and close menu
                                    applyToolToRectangle(tool.number, selectedRectIndex);
                                } else {
                                    // Just update selected tool for next rectangle
                                    setSelectedLegendToolNumber(tool.number);
                                }
                            } : undefined}
                        >
                            <div
                                className="measurement-legend-number"
                                style={{ backgroundColor: `${tool.color}88` }}
                            >
                                {tool.number}
                            </div>
                            {tool.imageUrl ? (
                                <img
                                    className="measurement-legend-img"
                                    src={tool.imageUrl}
                                    alt={tool.name}
                                    onError={(e) => {
                                        // Fallback to placeholder if image fails to load
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <div className="measurement-legend-img-placeholder">
                                    {tool.name.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                    )
                    )}
                </div>
            )}
            {showHelpMenu && (() => {
                const s = strings as Record<string, string>;
                return (
                    <div
                        className="help-menu-overlay"
                        onClick={() => setShowHelpMenu(false)}
                    >
                        <div
                            ref={helpMenuRef}
                            className="help-menu"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="help-menu-title">{s.HELP_MENU_TITLE}</div>
                            <div className="help-menu-list">
                                {effectiveRole === 'editor' && (
                                    <>
                                        <div className="help-menu-row"><kbd>Enter</kbd> / <kbd>F2</kbd><span>{s.HELP_ENTER}</span></div>
                                        <div className="help-menu-row"><kbd>Delete</kbd> / <kbd>F4</kbd><span>{s.HELP_DELETE}</span></div>
                                        <div className="help-menu-row"><kbd>Escape</kbd><span>{s.HELP_ESCAPE}</span></div>
                                        <div className="help-menu-row"><kbd>1</kbd>–<kbd>9</kbd><span>{s.HELP_TOOL_SELECT}</span></div>
                                    </>
                                )}
                                {(effectiveRole === 'user' || !role) && (
                                    <>
                                        <div className="help-menu-row"><kbd>Escape</kbd><span>{s.HELP_ESCAPE_USER}</span></div>
                                        <div className="help-menu-row"><kbd>+</kbd><span>{s.HELP_PLUS}</span></div>
                                        <div className="help-menu-row"><kbd>-</kbd><span>{s.HELP_MINUS}</span></div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}
            {showSettingsMenu && role === 'editor' && (() => {
                const s = strings as Record<string, string>;
                return (
                    <div
                        className="help-menu-overlay settings-menu-overlay"
                        aria-modal="true"
                        role="dialog"
                        aria-labelledby="pdf-settings-dialog-title"
                        onClick={() => setShowSettingsMenu(false)}
                    >
                        <div
                            ref={settingsMenuRef}
                            className="help-menu settings-menu"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="help-menu-title" id="pdf-settings-dialog-title">
                                {s.SETTINGS_MENU_TITLE}
                            </div>
                            <div className="settings-menu-list">
                                <div className="settings-menu-checkboxes-row">
                                    <label className="settings-menu-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={sequenceMeasurement}
                                            onChange={(e) => setSequenceMeasurement(e.target.checked)}
                                        />
                                        <span>{s.SETTINGS_SEQUENCE_MEASUREMENT}</span>
                                    </label>
                                    <label className="settings-menu-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={examMode}
                                            onChange={(e) => { setExamMode(e.target.checked); if (e.target.checked) setSequenceMeasurement(false); }}
                                        />
                                        <span>{s.SETTINGS_EXAM_MODE}</span>
                                    </label>
                                </div>
                                <div className="settings-menu-row">
                                    <span className="settings-menu-label">{s.PROFILE_REPORT_LANGUAGE_LABEL}</span>
                                    <div className="language-buttons">
                                        <button
                                            type="button"
                                            className={`language-flag-btn ${reportLanguage === 'et' ? 'language-flag-btn-active' : ''}`}
                                            onClick={() => handleEditorSettingsReportLanguageChange('et')}
                                        >
                                            ET
                                        </button>
                                        <button
                                            type="button"
                                            className={`language-flag-btn ${reportLanguage === 'en' ? 'language-flag-btn-active' : ''}`}
                                            onClick={() => handleEditorSettingsReportLanguageChange('en')}
                                        >
                                            EN
                                        </button>
                                        <button
                                            type="button"
                                            className={`language-flag-btn ${reportLanguage === 'ru' ? 'language-flag-btn-active' : ''}`}
                                            onClick={() => handleEditorSettingsReportLanguageChange('ru')}
                                        >
                                            RU
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {orderNumberModalForEditor && (
                <div className="modal-overlay">
                    <div className="modal-container order-number-modal">
                        <div className="modal-header">
                            <h2>{(strings as Record<string, string>).EDITOR_SAVE_ORDER_NUMBER_DIALOG_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="editor-order-number-input">{(strings as Record<string, string>).ORDER_NUMBER_LABEL}</label>
                                <input
                                    id="editor-order-number-input"
                                    type="text"
                                    className="form-input"
                                    value={orderNumberInputForSave}
                                    onChange={(e) => setOrderNumberInputForSave(e.target.value)}
                                    placeholder=""
                                    autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="editor-pdf-measured-detail-input">
                                    {(strings as Record<string, string>).EDITOR_SAVE_PDF_MEASURED_DETAIL_LABEL}
                                </label>
                                <input
                                    id="editor-pdf-measured-detail-input"
                                    type="text"
                                    className="form-input"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    value={editorPdfMeasuredDetailNumberForSave}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        setEditorPdfMeasuredDetailNumberForSave(digitsOnly);
                                    }}
                                    placeholder=""
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => setOrderNumberModalForEditor(false)}
                            >
                                {(strings as Record<string, string>).DASHBOARD_CANCEL}
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={async () => {
                                    setOrderNumberModalForEditor(false);
                                    await handleSave(
                                        orderNumberInputForSave.trim(),
                                        editorPdfMeasuredDetailNumberForSave.trim()
                                    );
                                }}
                            >
                                {strings.SAVE_BUTTON}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {saveSuccessModal && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        if (saveSuccessModal.pdfBlobUrl) {
                            scheduleRevokePdfBlobUrl(saveSuccessModal.pdfBlobUrl);
                        }
                        saveSuccessModal.onClose?.();
                        setSaveSuccessModal(null);
                    }}
                >
                    <div className="modal-container save-success-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{saveSuccessModal.title}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="save-success-file-label">{(strings as Record<string, string>).SAVE_SUCCESS_FILE_LABEL}:</p>
                            <div className="save-success-file-row">
                                <p className="save-success-file-value">{saveSuccessModal.fileName}</p>
                                {saveSuccessModal.pdfBlobUrl ? (
                                    <button
                                        type="button"
                                        className="save-success-open-pdf"
                                        onClick={() => {
                                            window.open(saveSuccessModal.pdfBlobUrl, '_blank', 'noopener,noreferrer');
                                        }}
                                    >
                                        {(strings as Record<string, string>).SAVE_SUCCESS_OPEN_PDF}
                                    </button>
                                ) : null}
                            </div>
                            {!saveSuccessModal.hideFolder ? (
                                <>
                                    <p className="save-success-folder-label">{(strings as Record<string, string>).SAVE_SUCCESS_FOLDER_LABEL}:</p>
                                    <p className="save-success-folder-value">{saveSuccessModal.folderName}</p>
                                </>
                            ) : null}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => {
                                    if (saveSuccessModal.pdfBlobUrl) {
                                        scheduleRevokePdfBlobUrl(saveSuccessModal.pdfBlobUrl);
                                    }
                                    saveSuccessModal.onClose?.();
                                    setSaveSuccessModal(null);
                                }}
                            >
                                {(strings as Record<string, string>).SAVE_SUCCESS_OK}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {localServerSaveErrorModal && (
                <div
                    className="modal-overlay"
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="local-server-save-error-title"
                >
                    <div className="modal-container order-number-modal">
                        <div className="modal-header">
                            <h2 id="local-server-save-error-title">{localServerSaveErrorModal.title}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="profile-error-message local-server-save-error-detail">
                                {localServerSaveErrorModal.message}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => setLocalServerSaveErrorModal(null)}
                            >
                                {(strings as Record<string, string>).SAVE_SUCCESS_OK}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 